export interface BlogPost {
  id: number;
  slug: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  excerpt: string;
  category: string;
  date: string;
  readTime: string;
  image: string;
  imageAlt: string;
  featured?: boolean;
  keywords: string[];
  content: BlogSection[];
}

export interface BlogSection {
  type: "h2" | "h3" | "p" | "list" | "quote" | "cta";
  text?: string;
  items?: string[];
}

// Category to English keyword mapping for fallback images
const categoryKeywordMap: Record<string, string> = {
  "הדרכות": "safety-training",
  "רגולציה": "regulation-compliance",
  "ניהול": "management-leadership",
  "טכנולוגיה": "technology-innovation",
  "מדריכים": "guide-manual",
  "כלכלה": "finance-business",
  "ענפים": "industry-factory",
  "בנייה": "construction-site",
  "גיהות": "hygiene-health",
  "סיכונים": "risk-hazard",
  "מגזרים": "sector-workplace",
  "בית וסביבה": "home-environment"
};

// Array of unique high-quality Unsplash image IDs for fallbacks
const fallbackImageIds = [
  "photo-1581092921461-eab62e97a780", // Safety equipment
  "photo-1504307651254-35680f356dfd", // Construction
  "photo-1581091226825-a6a2a5aee158", // Industrial
  "photo-1486312338219-ce68d2c6f44d", // Office work
  "photo-1504384308090-c894fdcc538d", // Business
  "photo-1517048676732-d65bc937f952", // Team meeting
  "photo-1552664730-d307ca884978", // Training
  "photo-1531498860502-7c67cf02f657", // Manufacturing
  "photo-1504328345606-18bbc8c9d7d1", // Industrial safety
  "photo-1586528116311-ad8dd3c8310d", // Warehouse
  "photo-1507003211169-0a1dd7228f2d", // Professional
  "photo-1454165804606-c3d57bc86b40", // Desk work
  "photo-1521791136064-7986c2920216", // Handshake
  "photo-1560472355-536de3962603", // Hard hat
  "photo-1565008447742-97f6f38c985c", // Safety vest
  "photo-1581094794329-c8112a89af12", // Factory worker
  "photo-1497366216548-37526070297c", // Office
  "photo-1497215842964-222b430dc094", // Modern office
  "photo-1560179707-f14e90ef3623", // Building
  "photo-1571019614242-c5c5dee9f50b", // Protective gear
  "photo-1572021335469-31706a17ber5", // Industry
  "photo-1541888946425-d81bb19240f5", // Construction crane
  "photo-1590650153855-d9e808231d41", // PPE
  "photo-1594736797933-d0501ba2fe65", // Warehouse worker
  "photo-1504384764586-bb4cdc1707b0", // Safety helmet
  "photo-1542744094-24638eff58bb", // Industrial work
  "photo-1489824904134-891ab64532f1", // Factory
  "photo-1578575436955-ef29da568c6c", // Construction worker
  "photo-1508873535684-277a3cbcc4e8", // Risk assessment
  "photo-1532619675605-1ede6c2ed2b0", // Training session
];

// Generate deterministic fallback image URL based on post ID and category
function generateFallbackImage(postId: number, category: string): string {
  const keyword = categoryKeywordMap[category] || "workplace-safety";
  // Use post ID to get a deterministic but unique image from the pool
  const imageIndex = postId % fallbackImageIds.length;
  const imageId = fallbackImageIds[imageIndex];
  return `https://images.unsplash.com/${imageId}?w=800&auto=format&fit=crop&q=80`;
}

// Check if image URL is valid (not a broken local path)
function isValidImageUrl(url: string): boolean {
  if (!url || url.trim() === "") return false;
  // Allow local assets served from /public (Vite) for known prefixes.
  // We only treat unknown absolute paths as broken to avoid 404s.
  if (url.startsWith("/") && !url.startsWith("//")) {
    const allowedLocalPrefixes = ["/assets/", "/images/", "/videos/", "/favicon", "/og-image", "/placeholder"]; 
    if (!allowedLocalPrefixes.some((p) => url.startsWith(p))) return false;
  }
  return true;
}

// Normalize blog posts to ensure unique, valid images
function normalizeBlogPosts(posts: BlogPost[]): BlogPost[] {
  const usedImages = new Set<string>();
  
  return posts.map((post, index) => {
    let finalImage = post.image;
    
    // Check if image is valid
    if (!isValidImageUrl(finalImage)) {
      // Generate unique fallback for broken paths
      finalImage = generateFallbackImage(post.id, post.category);
    }
    
    // Check for duplicates - if this exact URL was already used, generate unique fallback
    if (usedImages.has(finalImage)) {
      // Use index to ensure uniqueness even for posts with same category
      const uniqueId = (post.id * 7 + index) % fallbackImageIds.length;
      const uniqueImageId = fallbackImageIds[uniqueId];
      finalImage = `https://images.unsplash.com/${uniqueImageId}?w=800&auto=format&fit=crop&q=80&sig=${post.id}`;
    }
    
    usedImages.add(finalImage);
    
    return {
      ...post,
      image: finalImage
    };
  });
}

const rawBlogPosts: BlogPost[] = [
  // ===== FEATURED: 2025 CONSTRUCTION SAFETY REGULATIONS =====
  {
    id: 60,
    slug: "safety-regulations-construction-2025",
    title: "רעידת אדמה בענף הבנייה: כל מה שצריך לדעת על תקנות הבטיחות החדשות 2025",
    metaTitle: "תקנות בטיחות בנייה 2025 | הרפורמה החדשה | אדם בטיחות",
    metaDescription: "הרפורמה בבטיחות יצאה לדרך! תקנות הבטיחות בעבודה (עבודות בניה) 2025 משנות את כללי המשחק: אחריות ישירה ליזמים, חובת מינוי בקר בטיחות ומנהל אתר. היכנסו לקרוא.",
    excerpt: "התקנות החדשות לשנת 2025 מטילות אחריות ישירה על מזמיני עבודה, מחייבות מינוי בקר בטיחות ומנהל אתר. גלו את כל מה שחייבים לדעת.",
    category: "רגולציה",
    date: "2025-02-08",
    readTime: "12 דקות קריאה",
    image: "/assets/blog/construction-regulations-2025.jpg",
    imageAlt: "מנהל אתר בנייה עם קסדת בטיחות ואפוד זוהר בודק תוכניות באתר בנייה",
    featured: true,
    keywords: ["תקנות בטיחות 2025", "בקר בטיחות", "מנהל אתר", "אחריות יזם", "תקנות בנייה", "רפורמת בטיחות", "עבודות בניה"],
    content: [
      { type: "p", text: "שנת 2025 מסמנת נקודת מפנה דרמטית בענף הבנייה והתשתיות בישראל. התיקון החדש לתקנות הבטיחות בעבודה (עבודות בניה), שאושר לאחרונה ופורסם ברשומות, טורף את הקלפים וקובע \"שרשרת אחריות\" חדשה. אם עד היום האחריות \"התגלגלה\" בעיקר לפתחו של מנהל העבודה בשטח – מעתה, האחריות עולה למעלה, עד למזמין העבודה (היזם)." },
      { type: "p", text: "בחברת אדם בטיחות, אנו עוקבים מקרוב אחר השינויים ונערכים לספק לכם את המעטפת המקצועית המלאה לעמידה בדרישות החדשות. הנה עיקרי הרפורמה שכל יזם, קבלן ומנהל חייב להכיר." },

      { type: "h2", text: "המהפכה הגדולה: המזמין (היזם) נכנס למעגל האחריות" },
      { type: "p", text: "החידוש המרכזי בתקנות הוא הטלת אחריות ישירה (פלילית ואזרחית) על מזמין העבודה. היזם לא יכול עוד להסתפק ב\"העברת האחריות\" לקבלן בחוזה. התקנות החדשות אוסרות על המזמין לאשר את תחילת העבודות אלא אם וידא שהתקיימו תנאי סף קריטיים, ביניהם:" },
      { type: "list", items: [
        "קיומה של תוכנית בטיחות מאושרת",
        "הקצאת משאבים כספיים ייעודיים ליישום הבטיחות בהסכם מול הקבלן"
      ]},

      { type: "h2", text: "השחקן החדש במגרש: בקר בטיחות (Safety Controller)" },
      { type: "p", text: "כדי לאפשר למזמין לעמוד באחריות החדשה, התקנות מחייבות אותו למנות בקר בטיחות." },
      { type: "h3", text: "מי זה?" },
      { type: "p", text: "גורם מקצועי בלתי תלוי (מהנדס/הנדסאי אזרחי או ממונה בטיחות בכיר עם הכשרה ייעודית) שאינו כפוף לקבלן הביצוע." },
      { type: "h3", text: "תפקידו:" },
      { type: "p", text: "לשמש \"העיניים של היזם\" בשטח. הבקר יבצע ביקורות בצמתים קריטיים (חפירה, שלד, גמר) ויוודא שתוכנית הבטיחות מיושמת." },
      { type: "h3", text: "סמכות:" },
      { type: "p", text: "במקרה של זיהוי \"סיכון בלתי קביל\", הבקר מדווח למזמין, שחייב לאשר בכתב תוך 48 שעות כי הסיכון טופל." },

      { type: "h2", text: "מנהל אתר (Site Manager) – סמכות הנדסית באתרי ענק" },
      { type: "p", text: "באתרי בנייה גדולים (מעל 15,000 מ\"ר), המחוקק דורש כעת מינוי של מנהל אתר. זהו תפקיד חדש המיועד למהנדס או הנדסאי רשום, שתפקידו לנהל את הפן ההנדסי-בטיחותי:" },
      { type: "list", items: [
        "אישור תוכניות הנדסיות (כגון פיגומים ועגורנים)",
        "בקרה חודשית על מערך הבטיחות",
        "וידוא ביצוע הדרכות לעובדים"
      ]},
      { type: "p", text: "מנהל האתר מהווה סמכות מקצועית עליונה לצד מנהל העבודה." },

      { type: "h2", text: "תוכנית בטיחות לכל אתר – ללא יוצא מן הכלל" },
      { type: "p", text: "אם בעבר החובה להכנת תוכנית בטיחות (או תוכנית לניהול בטיחות) חלה בעיקר על אתרים גדולים, התקנות החדשות מרחיבות זאת לכולם:" },
      { type: "list", items: [
        "בכל אתר בנייה (גם הקטן ביותר) תידרש תוכנית בטיחות כתובה",
        "באתרים קטנים (עד 50 עובדים) תידרש \"תוכנית ארגון בטיחות\"",
        "באתרים גדולים תידרש \"תוכנית לניהול הבטיחות\" מלאה"
      ]},
      { type: "p", text: "המשמעות: מבצע בניה לא יוכל להתחיל לעבוד ללא \"מפת דרכים\" בטיחותית מוכנה מראש." },

      { type: "h2", text: "חיזוק מעמדו של ממונה הבטיחות ומנהל העבודה" },
      { type: "p", text: "הרפורמה לא פסחה על בעלי התפקידים המסורתיים:" },
      { type: "h3", text: "ממונה בטיחות:" },
      { type: "p", text: "סמכויותיו הורחבו, והוא נדרש כעת לדווח על ליקויים גם לבקר הבטיחות ולמנהל האתר. הממונה הוא הגורם המקצועי שמלווה את השוטף ומנחה את המערכת." },
      { type: "h3", text: "מנהל עבודה:" },
      { type: "p", text: "יוקם מרשם ארצי חדש למנהלי עבודה, ותוקף ההסמכה יוגבל ל-5 שנים. חידוש הרישיון יותנה בביצוע השתלמויות מקצועיות, כדי להבטיח רמה מקצועית גבוהה." },

      { type: "h2", text: "איך נערכים? אדם בטיחות לשירותכם" },
      { type: "p", text: "התקנות החדשות, שצפויות להיכנס לתוקף מלא באוקטובר 2026, מחייבות היערכות מוקדמת. יזמים וקבלנים שלא יתאימו את עצמם צפויים לחשיפה משפטית ועיכובים בפרויקטים." },
      { type: "p", text: "בחברת אדם בטיחות, אנו מציעים כבר היום את סל הפתרונות המלא לעידן החדש:" },
      { type: "list", items: [
        "שירותי בקר בטיחות ליזמים ומזמיני עבודה",
        "בניית תוכניות בטיחות ותוכניות ארגון אתר לכל סוגי הפרויקטים",
        "שירותי ממונה בטיחות חיצוני לליווי שוטף והדרכות",
        "ייעוץ הנדסי ופתרונות לעבודה בגובה"
      ]},

      { type: "quote", text: "אל תחכו לדקה ה-90. צרו קשר עוד היום עם אדם בטיחות ונתאים לכם את חליפת ההגנה המקצועית והמשפטית הטובה ביותר." },

      { type: "cta", text: "מוכנים להיערך לתקנות 2025? צרו קשר עכשיו לייעוץ ראשוני ללא עלות." }
    ]
  },
  // ===== EXISTING POSTS WITH FULL CONTENT =====
  {
    id: 1,
    slug: "5-common-safety-training-mistakes",
    title: "5 טעויות נפוצות בהדרכות בטיחות וכיצד להימנע מהן",
    metaTitle: "5 טעויות נפוצות בהדרכות בטיחות | Safe-Group ייעוץ בטיחות",
    metaDescription: "הדרכות בטיחות הן חובה חוקית, אבל לעתים קרובות הן נעשות בצורה לא אפקטיבית. גלו את 5 הטעויות הנפוצות וכיצד להימנע מהן.",
    excerpt: "הדרכות בטיחות הן חובה חוקית, אבל לעתים קרובות הן נעשות בצורה שלא מייצרת שינוי אמיתי. גלו את הטעויות הנפוצות וכיצד להימנע מהן.",
    category: "הדרכות",
    date: "2024-12-15",
    readTime: "5 דקות קריאה",
    image: "https://images.unsplash.com/photo-504328345606-18bbc8c9d7d1?w=800&auto=format&fit=crop",
    imageAlt: "הדרכת בטיחות בסביבת עבודה תעשייתית",
    featured: true,
    keywords: ["הדרכות בטיחות", "טעויות נפוצות", "בטיחות בעבודה", "הדרכת עובדים", "חובה חוקית"],
    content: [
      { type: "h2", text: "למה הדרכות בטיחות נכשלות?" },
      { type: "p", text: "הדרכות בטיחות הן לא רק חובה חוקית - הן ההזדמנות שלכם להציל חיים. אבל למרות השקעה של זמן וכסף, הרבה ארגונים מגלים שההדרכות לא משיגות את המטרה. העובדים לא זוכרים את החומר, לא מיישמים את מה שלמדו, ותאונות ממשיכות לקרות." },
      { type: "p", text: "אחרי שנים של ניסיון בתחום, זיהינו את חמש הטעויות הנפוצות ביותר שמפעלים ועסקים עושים בהדרכות בטיחות - ואיך לתקן אותן." },
      
      { type: "h2", text: "טעות #1: הדרכה גנרית שלא מתאימה לסביבת העבודה" },
      { type: "p", text: "אחת הטעויות הנפוצות ביותר היא שימוש בהדרכות מדף גנריות. עובד במחסן לוגיסטי צריך הדרכה שונה לחלוטין מעובד במפעל כימי. כשההדרכה לא רלוונטית לעבודה היומיומית, העובדים מאבדים עניין ולא מפנימים את המסרים." },
      { type: "h3", text: "הפתרון:" },
      { type: "list", items: [
        "התאימו את התוכן לסיכונים הספציפיים בארגון שלכם",
        "השתמשו בדוגמאות ותמונות מהשטח שלכם",
        "שלבו סיפורי מקרה אמיתיים מהתעשייה שלכם"
      ]},

      { type: "h2", text: "טעות #2: הדרכה ארוכה ומייגעת" },
      { type: "p", text: "הדרכה של 4 שעות רצופות עם מצגת של 100 שקפים? זו דרך בטוחה לאבד את תשומת הלב של העובדים. מחקרים מראים שיכולת הריכוז האנושית מוגבלת ל-20-30 דקות רצופות." },
      { type: "h3", text: "הפתרון:" },
      { type: "list", items: [
        "פצלו את ההדרכה למודולים קצרים של 20-30 דקות",
        "שלבו הפסקות קצרות בין חלקי ההדרכה",
        "הוסיפו אלמנטים אינטראקטיביים כל 10 דקות"
      ]},

      { type: "h2", text: "טעות #3: היעדר תרגול מעשי" },
      { type: "p", text: "הדרכה תיאורטית בלבד לא מספיקה. עובדים צריכים להתנסות בפועל כדי לזכור ולהפנים את הנהלים. בלי תרגול, גם המידע החשוב ביותר נשכח תוך ימים." },
      { type: "h3", text: "הפתרון:" },
      { type: "list", items: [
        "הקדישו לפחות 30% מזמן ההדרכה לתרגול מעשי",
        "ערכו סימולציות של מצבי חירום",
        "תנו לעובדים להדגים שימוש נכון בציוד מגן"
      ]},

      { type: "h2", text: "טעות #4: אי-עדכון התוכן" },
      { type: "p", text: "סביבת העבודה משתנה, רגולציות מתעדכנות, וסיכונים חדשים מתגלים. הדרכה שהייתה רלוונטית לפני שנתיים עלולה להיות מיושנת היום. שימוש בחומרים לא מעודכנים עלול ליצור פערי ידע מסוכנים." },
      { type: "h3", text: "הפתרון:" },
      { type: "list", items: [
        "סקרו ועדכנו את חומרי ההדרכה לפחות פעם בשנה",
        "עקבו אחר שינויים רגולטוריים ושלבו אותם",
        "הוסיפו לקחים מאירועים ותקריות שקרו בארגון"
      ]},

      { type: "h2", text: "טעות #5: היעדר מעקב ומדידה" },
      { type: "p", text: "איך תדעו אם ההדרכה הצליחה? בלי מעקב ומדידה, אתם פועלים בחושך. הרבה ארגונים מסתפקים ברשימת נוכחות ושוכחים לבדוק אם העובדים באמת הפנימו את החומר." },
      { type: "h3", text: "הפתרון:" },
      { type: "list", items: [
        "ערכו מבחנים קצרים בסיום ההדרכה",
        "בצעו ביקורות שטח לבדיקת יישום בפועל",
        "עקבו אחר מדדים כמו תאונות, כמעט-תאונות ודיווחי סיכונים"
      ]},

      { type: "h2", text: "סיכום: הדרכה אפקטיבית = השקעה חכמה" },
      { type: "p", text: "הדרכת בטיחות טובה היא לא הוצאה - היא השקעה. השקעה בהדרכות איכותיות, מותאמות ומעודכנות תחזיר את עצמה בצורת עובדים בריאים, פחות ימי מחלה, פחות תאונות, ותרבות בטיחות חזקה יותר." },
      
      { type: "cta", text: "רוצים לשדרג את הדרכות הבטיחות בארגון שלכם? צרו איתנו קשר לייעוץ ראשוני ללא עלות." }
    ]
  },
  {
    id: 2,
    slug: "safety-investment-roi",
    title: "החזר ROI על השקעה בבטיחות - המספרים מדברים",
    metaTitle: "ROI על השקעה בבטיחות בעבודה | Safe-Group ייעוץ בטיחות",
    metaDescription: "מחקרים מראים שכל שקל שמושקע בבטיחות מחזיר בין 2 ל-6 שקלים. למדו כיצד לחשב את ההחזר על ההשקעה בבטיחות בארגון שלכם.",
    excerpt: "מחקרים מראים שכל שקל שמושקע בבטיחות מחזיר בין 2 ל-6 שקלים. הנה איך לחשב את ה-ROI בארגון שלכם.",
    category: "כלכלה",
    date: "2024-12-10",
    readTime: "7 דקות קריאה",
    image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&auto=format&fit=crop",
    imageAlt: "גרפים וניתוח פיננסי של עלויות בטיחות",
    keywords: ["ROI בטיחות", "עלות תאונת עבודה", "השקעה בבטיחות", "חיסכון בעלויות", "ניהול סיכונים"],
    content: [
      { type: "h2", text: "האם בטיחות באמת משתלמת כלכלית?" },
      { type: "p", text: "רבים מהמנהלים רואים בבטיחות הוצאה הכרחית אבל מכבידה. האמת? בטיחות היא אחת ההשקעות הכדאיות ביותר שעסק יכול לעשות. מחקרים בינלאומיים מראים שוב ושוב שכל שקל שמושקע בבטיחות מחזיר בין 2 ל-6 שקלים." },
      
      { type: "h2", text: "עלות תאונת עבודה - המספרים שמנהלים לא מכירים" },
      { type: "p", text: "כשחושבים על עלות תאונה, רוב האנשים חושבים רק על ימי המחלה. אבל העלות האמיתית גבוהה הרבה יותר:" },
      { type: "list", items: [
        "עלויות רפואיות ותביעות פיצויים",
        "ימי עבודה אבודים של הנפגע",
        "ירידה בפרודוקטיביות של צוות העבודה",
        "עלות הכשרה והחלפת עובד",
        "נזק לציוד ולרכוש",
        "עלויות משפטיות ורגולטוריות",
        "פגיעה במוניטין ובמורל העובדים"
      ]},
      { type: "quote", text: "מחקר של משרד העבודה מעריך שעלות ממוצעת של תאונת עבודה בישראל נעה בין 50,000 ל-500,000 ש\"ח, תלוי בחומרה." },

      { type: "h2", text: "איך מחשבים ROI על בטיחות?" },
      { type: "p", text: "החישוב פשוט יחסית. ראשית, סכמו את כל ההשקעות בבטיחות: הדרכות, ציוד מגן, ייעוץ, שיפורים תשתיתיים. לאחר מכן, חשבו את החיסכון: ירידה בתאונות, פחות ימי מחלה, ירידה בפרמיות ביטוח, הימנעות מקנסות." },
      { type: "h3", text: "נוסחת החישוב:" },
      { type: "p", text: "ROI = (חיסכון שנתי - השקעה שנתית) / השקעה שנתית × 100%" },
      
      { type: "h2", text: "דוגמה מהשטח" },
      { type: "p", text: "מפעל ייצור בינוני השקיע 200,000 ש\"ח בשנה בתוכנית בטיחות מקיפה. התוצאות:" },
      { type: "list", items: [
        "ירידה של 60% בתאונות עבודה",
        "חיסכון של 150,000 ש\"ח בימי מחלה",
        "חיסכון של 100,000 ש\"ח בפרמיות ביטוח",
        "הימנעות מקנס של 80,000 ש\"ח מפיקוח העבודה"
      ]},
      { type: "p", text: "סה\"כ חיסכון: 330,000 ש\"ח. ROI = 65%!" },

      { type: "h2", text: "יתרונות נוספים שקשה למדוד" },
      { type: "p", text: "מעבר לחיסכון הכספי הישיר, יש יתרונות נוספים לתרבות בטיחות חזקה:" },
      { type: "list", items: [
        "שימור עובדים גבוה יותר",
        "משיכת טאלנטים איכותיים",
        "מוניטין טוב יותר מול לקוחות ושותפים",
        "יחסי עבודה טובים יותר",
        "פרודוקטיביות גבוהה יותר"
      ]},

      { type: "h2", text: "איך להתחיל?" },
      { type: "p", text: "הצעד הראשון הוא להבין איפה אתם עומדים. סקר סיכונים מקצועי יזהה את הפערים ויאפשר לכם לתעדף את ההשקעות בצורה חכמה. לא צריך להשקיע מיליונים - לפעמים שינויים קטנים מביאים תוצאות גדולות." },
      
      { type: "cta", text: "רוצים לדעת כמה אתם יכולים לחסוך? צרו קשר לניתוח עלות-תועלת ללא התחייבות." }
    ]
  },
  {
    id: 3,
    slug: "safety-regulations-2024-update",
    title: "עדכוני רגולציה 2024: מה חדש בתקנות הבטיחות",
    metaTitle: "עדכוני תקנות בטיחות 2024 | שינויי רגולציה | Safe-Group",
    metaDescription: "סקירה מקיפה של השינויים הרגולטוריים בתחום הבטיחות בעבודה שנכנסו לתוקף ב-2024. עדכונים חשובים לכל מעסיק ומנהל בטיחות.",
    excerpt: "סקירה מקיפה של השינויים הרגולטוריים שנכנסו לתוקף ב-2024 וההשפעה שלהם על הארגון שלכם.",
    category: "רגולציה",
    date: "2024-12-05",
    readTime: "10 דקות קריאה",
    image: "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800&auto=format&fit=crop",
    imageAlt: "מסמכים משפטיים ותקנות בטיחות",
    featured: true,
    keywords: ["תקנות בטיחות 2024", "רגולציה", "חוק בטיחות בעבודה", "עדכוני חקיקה", "משרד העבודה"],
    content: [
      { type: "h2", text: "מבוא: שנת 2024 מביאה שינויים משמעותיים" },
      { type: "p", text: "שנת 2024 מסמנת נקודת מפנה ברגולציה של בטיחות בעבודה בישראל. משרד העבודה והרווחה הוציא מספר תקנות חדשות והחמרות שכל מעסיק צריך להכיר. במאמר זה נסקור את השינויים העיקריים ומה הם אומרים עבורכם." },

      { type: "h2", text: "עדכון #1: דרישות ממונה בטיחות" },
      { type: "p", text: "תקנות ממונים על הבטיחות התשנו-1996 מגדירות את הדרישות:" },
      { type: "list", items: [
        "מפעל עם 50 עובדים ומעלה - חובה למנות ממונה בטיחות",
        "מבצע בנייה עם 100 עובדים ומעלה - חובה למנות ממונה בטיחות",
        "בענפים מסוכנים (מהתוספת השנייה) - מפקח עבודה רשאי לחייב מינוי גם מתחת ל-50 עובדים",
        "חובת השתלמות שנתית של 4 ימים לעדכון ידע מקצועי"
      ]},
      { type: "h3", text: "מה זה אומר עבורכם?" },
      { type: "p", text: "אם יש לכם 50 עובדים ומעלה, או שאתם בענף מסוכן, חלה עליכם חובת מינוי ממונה בטיחות. וודאו שהממונה שלכם עומד בדרישות ההכשרה." },

      { type: "h2", text: "עדכון #2: סקר סיכונים מחמיר" },
      { type: "p", text: "התקנות החדשות מחייבות סקר סיכונים מקיף יותר:" },
      { type: "list", items: [
        "סקר סיכונים מלא אחת לשנה (במקום שנתיים)",
        "חובת תיעוד ממוחשב של ממצאי הסקר",
        "דרישה להערכת סיכונים פסיכו-סוציאליים",
        "חובת עדכון הסקר לאחר כל תאונה או שינוי מהותי"
      ]},

      { type: "h2", text: "עדכון #3: הדרכות בטיחות מחייבות" },
      { type: "p", text: "שינויים בדרישות ההדרכות:" },
      { type: "list", items: [
        "הדרכת כניסה לעבודה מורחבת לכל עובד חדש",
        "הדרכות רענון שנתיות הפכו לחובה בכל הענפים",
        "חובת הדרכה בשפה שהעובד מבין",
        "דרישה לתיעוד דיגיטלי של הדרכות"
      ]},

      { type: "h2", text: "עדכון #4: עבודה בגובה - החמרות משמעותיות" },
      { type: "p", text: "לאור מספר התאונות הגבוה בעבודה בגובה, נקבעו החמרות:" },
      { type: "list", items: [
        "הגדרת \"עבודה בגובה\" מ-1.5 מטר (במקום 2 מטר)",
        "חובת הכשרה מוסמכת לכל עובד בגובה",
        "דרישות מחמירות לציוד הגנה אישית",
        "בדיקות תקופתיות של ציוד עבודה בגובה"
      ]},

      { type: "h2", text: "עדכון #5: קנסות והטלת עיצומים" },
      { type: "p", text: "ההפרות הפכו יקרות יותר:" },
      { type: "list", items: [
        "הכפלת הקנסות המנהליים על הפרות בטיחות",
        "סמכות מורחבת לסגירת אתרים מסוכנים",
        "פרסום פומבי של מעסיקים מפרים",
        "אחריות אישית מורחבת של מנהלים"
      ]},

      { type: "h2", text: "לוח זמנים ליישום" },
      { type: "p", text: "לא כל השינויים נכנסים לתוקף מיידי. הנה לוח הזמנים:" },
      { type: "list", items: [
        "ינואר 2024: דרישות ממונה בטיחות מורחבות",
        "אפריל 2024: עדכוני עבודה בגובה",
        "יולי 2024: דרישות הדרכות מחודשות",
        "אוקטובר 2024: מערכת קנסות חדשה"
      ]},

      { type: "h2", text: "מה לעשות עכשיו?" },
      { type: "list", items: [
        "סקרו את מצב הבטיחות הנוכחי בארגון",
        "בדקו אם אתם עומדים בדרישות החדשות",
        "תכננו תקציב להתאמות הנדרשות",
        "עדכנו את הנהלים והמסמכים",
        "הדריכו את הצוות על השינויים"
      ]},
      
      { type: "cta", text: "לא בטוחים אם אתם עומדים בדרישות החדשות? פנו אלינו לסקירת ציות מקיפה." }
    ]
  },
  {
    id: 4,
    slug: "building-safety-culture-guide",
    title: "בניית תרבות בטיחות: מדריך מעשי למנהלים",
    metaTitle: "איך לבנות תרבות בטיחות בארגון | מדריך למנהלים | Safe-Group",
    metaDescription: "תרבות בטיחות לא נבנית ביום אחד. המדריך המלא למנהלים: איך ליצור סביבת עבודה בטוחה שבה עובדים משתתפים ומחויבים לבטיחות.",
    excerpt: "תרבות בטיחות לא נבנית ביום אחד. הנה המדריך המלא למנהלים שרוצים לייצר שינוי אמיתי.",
    category: "ניהול",
    date: "2024-11-28",
    readTime: "8 דקות קריאה",
    image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&auto=format&fit=crop",
    imageAlt: "צוות עובדים בפגישת בטיחות",
    keywords: ["תרבות בטיחות", "ניהול בטיחות", "מנהיגות", "שינוי ארגוני", "מעורבות עובדים"],
    content: [
      { type: "h2", text: "מהי תרבות בטיחות?" },
      { type: "p", text: "תרבות בטיחות היא האופן שבו עובדים חושבים, מרגישים ומתנהגים בכל הקשור לבטיחות. זו לא רק מערכת נהלים על הנייר - זו הדרך שבה דברים באמת נעשים כשאף אחד לא מסתכל. בארגון עם תרבות בטיחות חזקה, עובדים דואגים לעצמם ולאחרים, מדווחים על סיכונים, ומרגישים בנוח להעיר הערות על בטיחות." },

      { type: "h2", text: "למה תרבות בטיחות חשובה?" },
      { type: "p", text: "מחקרים מראים שארגונים עם תרבות בטיחות חזקה חווים:" },
      { type: "list", items: [
        "עד 70% פחות תאונות עבודה",
        "ירידה של 50% בימי מחלה",
        "עלייה של 20% בפרודוקטיביות",
        "שימור עובדים גבוה יותר",
        "מוניטין טוב יותר בשוק"
      ]},

      { type: "h2", text: "שלב 1: מחויבות הנהלה" },
      { type: "p", text: "הכל מתחיל מלמעלה. עובדים צופים במנהלים ולומדים מהתנהגותם. אם המנכ\"ל לא עוטה קסדה כשהוא מבקר באתר, מה המסר שהוא שולח?" },
      { type: "h3", text: "פעולות קונקרטיות:" },
      { type: "list", items: [
        "הנהלה בכירה משתתפת בסיורי בטיחות שבועיים",
        "בטיחות כנושא ראשון בישיבות הנהלה",
        "מנהלים מדגמנים שימוש נכון בציוד מגן",
        "הקצאת תקציב ומשאבים לבטיחות"
      ]},

      { type: "h2", text: "שלב 2: תקשורת פתוחה" },
      { type: "p", text: "עובדים צריכים להרגיש בנוח לדווח על סיכונים ו\"כמעט תאונות\" בלי פחד מעונש. זה דורש יצירת אווירה של אמון ופתיחות." },
      { type: "h3", text: "כלים מעשיים:" },
      { type: "list", items: [
        "קו דיווח אנונימי על סיכונים",
        "ישיבות בטיחות שבועיות קצרות (Toolbox Talks)",
        "לוחות מודעות עם עדכוני בטיחות",
        "תגמול על דיווחים איכותיים"
      ]},

      { type: "h2", text: "שלב 3: הכשרה ופיתוח" },
      { type: "p", text: "הדרכות בטיחות צריכות להיות רלוונטיות, מעניינות ותכופות. לא מספיק הדרכה אחת בשנה - בטיחות צריכה להיות נוכחת כל הזמן." },
      { type: "list", items: [
        "הדרכות קצרות ותכופות (מיקרו-למידה)",
        "סימולציות ותרגולים מעשיים",
        "למידה מאירועים ו\"כמעט תאונות\"",
        "הכשרת מנהלים בהובלת בטיחות"
      ]},

      { type: "h2", text: "שלב 4: מעורבות עובדים" },
      { type: "p", text: "בטיחות לא צריכה להיות משהו שנכפה מלמעלה. כשעובדים משתתפים בתהליך, הם מחויבים יותר לתוצאות." },
      { type: "list", items: [
        "ועדות בטיחות עם נציגות עובדים",
        "שמירנים (Champions) בכל מחלקה",
        "הזמנת עובדים לזהות סיכונים ולהציע פתרונות",
        "הכרה ותגמול על יוזמות בטיחות"
      ]},

      { type: "h2", text: "שלב 5: מדידה ושיפור מתמיד" },
      { type: "p", text: "מה שלא נמדד לא משתפר. עקבו אחר מדדים והשתמשו בהם לשיפור מתמיד." },
      { type: "h3", text: "מדדים מומלצים:" },
      { type: "list", items: [
        "מספר תאונות וכמעט-תאונות",
        "ימי עבודה אבודים",
        "אחוז השתתפות בהדרכות",
        "מספר דיווחי סיכונים",
        "סקרי שביעות רצון עובדים מהבטיחות"
      ]},

      { type: "h2", text: "טעויות נפוצות להימנע מהן" },
      { type: "list", items: [
        "להאשים עובדים במקום לחפש סיבות שורשיות",
        "להתעלם מ\"כמעט תאונות\" כי לא קרה כלום",
        "לראות בטיחות כהוצאה ולא כהשקעה",
        "לחשוב שזה עניין של מחלקת הבטיחות בלבד"
      ]},
      
      { type: "cta", text: "רוצים לבנות תרבות בטיחות בארגון שלכם? נשמח ללוות אתכם בתהליך." }
    ]
  },
  {
    id: 5,
    slug: "technology-in-safety-management",
    title: "טכנולוגיה בשירות הבטיחות: הכלים שמשנים את התחום",
    metaTitle: "טכנולוגיה וחדשנות בניהול בטיחות | כלים דיגיטליים | Safe-Group",
    metaDescription: "מאפליקציות דיווח ועד בינה מלאכותית לזיהוי סיכונים - סקירת הטכנולוגיות החדשות שמשנות את עולם הבטיחות בעבודה.",
    excerpt: "מאפליקציות דיווח ועד בינה מלאכותית לזיהוי סיכונים - הטכנולוגיות שמשנות את עולם הבטיחות.",
    category: "טכנולוגיה",
    date: "2024-11-20",
    readTime: "6 דקות קריאה",
    image: "https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&auto=format&fit=crop",
    imageAlt: "טכנולוגיה וחדשנות בניהול בטיחות",
    keywords: ["טכנולוגיה בטיחות", "בינה מלאכותית", "אפליקציות בטיחות", "דיגיטציה", "IoT בטיחות"],
    content: [
      { type: "h2", text: "המהפכה הדיגיטלית בבטיחות בעבודה" },
      { type: "p", text: "עולם הבטיחות עובר מהפכה טכנולוגית. כלים שהיו נראים כמדע בדיוני לפני עשור הפכו למציאות יומיומית במפעלים ואתרי בנייה ברחבי העולם. במאמר זה נסקור את הטכנולוגיות המובילות ואיך הן יכולות לשרת את הארגון שלכם." },

      { type: "h2", text: "אפליקציות דיווח ומעקב" },
      { type: "p", text: "נגמרו הימים של טפסי נייר וקלסרים. אפליקציות מודרניות מאפשרות:" },
      { type: "list", items: [
        "דיווח מיידי על סיכונים מהשטח",
        "צילום ותיעוד ויזואלי של ממצאים",
        "מעקב אחר ביצוע משימות בטיחות",
        "התראות אוטומטיות למנהלים",
        "דוחות ודשבורדים בזמן אמת"
      ]},
      { type: "p", text: "היתרון הגדול: הנתונים זמינים מיידית לניתוח ולקבלת החלטות." },

      { type: "h2", text: "חיישנים ו-IoT (האינטרנט של הדברים)" },
      { type: "p", text: "חיישנים חכמים יכולים לזהות סיכונים לפני שהם הופכים לתאונות:" },
      { type: "list", items: [
        "חיישני גז לזיהוי דליפות מסוכנות",
        "חיישני רעידות לזיהוי בעיות במכונות",
        "מעקב אחר ציוד מגן אישי (האם העובד עוטה קסדה?)",
        "ניטור תנאים סביבתיים (טמפרטורה, לחות, רעש)",
        "התראות על כניסה לאזורים מסוכנים"
      ]},

      { type: "h2", text: "בינה מלאכותית (AI) ולמידת מכונה" },
      { type: "p", text: "AI מביא יכולות חדשות לזיהוי סיכונים:" },
      { type: "list", items: [
        "ניתוח תמונות וזיהוי אוטומטי של מצבים מסוכנים",
        "חיזוי סבירות לתאונות על בסיס נתונים היסטוריים",
        "זיהוי דפוסי התנהגות מסוכנת",
        "אופטימיזציה של משמרות וסידורי עבודה",
        "ניתוח טקסט של דיווחי בטיחות לזיהוי מגמות"
      ]},

      { type: "h2", text: "מציאות מדומה (VR) ורבודה (AR)" },
      { type: "p", text: "VR ו-AR מחוללים מהפכה בהדרכות בטיחות:" },
      { type: "list", items: [
        "סימולציות של מצבי חירום בסביבה בטוחה",
        "הדרכה על ציוד מסוכן בלי סיכון",
        "תרגול תגובה לתרחישים מורכבים",
        "הנחיות בזמן אמת לטכנאים בשטח",
        "למידה חוויתית שמשאירה רושם"
      ]},
      { type: "quote", text: "מחקרים מראים ששימור הידע מהדרכות VR גבוה ב-75% מהדרכות מסורתיות." },

      { type: "h2", text: "דרונים לבדיקות בטיחות" },
      { type: "p", text: "דרונים מאפשרים בדיקות במקומות מסוכנים או קשי גישה:" },
      { type: "list", items: [
        "בדיקת גגות ומבנים גבוהים",
        "צילום אתרי בנייה לניתוח סיכונים",
        "בדיקת תשתיות (מגדלי חשמל, ארובות)",
        "חיפוש והצלה באזורים מסוכנים"
      ]},

      { type: "h2", text: "איך להתחיל?" },
      { type: "p", text: "לא חייבים להטמיע את כל הטכנולוגיות בבת אחת. הנה גישה מומלצת:" },
      { type: "list", items: [
        "זהו את הכאבים העיקריים בניהול הבטיחות",
        "חפשו פתרון טכנולוגי ממוקד לבעיה ספציפית",
        "התחילו עם פיילוט קטן",
        "מדדו תוצאות והחליטו על הרחבה"
      ]},
      
      { type: "cta", text: "מעוניינים לשמוע על פתרונות טכנולוגיים לבטיחות? נשמח לייעץ." }
    ]
  },
  {
    id: 6,
    slug: "safety-audit-checklist",
    title: "הכנה לביקורת בטיחות: רשימת תיוג מלאה",
    metaTitle: "רשימת תיוג לביקורת בטיחות | איך להתכונן | Safe-Group",
    metaDescription: "ביקורת בטיחות בפתח? הורידו את רשימת התיוג המלאה שלנו והתכוננו בצורה מושלמת לביקורת של פיקוח העבודה.",
    excerpt: "ביקורת בטיחות בפתח? הנה רשימת התיוג המלאה שתעזור לכם להתכונן בצורה הטובה ביותר.",
    category: "מדריכים",
    date: "2024-10-15",
    readTime: "4 דקות קריאה",
    image: "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800&auto=format&fit=crop",
    imageAlt: "רשימת תיוג ומסמכי בטיחות",
    keywords: ["ביקורת בטיחות", "רשימת תיוג", "פיקוח עבודה", "הכנה לביקורת", "ציות לחוק"],
    content: [
      { type: "h2", text: "מבוא: ביקורת בטיחות - לא צריך לפחד" },
      { type: "p", text: "ביקורת בטיחות יכולה להיות חוויה מלחיצה, אבל עם הכנה נכונה היא יכולה להפוך להזדמנות להראות את המחויבות שלכם לבטיחות. הנה רשימת התיוג המלאה שתעזור לכם להתכונן." },

      { type: "h2", text: "מסמכים נדרשים" },
      { type: "list", items: [
        "מינוי ממונה בטיחות בתוקף",
        "סקר סיכונים עדכני (עד שנה)",
        "תיק מפעל/אתר מעודכן",
        "רישומי הדרכות בטיחות לכל העובדים",
        "פרוטוקולי ועדת בטיחות",
        "דוחות ביקורות פנימיות",
        "אישורי ציוד ומכונות",
        "רישיונות עבודה רלוונטיים"
      ]},

      { type: "h2", text: "ציוד מגן אישי" },
      { type: "list", items: [
        "כל העובדים מצוידים בציוד מגן מתאים",
        "הציוד בתוקף ובמצב תקין",
        "יש תיעוד של הנפקת ציוד",
        "עובדים הודרכו על שימוש נכון",
        "יש מלאי רזרבי זמין"
      ]},

      { type: "h2", text: "סביבת העבודה" },
      { type: "list", items: [
        "מעברים פנויים וללא מכשולים",
        "שילוט בטיחות נראה ועדכני",
        "תאורה מספקת בכל האזורים",
        "אוורור תקין",
        "סדר וניקיון",
        "ציוד כיבוי אש נגיש ובתוקף",
        "ערכות עזרה ראשונה מאובזרות"
      ]},

      { type: "h2", text: "מכונות וציוד" },
      { type: "list", items: [
        "כל המכונות עם מיגונים תקינים",
        "כפתורי חירום פועלים",
        "תיעוד בדיקות תקופתיות",
        "הוראות הפעלה בעברית צמודות למכונה",
        "רק עובדים מוסמכים מפעילים ציוד מסוכן"
      ]},

      { type: "h2", text: "חומרים מסוכנים (אם רלוונטי)" },
      { type: "list", items: [
        "גליונות בטיחות (MSDS) לכל החומרים",
        "אחסון תקני ומסומן",
        "ציוד התגוננות מתאים",
        "נהלי טיפול בשפכים",
        "ערכת ספיגה לדליפות"
      ]},

      { type: "h2", text: "מוכנות לחירום" },
      { type: "list", items: [
        "תוכנית פינוי מעודכנת",
        "יציאות חירום מסומנות ופנויות",
        "תרגיל פינוי בוצע בשנה האחרונה",
        "צוות כיבוי/עזרה ראשונה מוכשר",
        "אמצעי התרעה פועלים"
      ]},

      { type: "h2", text: "טיפים ליום הביקורת" },
      { type: "list", items: [
        "מנו מישהו ללוות את המבקר",
        "הכינו את כל המסמכים מראש בקלסר מסודר",
        "ענו על שאלות בכנות - אל תנסו להסתיר",
        "רשמו את ההערות שניתנות",
        "בקשו הסברים אם משהו לא ברור"
      ]},
      
      { type: "cta", text: "צריכים עזרה בהכנה לביקורת? אנחנו יכולים לערוך ביקורת פנימית מכינה." }
    ]
  },

  // ===== NEW SEO-OPTIMIZED POSTS =====
  {
    id: 7,
    slug: "construction-site-safety-training-guide",
    title: "מדריך מלא להדרכות בטיחות באתרי בנייה",
    metaTitle: "הדרכות בטיחות באתרי בנייה | מדריך מקיף 2024 | Safe-Group",
    metaDescription: "מדריך מקיף להדרכות בטיחות באתרי בנייה: עבודה בגובה, ציוד מגן, סיכונים ייחודיים. כל מה שקבלן או מנהל אתר צריך לדעת.",
    excerpt: "אתרי בנייה הם מהמקומות המסוכנים ביותר לעבודה. הנה המדריך המלא להדרכות בטיחות שיגנו על העובדים שלכם.",
    category: "הדרכות",
    date: "2024-12-18",
    readTime: "9 דקות קריאה",
    image: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&auto=format&fit=crop",
    imageAlt: "עובדי בנייה עם ציוד מגן באתר בנייה",
    featured: true,
    keywords: ["הדרכת בטיחות בנייה", "עבודה בגובה", "בטיחות אתר בנייה", "קבלן", "ציוד מגן בנייה"],
    content: [
      { type: "h2", text: "למה בטיחות באתרי בנייה קריטית?" },
      { type: "p", text: "ענף הבנייה מוביל בנתוני תאונות עבודה קטלניות בישראל. כמעט מחצית ממקרי המוות בתאונות עבודה מתרחשים באתרי בנייה. הסיבות מגוונות: עבודה בגובה, מכונות כבדות, חומרים מסוכנים, ותנאי עבודה משתנים. הדרכת בטיחות איכותית יכולה להציל חיים - פשוטו כמשמעו." },
      
      { type: "h2", text: "חובות חוקיות להדרכות באתרי בנייה" },
      { type: "p", text: "החוק מחייב את הקבלן והמזמין בהדרכות ספציפיות:" },
      { type: "list", items: [
        "הדרכת כניסה לאתר לכל עובד חדש",
        "הדרכת עבודה בגובה לכל העוסקים בכך",
        "הדרכה על ציוד מגן אישי",
        "הדרכה על סיכונים ספציפיים לאתר",
        "הדרכות רענון שנתיות"
      ]},

      { type: "h2", text: "הדרכת עבודה בגובה - הנושא הקריטי ביותר" },
      { type: "p", text: "נפילה מגובה היא סיבת המוות המובילה באתרי בנייה. הדרכת עבודה בגובה חייבת לכלול:" },
      { type: "list", items: [
        "זיהוי מצבים המוגדרים כעבודה בגובה (מ-1.5 מטר)",
        "בחירה נכונה של אמצעי גישה (פיגום, סולם, מעלית הרמה)",
        "שימוש נכון ברתמות ואביזרי עיגון",
        "בדיקת ציוד לפני השימוש",
        "זיהוי סיכונים ייחודיים (רוחות, משטחים רטובים)",
        "נהלי חירום והצלה"
      ]},
      { type: "h3", text: "דרישות הכשרה:" },
      { type: "p", text: "עובד בגובה חייב לעבור הכשרה מוסמכת של לפחות 8 שעות, כולל תרגול מעשי. ההכשרה בתוקף לשנתיים ודורשת רענון." },

      { type: "h2", text: "ציוד מגן אישי באתר בנייה" },
      { type: "p", text: "כל עובד באתר חייב להיות מצויד ומודרך בשימוש ב:" },
      { type: "list", items: [
        "קסדת מגן - חובה בכל אזור האתר",
        "נעלי בטיחות עם כיפת פלדה",
        "וסט זוהר בצבע בולט",
        "כפפות עבודה מתאימות למשימה",
        "משקפי מגן (לפי הצורך)",
        "אוזניות/אטמי אוזניים באזורי רעש"
      ]},

      { type: "h2", text: "סיכונים ייחודיים באתרי בנייה" },
      { type: "h3", text: "חפירות ותעלות:" },
      { type: "p", text: "עבודה בחפירות דורשת הדרכה על: דיפון תקני, בדיקת יציבות קירות, איתור תשתיות תת-קרקעיות, ונהלי כניסה ויציאה." },
      { type: "h3", text: "עגורנים והרמה:" },
      { type: "p", text: "הפעלת עגורן דורשת רישיון ספציפי. יש להדריך את כל העובדים על: אזורי סכנה מתחת למשאות, איתותים, ואיסור שהייה באזורי הרמה." },
      { type: "h3", text: "עבודה עם חשמל:" },
      { type: "p", text: "חיבורים זמניים, כבלים חשופים וציוד חשמלי באתר יוצרים סיכונים ייחודיים. נדרשת הדרכה על זיהוי סיכונים ושימוש בטוח." },

      { type: "h2", text: "תדירות הדרכות מומלצת" },
      { type: "list", items: [
        "הדרכת כניסה לאתר - לכל עובד חדש ביום הראשון",
        "הדרכת בוקר יומית (Toolbox Talk) - 5-10 דקות על נושא ספציפי",
        "הדרכות נושאיות - לפי התקדמות הפרויקט",
        "רענון חודשי - על נהלי חירום",
        "הדרכה שנתית מקיפה - לכל העובדים"
      ]},

      { type: "h2", text: "טיפים למנהל אתר" },
      { type: "list", items: [
        "תעדו כל הדרכה עם חתימות",
        "וודאו שקבלני משנה מדריכים את עובדיהם",
        "עדכנו הדרכות כשהאתר משתנה",
        "שלבו עובדים ותיקים כמנטורים",
        "הפכו את הבטיחות לשיח יומי"
      ]},
      
      { type: "cta", text: "מחפשים הדרכות בטיחות מותאמות לאתר הבנייה שלכם? צרו קשר לתיאום." }
    ]
  },
  {
    id: 8,
    slug: "factory-risk-assessment-complete-guide",
    title: "סקר סיכונים למפעל - המדריך המלא",
    metaTitle: "סקר סיכונים למפעל | מדריך מלא ומעשי | Safe-Group",
    metaDescription: "כל מה שצריך לדעת על סקר סיכונים למפעל: מתי חייבים לעשות, מה כולל, איך מתכוננים, ומה קורה אחרי. המדריך המקיף ביותר בעברית.",
    excerpt: "סקר סיכונים הוא הבסיס לניהול בטיחות נכון. למדו מה הוא כולל, מתי לעשות אותו, ואיך להפיק ממנו את המקסימום.",
    category: "מדריכים",
    date: "2024-12-16",
    readTime: "11 דקות קריאה",
    image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&auto=format&fit=crop",
    imageAlt: "מפעל תעשייתי עם מכונות וציוד",
    keywords: ["סקר סיכונים", "מפעל", "תעשייה", "ניהול סיכונים", "בטיחות במפעל"],
    content: [
      { type: "h2", text: "מהו סקר סיכונים ולמה הוא חשוב?" },
      { type: "p", text: "סקר סיכונים הוא תהליך שיטתי לזיהוי, הערכה ותעדוף סיכונים בסביבת העבודה. זהו לא רק דרישה חוקית - זהו הכלי המרכזי שלכם למניעת תאונות והגנה על העובדים. סקר סיכונים טוב מאפשר לכם להקצות משאבים בצורה חכמה ולהתמקד בסיכונים המשמעותיים ביותר." },
      
      { type: "h2", text: "מתי חייבים לערוך סקר סיכונים?" },
      { type: "list", items: [
        "בהקמת מפעל חדש או תחילת פעילות",
        "פעם בשנה לפחות (לפי דרישות החוק)",
        "בהכנסת מכונה או תהליך חדש",
        "לאחר תאונה או אירוע מסוכן",
        "בשינוי מהותי בתהליכי הייצור",
        "במעבר למבנה או אתר חדש"
      ]},

      { type: "h2", text: "השלבים בביצוע סקר סיכונים" },
      { type: "h3", text: "שלב 1: הכנה ותכנון" },
      { type: "p", text: "לפני היציאה לשטח, יש להכין:" },
      { type: "list", items: [
        "מיפוי כל אזורי העבודה במפעל",
        "רשימת תהליכים ומכונות",
        "סקירת תאונות ודיווחי סיכונים קודמים",
        "רשימת חומרים מסוכנים",
        "הזמנת נציגי עובדים להשתתף"
      ]},

      { type: "h3", text: "שלב 2: זיהוי הסיכונים" },
      { type: "p", text: "סיור מקיף בכל אזורי המפעל לזיהוי:" },
      { type: "list", items: [
        "סיכונים פיזיים (רעש, חום, קור, קרינה)",
        "סיכונים מכניים (מכונות, חלקים נעים)",
        "סיכונים כימיים (חומרים מסוכנים, אבק)",
        "סיכונים ארגונומיים (הרמת משאות, תנוחות)",
        "סיכוני שריפה ופיצוץ",
        "סיכונים חשמליים"
      ]},

      { type: "h3", text: "שלב 3: הערכת הסיכונים" },
      { type: "p", text: "לכל סיכון שזוהה, מעריכים:" },
      { type: "list", items: [
        "חומרה: מה יכול לקרות אם הסיכון יתממש?",
        "הסתברות: מה הסיכוי שזה יקרה?",
        "חשיפה: כמה עובדים חשופים ובאיזו תדירות?"
      ]},
      { type: "p", text: "הציון המשוקלל קובע את עדיפות הטיפול. סיכונים ברמה גבוהה דורשים טיפול מיידי." },

      { type: "h3", text: "שלב 4: קביעת אמצעי בקרה" },
      { type: "p", text: "לכל סיכון משמעותי, קובעים אמצעי בקרה לפי היררכיית הבקרות:" },
      { type: "list", items: [
        "הסרה: האם ניתן לבטל את הסיכון לחלוטין?",
        "החלפה: האם ניתן להחליף בחומר/תהליך בטוח יותר?",
        "הנדסה: האם ניתן לבודד או למגן?",
        "ניהול: נהלים, הדרכות, שילוט",
        "ציוד מגן אישי: הקו האחרון של ההגנה"
      ]},

      { type: "h3", text: "שלב 5: תיעוד ודיווח" },
      { type: "p", text: "הסקר חייב להיות מתועד ולכלול:" },
      { type: "list", items: [
        "תאריך הסקר ושם הסוקר",
        "רשימת כל הסיכונים שזוהו",
        "דירוג כל סיכון",
        "אמצעי הבקרה הנדרשים",
        "אחראי ולוח זמנים לביצוע",
        "מועד הסקר הבא"
      ]},

      { type: "h2", text: "טעויות נפוצות בסקרי סיכונים" },
      { type: "list", items: [
        "התמקדות רק בסיכונים ברורים והתעלמות מסיכונים \"שקטים\"",
        "אי-שיתוף עובדים בתהליך",
        "תיעוד לקוי או חסר",
        "אי-מעקב אחר יישום ההמלצות",
        "שימוש בסקר גנרי במקום מותאם",
        "אי-עדכון הסקר בשינויים"
      ]},

      { type: "h2", text: "מה קורה אחרי הסקר?" },
      { type: "p", text: "הסקר הוא רק ההתחלה. הערך האמיתי הוא בפעולה:" },
      { type: "list", items: [
        "הציגו את הממצאים להנהלה",
        "קבעו תקציב ולוחות זמנים לתיקונים",
        "עדכנו את העובדים על הסיכונים והאמצעים",
        "בצעו מעקב שוטף אחר הביצוע",
        "תעדו את כל הפעולות שננקטו"
      ]},
      
      { type: "cta", text: "צריכים סקר סיכונים מקצועי למפעל שלכם? צרו קשר להצעה מותאמת." }
    ]
  },
  {
    id: 9,
    slug: "warehouse-logistics-safety",
    title: "בטיחות במחסנים ומרכזי לוגיסטיקה",
    metaTitle: "בטיחות במחסנים ולוגיסטיקה | מדריך מקיף | Safe-Group",
    metaDescription: "המדריך המלא לבטיחות במחסנים ומרכזי לוגיסטיקה: מלגזות, מדפים, סידור מחסן, ציוד מגן. כל מה שמנהל מחסן צריך לדעת.",
    excerpt: "מחסנים ומרכזי לוגיסטיקה מציבים אתגרי בטיחות ייחודיים. הנה המדריך המלא לניהול בטוח של המחסן שלכם.",
    category: "ענפים",
    date: "2024-12-14",
    readTime: "8 דקות קריאה",
    image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&auto=format&fit=crop",
    imageAlt: "מחסן לוגיסטי עם מדפים ומלגזות",
    keywords: ["בטיחות מחסן", "מלגזה", "לוגיסטיקה", "מדפים", "בטיחות אחסון"],
    content: [
      { type: "h2", text: "הסיכונים הייחודיים במחסנים" },
      { type: "p", text: "מחסנים ומרכזי לוגיסטיקה הם סביבה דינמית עם תנועה מתמדת של סחורות, כלי רכב ואנשים. הסיכונים העיקריים כוללים התנגשויות עם מלגזות, נפילת חבילות ממדפים, מעידות והחלקות, ופציעות גב מהרמת משאות. ניהול נכון של הסיכונים הללו יכול להפחית תאונות דרמטית." },

      { type: "h2", text: "בטיחות מלגזות - הנושא המרכזי" },
      { type: "p", text: "מלגזות הן הסיבה לחלק ניכר מהתאונות במחסנים. הנה מה שחייבים לדעת:" },
      { type: "h3", text: "דרישות לנהג מלגזה:" },
      { type: "list", items: [
        "רישיון להפעלת מלגזה בתוקף",
        "הכשרה ספציפית לסוג המלגזה",
        "הדרכה על המחסן הספציפי",
        "בדיקת תקינות יומית של המלגזה"
      ]},
      { type: "h3", text: "כללי תנועה במחסן:" },
      { type: "list", items: [
        "מהירות מוגבלת (בדרך כלל 10 קמ\"ש)",
        "עדיפות להולכי רגל תמיד",
        "נסיעה לאחור עם משקיף או מראות",
        "איסור הרמת אנשים על המזלגות",
        "שימוש בחגורת בטיחות בכל נסיעה"
      ]},

      { type: "h2", text: "בטיחות מדפים" },
      { type: "p", text: "קריסת מדפים יכולה לגרום לתוצאות הרסניות. וודאו:" },
      { type: "list", items: [
        "בדיקה תקופתית של מצב המדפים (שנתית לפחות)",
        "עמידה בעומסים המותרים המצוינים",
        "עיגון נכון לרצפה/קיר",
        "מיגון רגליים מפני פגיעות מלגזה",
        "אין מדפים פגומים או מעוקמים בשימוש",
        "סידור נכון של סחורה (כבד למטה)"
      ]},

      { type: "h2", text: "הרמת משאות ידנית" },
      { type: "p", text: "פציעות גב נפוצות מאוד בעבודת מחסן. יש להדריך עובדים על:" },
      { type: "list", items: [
        "טכניקה נכונה להרמה (כיפוף ברכיים, גב ישר)",
        "משקל מקסימלי להרמה ידנית (25 ק\"ג)",
        "הרמה בזוגות למשאות כבדים",
        "שימוש בעזרים מכניים (עגלות, מעליות)",
        "הימנעות מסיבובי גב תוך כדי הרמה"
      ]},

      { type: "h2", text: "סידור וארגון המחסן" },
      { type: "p", text: "מחסן מאורגן הוא מחסן בטוח:" },
      { type: "list", items: [
        "מעברים רחבים ופנויים (לפחות 1.2 מ' להולכי רגל)",
        "הפרדה בין אזורי הולכי רגל למלגזות",
        "שילוט ברור של מעברים ואזורים",
        "תאורה מספקת בכל אזור",
        "רצפה נקייה וללא מכשולים",
        "סימון ברור של יציאות חירום"
      ]},

      { type: "h2", text: "ציוד מגן אישי במחסן" },
      { type: "list", items: [
        "נעלי בטיחות עם כיפת פלדה - חובה לכולם",
        "וסט זוהר בצבע בולט",
        "כפפות עבודה להגנה על הידיים",
        "קסדה באזורי עבודה בגובה או ליד מלגזות"
      ]},

      { type: "h2", text: "נהלי חירום במחסן" },
      { type: "list", items: [
        "יציאות חירום מסומנות וללא חסימה",
        "ציוד כיבוי אש נגיש ותקין",
        "ערכת עזרה ראשונה מאובזרת",
        "נהלי פינוי מתורגלים",
        "טלפון חירום זמין"
      ]},
      
      { type: "cta", text: "רוצים לשפר את הבטיחות במחסן שלכם? צרו קשר לייעוץ מקצועי." }
    ]
  },
  {
    id: 10,
    slug: "iso-45001-certification-guide",
    title: "ISO 45001 - המדריך המלא להסמכה",
    metaTitle: "הסמכת ISO 45001 | מדריך מלא ליישום | Safe-Group",
    metaDescription: "כל מה שצריך לדעת על תקן ISO 45001 לניהול בטיחות ובריאות תעסוקתית: דרישות, שלבי יישום, עלויות ויתרונות ההסמכה.",
    excerpt: "תקן ISO 45001 הוא התקן הבינלאומי המוביל לניהול בטיחות. למדו מה הוא דורש ואיך להשיג הסמכה.",
    category: "רגולציה",
    date: "2024-12-12",
    readTime: "12 דקות קריאה",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&auto=format&fit=crop",
    imageAlt: "תעודת הסמכה ISO 45001",
    featured: true,
    keywords: ["ISO 45001", "הסמכה", "תקן בטיחות", "ניהול בטיחות", "מערכת ניהול"],
    content: [
      { type: "h2", text: "מהו תקן ISO 45001?" },
      { type: "p", text: "ISO 45001 הוא התקן הבינלאומי לניהול בטיחות ובריאות תעסוקתית (OH&S). הוא מספק מסגרת לבניית מערכת ניהול בטיחות שיטתית שמטרתה למנוע פציעות ומחלות הקשורות לעבודה, ולספק סביבת עבודה בטוחה ובריאה. התקן פורסם ב-2018 והחליף את תקן OHSAS 18001." },

      { type: "h2", text: "למה לשאוף להסמכה?" },
      { type: "list", items: [
        "הפחתה משמעותית בתאונות עבודה ומחלות מקצוע",
        "עמידה בדרישות חוקיות ורגולטוריות",
        "שיפור המוניטין מול לקוחות ושותפים",
        "יתרון תחרותי במכרזים (במיוחד בינלאומיים)",
        "הפחתת עלויות ביטוח ופיצויים",
        "שיפור מורל העובדים ושימורם",
        "תהליכים מסודרים ויעילים יותר"
      ]},

      { type: "h2", text: "עקרונות הליבה של התקן" },
      { type: "h3", text: "1. הקשר הארגון" },
      { type: "p", text: "הבנת הארגון, מחזיקי העניין שלו, והסביבה שבה הוא פועל. זיהוי סיכונים והזדמנויות." },
      { type: "h3", text: "2. מנהיגות" },
      { type: "p", text: "מחויבות ההנהלה הבכירה לבטיחות. קביעת מדיניות OH&S ברורה והקצאת משאבים." },
      { type: "h3", text: "3. תכנון" },
      { type: "p", text: "זיהוי סיכונים, הערכתם, וקביעת יעדים מדידים לשיפור הבטיחות." },
      { type: "h3", text: "4. תמיכה" },
      { type: "p", text: "הקצאת משאבים, הכשרות, תקשורת, ותיעוד המערכת." },
      { type: "h3", text: "5. תפעול" },
      { type: "p", text: "יישום בקרות, ניהול שינויים, והיערכות לחירום." },
      { type: "h3", text: "6. הערכת ביצועים" },
      { type: "p", text: "ניטור, מדידה, ביקורות פנימיות, וסקירת הנהלה." },
      { type: "h3", text: "7. שיפור" },
      { type: "p", text: "טיפול באי-התאמות, פעולות מתקנות, ושיפור מתמיד." },

      { type: "h2", text: "שלבי היישום" },
      { type: "h3", text: "שלב 1: אבחון מצב קיים (1-2 חודשים)" },
      { type: "list", items: [
        "מיפוי המצב הנוכחי מול דרישות התקן",
        "זיהוי פערים",
        "הכנת תוכנית עבודה"
      ]},
      { type: "h3", text: "שלב 2: תכנון ובניית המערכת (2-4 חודשים)" },
      { type: "list", items: [
        "כתיבת מדיניות OH&S",
        "הגדרת תפקידים ואחריות",
        "בניית נהלים ותהליכים",
        "הטמעת מנגנוני בקרה"
      ]},
      { type: "h3", text: "שלב 3: הטמעה והפעלה (3-6 חודשים)" },
      { type: "list", items: [
        "הדרכת עובדים",
        "הטמעת הנהלים בפועל",
        "ביצוע ביקורות פנימיות",
        "סקירת הנהלה"
      ]},
      { type: "h3", text: "שלב 4: הסמכה (1-2 חודשים)" },
      { type: "list", items: [
        "בחירת גוף מסמיך",
        "מבדק הסמכה שלב 1 (בדיקת תיעוד)",
        "מבדק הסמכה שלב 2 (בדיקת יישום)",
        "קבלת תעודת הסמכה"
      ]},

      { type: "h2", text: "משאבים נדרשים" },
      { type: "p", text: "ההשקעה הנדרשת משתנה לפי גודל הארגון ומורכבותו ותלויה בספק שנבחר. לקבלת הערכה מותאמת לארגון שלכם, צרו קשר לייעוץ חינם." },

      { type: "h2", text: "טיפים להצלחה" },
      { type: "list", items: [
        "הבטיחו מחויבות אמיתית של ההנהלה הבכירה",
        "שלבו את העובדים בתהליך מההתחלה",
        "אל תיצרו מערכת נפרדת - שלבו בתהליכים הקיימים",
        "התמקדו בתועלת ולא רק בתיעוד",
        "בחרו יועץ מנוסה עם ניסיון בתחום שלכם"
      ]},
      
      { type: "cta", text: "מתכננים הסמכת ISO 45001? נשמח ללוות אתכם בתהליך." }
    ]
  },
  {
    id: 11,
    slug: "workplace-safety-law-explained",
    title: "חוק ארגון הפיקוח על העבודה - פשוט מוסבר",
    metaTitle: "חוק בטיחות בעבודה | הסבר פשוט ומקיף | Safe-Group",
    metaDescription: "הסבר פשוט וברור של חוק ארגון הפיקוח על העבודה: חובות המעסיק, זכויות העובד, קנסות ועונשים. כל מה שמנהל צריך לדעת.",
    excerpt: "חוק הפיקוח על העבודה הוא הבסיס לבטיחות בישראל. הנה הסבר פשוט וברור של מה שהוא דורש מכם.",
    category: "רגולציה",
    date: "2024-12-08",
    readTime: "7 דקות קריאה",
    image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&auto=format&fit=crop",
    imageAlt: "ספרי חוק ומסמכים משפטיים",
    keywords: ["חוק בטיחות", "פיקוח עבודה", "חובות מעסיק", "חוק ארגון", "רגולציה"],
    content: [
      { type: "h2", text: "מבוא: החוק בקצרה" },
      { type: "p", text: "חוק ארגון הפיקוח על העבודה, התשי\"ד-1954, הוא החוק המרכזי שמסדיר את נושא הבטיחות בעבודה בישראל. מטרתו להבטיח סביבת עבודה בטוחה לכל עובד. להלן הסבר פשוט של הנקודות העיקריות." },

      { type: "h2", text: "חובות המעסיק" },
      { type: "h3", text: "חובת הבטחת הבטיחות" },
      { type: "p", text: "המעסיק אחראי לדאוג לבטיחות העובדים במקום העבודה. זה כולל סביבת עבודה בטוחה, ציוד תקין, והגנה מפני סיכונים." },
      { type: "h3", text: "מינוי ממונה בטיחות" },
      { type: "p", text: "מעסיק עם 50 עובדים ומעלה חייב למנות ממונה בטיחות מוסמך. בענפים מסוכנים מהתוספת השנייה לתקנות - מפקח עבודה רשאי לחייב מינוי גם מתחת ל-50 עובדים." },
      { type: "h3", text: "הקמת ועדת בטיחות" },
      { type: "p", text: "במקום עבודה עם 50 עובדים ומעלה, יש להקים ועדת בטיחות עם נציגות שווה של הנהלה ועובדים." },
      { type: "h3", text: "עריכת סקר סיכונים" },
      { type: "p", text: "חובה לערוך סקר סיכונים ולפעול לפי ממצאיו. הסקר צריך להיות מעודכן ולשקף את המצב בפועל." },
      { type: "h3", text: "הדרכות בטיחות" },
      { type: "p", text: "חובה להדריך כל עובד על סיכוני העבודה ודרכי ההתגוננות. ההדרכות חייבות להיות מתועדות." },
      { type: "h3", text: "אספקת ציוד מגן" },
      { type: "p", text: "על המעסיק לספק ציוד מגן אישי מתאים ולוודא שימוש בו." },

      { type: "h2", text: "חובות העובד" },
      { type: "list", items: [
        "לציית להוראות הבטיחות של המעסיק",
        "להשתמש בציוד מגן שסופק לו",
        "לדווח על סיכונים ומפגעים",
        "לא לסכן את עצמו או אחרים",
        "להשתתף בהדרכות בטיחות"
      ]},

      { type: "h2", text: "זכויות העובד" },
      { type: "list", items: [
        "סביבת עבודה בטוחה",
        "קבלת ציוד מגן ללא תשלום",
        "הדרכה על סיכונים",
        "סירוב לעבוד במצב מסוכן (בתנאים מסוימים)",
        "דיווח על הפרות ללא חשש מנקמה"
      ]},

      { type: "h2", text: "סמכויות מפקח העבודה" },
      { type: "p", text: "למפקחי העבודה סמכויות נרחבות:" },
      { type: "list", items: [
        "כניסה לכל מקום עבודה ללא הודעה מראש",
        "דרישת מסמכים ומידע",
        "מתן צווי שיפור לתיקון ליקויים",
        "צו סגירה של מקום או ציוד מסוכן",
        "הגשת כתבי אישום"
      ]},

      { type: "h2", text: "קנסות ועונשים" },
      { type: "p", text: "הפרת חוקי הבטיחות עלולה לגרור:" },
      { type: "list", items: [
        "קנסות מנהליים של עשרות אלפי שקלים",
        "צו הפסקת עבודה",
        "העמדה לדין פלילי",
        "מאסר בפועל במקרים חמורים",
        "אחריות אישית של מנהלים"
      ]},
      { type: "quote", text: "במקרים של תאונות קטלניות, מנהלים הורשעו והוטלו עליהם עונשי מאסר בפועל." },

      { type: "h2", text: "תקנות חשובות מכוח החוק" },
      { type: "list", items: [
        "תקנות הבטיחות בעבודה (ציוד מגן אישי)",
        "תקנות הבטיחות בעבודה (עבודות בנייה)",
        "תקנות הבטיחות בעבודה (עבודה בגובה)",
        "תקנות ארגון הפיקוח על העבודה (ממונים על הבטיחות)",
        "פקודת הבטיחות בעבודה (נוסח חדש)"
      ]},

      { type: "h2", text: "טיפ חשוב" },
      { type: "p", text: "אל תחכו לביקורת או תאונה כדי לטפל בבטיחות. עמידה בדרישות החוק היא המינימום - השאיפה צריכה להיות יצירת סביבת עבודה בטוחה באמת, לא רק \"לצאת ידי חובה\"." },
      
      { type: "cta", text: "לא בטוחים אם אתם עומדים בכל דרישות החוק? פנו אלינו לבדיקת ציות מקצועית." }
    ]
  },
  {
    id: 12,
    slug: "safety-officer-faq",
    title: "10 שאלות נפוצות על ממונה בטיחות",
    metaTitle: "שאלות נפוצות על ממונה בטיחות | FAQ מקיף | Safe-Group",
    metaDescription: "תשובות ל-10 השאלות הנפוצות ביותר על ממונה בטיחות: מתי חייבים, מה הכישורים, מה האחריות, וכמה עולה. הכל במקום אחד.",
    excerpt: "כל מה שרציתם לדעת על ממונה בטיחות ולא ידעתם את מי לשאול - 10 התשובות לשאלות הנפוצות ביותר.",
    category: "מדריכים",
    date: "2024-12-06",
    readTime: "6 דקות קריאה",
    image: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=800&auto=format&fit=crop",
    imageAlt: "ממונה בטיחות בפגישה עם עובדים",
    keywords: ["ממונה בטיחות", "דרישות חוק", "הכשרת ממונה", "תפקיד ממונה", "שאלות נפוצות"],
    content: [
      { type: "h2", text: "1. מתי חייבים למנות ממונה בטיחות?" },
      { type: "p", text: "לפי תקנה 4 לתקנות ממונים על הבטיחות התשנ\"ו-1996, מקום עבודה עם 50 עובדים ומעלה חייב למנות ממונה בטיחות. מבצע בנייה חייב במינוי כאשר מספר העובדים מגיע ל-100 ומעלה. בענפים מסוכנים מהתוספת השנייה - מפקח עבודה רשאי לחייב מינוי גם מתחת לסף." },

      { type: "h2", text: "2. מה הכישורים הנדרשים?" },
      { type: "list", items: [
        "השכלה מינימלית של 12 שנות לימוד",
        "קורס ממוני בטיחות מוכר (כ-180 שעות)",
        "בחינות הסמכה",
        "השתלמויות רענון שנתיות (16 שעות)"
      ]},

      { type: "h2", text: "3. האם ממונה יכול לעבוד בחלקיות משרה?" },
      { type: "p", text: "כן, אפשר גם ממונה במשרה חלקית. היקף המשרה נקבע לפי גודל הארגון והסיכונים בו. חלופה נוספת היא שימוש בשירותי ממונה חיצוני מחברת ייעוץ." },

      { type: "h2", text: "4. מה תפקידיו העיקריים של הממונה?" },
      { type: "list", items: [
        "ביצוע סיורי בטיחות שוטפים",
        "עריכת/עדכון סקר סיכונים",
        "הכנת תוכניות הדרכה ופיקוח על ביצוען",
        "חקירת תאונות והפקת לקחים",
        "ייעוץ להנהלה בנושאי בטיחות",
        "טיפול בדרישות הרגולטוריות",
        "ניהול ועדת הבטיחות",
        "קשר עם גורמי פיקוח"
      ]},

      { type: "h2", text: "5. האם הממונה אחראי באופן אישי?" },
      { type: "p", text: "הממונה נושא באחריות מקצועית לייעץ ולהתריע. האחריות החוקית העיקרית נותרת על המעסיק. עם זאת, ממונה שהתרשל בתפקידו עלול לעמוד לדין." },

      { type: "h2", text: "6. האם מנהל יכול לשמש גם כממונה?" },
      { type: "p", text: "טכנית כן, אם הוא עומד בדרישות ההסמכה. אולם, מומלץ שהממונה יהיה עצמאי ולא בניגוד עניינים עם שיקולים תפעוליים או כלכליים." },

      { type: "h2", text: "7. כמה עולה ממונה בטיחות?" },
      { type: "p", text: "העלות משתנה לפי היקף המשרה, הניסיון וגודל הארגון. צרו קשר לקבלת הצעת מחיר מותאמת לצרכים שלכם." },

      { type: "h2", text: "8. מה קורה אם אין לנו ממונה?" },
      { type: "p", text: "אי-מינוי ממונה כנדרש היא עבירה על החוק. זה עלול לגרור קנסות כספיים, צווי שיפור, ובמקרה של תאונה - החמרה בעונש ובאחריות." },

      { type: "h2", text: "9. איך בוחרים ממונה טוב?" },
      { type: "list", items: [
        "ניסיון בתחום הפעילות שלכם",
        "כישורי תקשורת טובים",
        "יכולת לעמוד מול הנהלה",
        "עדכניות בידע ורגולציה",
        "המלצות ממעסיקים קודמים"
      ]},

      { type: "h2", text: "10. האם עדיף ממונה פנימי או חיצוני?" },
      { type: "p", text: "לשתי האפשרויות יתרונות:" },
      { type: "h3", text: "ממונה פנימי:" },
      { type: "list", items: [
        "היכרות עמוקה עם הארגון",
        "זמינות גבוהה",
        "מחויבות לאורך זמן"
      ]},
      { type: "h3", text: "ממונה חיצוני:" },
      { type: "list", items: [
        "מבט רענן ואובייקטיבי",
        "ניסיון ממגוון ארגונים",
        "גמישות בהיקף השירות",
        "עלות נמוכה יותר לארגונים קטנים"
      ]},
      
      { type: "cta", text: "צריכים עזרה בבחירת או גיוס ממונה בטיחות? נשמח לייעץ." }
    ]
  },
  {
    id: 13,
    slug: "chemical-safety-manufacturing",
    title: "בטיחות כימית במפעלים - מדריך מעשי",
    metaTitle: "בטיחות כימית במפעלים | מדריך מקיף | Safe-Group",
    metaDescription: "המדריך המלא לניהול בטיחות כימית במפעלים: זיהוי חומרים מסוכנים, אחסון נכון, ציוד מגן, ונהלי חירום. חובה לכל מפעל שעובד עם כימיקלים.",
    excerpt: "עבודה עם חומרים מסוכנים דורשת ידע ונהלים מיוחדים. הנה המדריך המעשי לבטיחות כימית במפעל שלכם.",
    category: "ענפים",
    date: "2024-12-04",
    readTime: "10 דקות קריאה",
    image: "https://images.unsplash.com/photo-1532094349884-543bc11b234d?w=800&auto=format&fit=crop",
    imageAlt: "עובד עם חליפת מגן כימית במעבדה",
    keywords: ["בטיחות כימית", "חומרים מסוכנים", "MSDS", "אחסון כימיקלים", "הגנה כימית"],
    content: [
      { type: "h2", text: "מבוא: למה בטיחות כימית קריטית?" },
      { type: "p", text: "חומרים מסוכנים נמצאים כמעט בכל סביבת עבודה תעשייתית. מחומרי ניקוי פשוטים ועד כימיקלים מורכבים - כולם דורשים טיפול נכון. חשיפה לא מבוקרת יכולה לגרום לנזקי בריאות קשים, שריפות, פיצוצים ונזק סביבתי." },

      { type: "h2", text: "זיהוי וסיווג חומרים מסוכנים" },
      { type: "p", text: "הצעד הראשון הוא לדעת עם מה עובדים:" },
      { type: "list", items: [
        "ערכו רשימה של כל החומרים הכימיים במפעל",
        "השיגו גליון בטיחות (MSDS/SDS) לכל חומר",
        "הכירו את סמלי הסכנה הבינלאומיים (GHS)",
        "סווגו חומרים לפי סוג הסכנה: רעיל, דליק, מאכל, מחמצן וכו'"
      ]},

      { type: "h2", text: "גליון בטיחות (MSDS) - הכלי החשוב ביותר" },
      { type: "p", text: "גליון הבטיחות מספק מידע קריטי על כל חומר:" },
      { type: "list", items: [
        "מזהה החומר והרכבו",
        "סכנות אפשריות",
        "עזרה ראשונה במקרה של חשיפה",
        "אמצעי כיבוי אש",
        "דרישות אחסון",
        "ציוד מגן נדרש",
        "הוראות פינוי וסילוק"
      ]},
      { type: "p", text: "חובה שגליונות הבטיחות יהיו זמינים לכל העובדים, בעברית, ליד אזור האחסון והשימוש." },

      { type: "h2", text: "אחסון נכון של חומרים מסוכנים" },
      { type: "list", items: [
        "הפרדה בין חומרים לא מתאימים (חומצות מבסיסים, מחמצנים מדליקים)",
        "אוורור מספק באזור האחסון",
        "מערכות גילוי וכיבוי אש מתאימות",
        "שילוט ברור של סוג הסכנה",
        "אמצעי ספיגה לדליפות",
        "גישה מוגבלת לעובדים מורשים בלבד",
        "בדיקה תקופתית של מיכלים ואריזות"
      ]},

      { type: "h2", text: "ציוד מגן אישי (PPE) לעבודה כימית" },
      { type: "p", text: "הציוד הנדרש משתנה לפי סוג החומר והפעולה:" },
      { type: "list", items: [
        "כפפות כימיות מתאימות (סוג הכפפה לפי סוג החומר)",
        "משקפי מגן או מסכת פנים מלאה",
        "מסכת נשימה או מערכת אוויר נשימה",
        "חליפת מגן כימית (במקרה הצורך)",
        "נעלי בטיחות עמידות בכימיקלים"
      ]},
      { type: "quote", text: "חשוב: לא כל כפפה מתאימה לכל חומר. יש לבדוק התאמה בגליון הבטיחות." },

      { type: "h2", text: "הדרכות נדרשות" },
      { type: "list", items: [
        "הכרת החומרים המסוכנים במקום העבודה",
        "קריאה והבנה של גליונות בטיחות",
        "שימוש נכון בציוד מגן",
        "נהלי עבודה בטוחים",
        "זיהוי סימנים של חשיפה",
        "נהלי חירום: דליפה, שריפה, חשיפה"
      ]},

      { type: "h2", text: "נהלי חירום" },
      { type: "h3", text: "דליפה או שפך:" },
      { type: "list", items: [
        "פנו את האזור מיידית",
        "אוורוו את המקום",
        "השתמשו בערכת ספיגה מתאימה",
        "אל תשטפו למערכת הביוב ללא אישור",
        "דווחו למשרד להגנת הסביבה במקרה הצורך"
      ]},
      { type: "h3", text: "חשיפה:" },
      { type: "list", items: [
        "הוציאו את הנפגע מאזור החשיפה",
        "פעלו לפי הוראות העזרה הראשונה בגליון הבטיחות",
        "שטיפה במים (עור/עיניים) - לפחות 15 דקות",
        "פנו לסיוע רפואי עם גליון הבטיחות"
      ]},
      
      { type: "cta", text: "צריכים עזרה בניהול בטיחות כימית במפעל? פנו אלינו לייעוץ מקצועי." }
    ]
  },
  {
    id: 14,
    slug: "workplace-accident-cost-analysis",
    title: "עלות תאונת עבודה - המספרים שמנהלים לא מכירים",
    metaTitle: "עלות תאונת עבודה | ניתוח מקיף | Safe-Group",
    metaDescription: "ניתוח מעמיק של העלויות האמיתיות של תאונת עבודה: ישירות ועקיפות, גלויות ונסתרות. המספרים שכל מנהל חייב להכיר.",
    excerpt: "תאונת עבודה עולה הרבה יותר ממה שחושבים. הנה המספרים האמיתיים שיגרמו לכם לחשוב אחרת על השקעה בבטיחות.",
    category: "כלכלה",
    date: "2024-12-02",
    readTime: "8 דקות קריאה",
    image: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&auto=format&fit=crop",
    imageAlt: "גרפים וחישובי עלויות",
    keywords: ["עלות תאונה", "כלכלת בטיחות", "עלויות נסתרות", "ביטוח לאומי", "פיצויים"],
    content: [
      { type: "h2", text: "מבוא: התמונה המלאה" },
      { type: "p", text: "כשמנהלים שומעים על תאונת עבודה, הם בדרך כלל חושבים על ימי המחלה של העובד. אבל זו רק קצה הקרחון. העלות האמיתית של תאונה כוללת מרכיבים רבים - חלקם גלויים וחלקם נסתרים. הבנת התמונה המלאה משנה את החשיבה על השקעה בבטיחות." },

      { type: "h2", text: "עלויות ישירות (גלויות)" },
      { type: "list", items: [
        "הוצאות רפואיות (טיפול, אשפוז, שיקום)",
        "פיצויים לעובד (ביטוח לאומי, ביטוח מעסיקים)",
        "תשלום ימי מחלה",
        "נזק לציוד או רכוש",
        "קנסות ועיצומים מרשויות"
      ]},
      { type: "quote", text: "עלות רפואית ממוצעת של תאונת עבודה בינונית בישראל: 30,000-80,000 ש\"ח" },

      { type: "h2", text: "עלויות עקיפות (נסתרות)" },
      { type: "p", text: "אלה העלויות שלא רואים בשורה התחתונה, אבל הן משמעותיות:" },
      { type: "h3", text: "עלויות כוח אדם:" },
      { type: "list", items: [
        "זמן של עובדים שעוזרים בתאונה",
        "חקירת התאונה",
        "גיוס והכשרת עובד מחליף",
        "שעות נוספות של עובדים אחרים",
        "ירידה בפרודוקטיביות של הצוות"
      ]},
      { type: "h3", text: "עלויות תפעוליות:" },
      { type: "list", items: [
        "עצירת פס ייצור",
        "עיכובים באספקות",
        "בזבוז חומרי גלם",
        "תיקון או החלפת ציוד"
      ]},
      { type: "h3", text: "עלויות מנהלתיות:" },
      { type: "list", items: [
        "זמן הנהלה לטיפול באירוע",
        "טיפול מול ביטוח ורשויות",
        "הוצאות משפטיות",
        "דוחות ותיעוד"
      ]},

      { type: "h2", text: "עלויות ארוכות טווח" },
      { type: "list", items: [
        "עליית פרמיות ביטוח",
        "פגיעה במוניטין ובמותג המעסיק",
        "קושי בגיוס עובדים איכותיים",
        "ירידה במורל העובדים",
        "תביעות משפטיות נגררות"
      ]},

      { type: "h2", text: "\"כלל הפירמידה\" - יחס עלויות" },
      { type: "p", text: "מחקרים מראים שהעלויות הנסתרות גבוהות פי 4-10 מהעלויות הגלויות. כלומר:" },
      { type: "list", items: [
        "עלות גלויה של 50,000 ש\"ח = עלות אמיתית של 200,000-500,000 ש\"ח",
        "עלות גלויה של 100,000 ש\"ח = עלות אמיתית של 400,000-1,000,000 ש\"ח"
      ]},

      { type: "h2", text: "דוגמה מספרית" },
      { type: "p", text: "נפילה מסולם במחסן - פציעת גב בינונית:" },
      { type: "list", items: [
        "הוצאות רפואיות: 45,000 ש\"ח",
        "ימי מחלה (3 חודשים): 36,000 ש\"ח",
        "עובד מחליף זמני: 42,000 ש\"ח",
        "חקירה ותיעוד: 8,000 ש\"ח",
        "תיקון ציוד: 5,000 ש\"ח",
        "ירידה בפרודוקטיביות צוות: 15,000 ש\"ח",
        "עליית פרמיית ביטוח (שנתי): 12,000 ש\"ח"
      ]},
      { type: "p", text: "סה\"כ: 163,000 ש\"ח - ותאונה זו נחשבת \"קלה\"!" },

      { type: "h2", text: "ההשוואה שמשנה את התמונה" },
      { type: "p", text: "עלות מניעה מול עלות תאונה:" },
      { type: "list", items: [
        "סולם בטיחותי איכותי: 2,000 ש\"ח",
        "הדרכת עבודה בגובה: 1,500 ש\"ח",
        "בדיקות תקופתיות שנתיות: 3,000 ש\"ח",
        "סה\"כ מניעה: 6,500 ש\"ח"
      ]},
      { type: "p", text: "יחס עלות/תועלת: השקעה של 6,500 ש\"ח יכולה למנוע נזק של 163,000 ש\"ח. זה ROI של פי 25!" },

      { type: "h2", text: "איך להציג את המספרים להנהלה" },
      { type: "list", items: [
        "חשבו עלויות מלאות, לא רק ישירות",
        "השוו לעלויות מניעה",
        "הציגו מגמות רב-שנתיות",
        "הוסיפו ניתוח סיכונים: מה יקרה אם לא נשקיע?"
      ]},
      
      { type: "cta", text: "רוצים לבנות תיק עסקי להשקעה בבטיחות? נעזור לכם להציג את המספרים." }
    ]
  },
  {
    id: 15,
    slug: "emergency-evacuation-drill-guide",
    title: "תרגיל פינוי - איך לתכנן ולבצע נכון",
    metaTitle: "מדריך לתרגיל פינוי | תכנון וביצוע | Safe-Group",
    metaDescription: "המדריך המלא לתכנון וביצוע תרגיל פינוי אפקטיבי: הכנה, ביצוע, הפקת לקחים. כולל רשימת תיוג להורדה.",
    excerpt: "תרגיל פינוי טוב יכול להציל חיים. הנה המדריך המלא לתכנון וביצוע תרגיל פינוי שבאמת עובד.",
    category: "מדריכים",
    date: "2024-11-30",
    readTime: "7 דקות קריאה",
    image: "https://images.unsplash.com/photo-1582139329536-e7284fece509?w=800&auto=format&fit=crop",
    imageAlt: "יציאת חירום ושילוט פינוי",
    keywords: ["תרגיל פינוי", "פינוי חירום", "בטיחות אש", "תוכנית חירום", "יציאות חירום"],
    content: [
      { type: "h2", text: "למה תרגילי פינוי חשובים?" },
      { type: "p", text: "במצב חירום אמיתי, אנשים נוטים לפאניקה. תרגול יוצר זיכרון שרירי ומאפשר פעולה אוטומטית גם בלחץ. בנוסף, תרגילים חושפים בעיות בתוכנית הפינוי לפני שזה קריטי. החוק מחייב תרגיל פינוי אחד לשנה לפחות." },

      { type: "h2", text: "שלב 1: תכנון התרגיל" },
      { type: "h3", text: "הגדרת מטרות:" },
      { type: "list", items: [
        "בדיקת זמן פינוי מול היעד",
        "תרגול צוותי חירום",
        "בדיקת מערכות התרעה",
        "איתור חסמים ובעיות",
        "הטמעת נהלים לעובדים חדשים"
      ]},
      { type: "h3", text: "בחירת תאריך ושעה:" },
      { type: "p", text: "בחרו זמן שבו רוב העובדים נוכחים. שקלו לערוך תרגילים בזמנים שונים לאורך השנה (בוקר, צהריים, משמרות)." },
      { type: "h3", text: "תיאום גורמים:" },
      { type: "list", items: [
        "הנהלת הארגון",
        "שירותי ביטחון/אבטחה",
        "כיבוי אש מקומי (במקרה הצורך)",
        "שכנים (אם יש השפעה)"
      ]},

      { type: "h2", text: "שלב 2: הכנת התשתית" },
      { type: "h3", text: "בדיקות מקדימות:" },
      { type: "list", items: [
        "יציאות חירום פנויות וניתנות לפתיחה",
        "שילוט יציאות חירום מואר ותקין",
        "מערכת כריזה/התרעה פועלת",
        "נקודות התכנסות מוגדרות ומסומנות",
        "רשימות נוכחות עדכניות"
      ]},
      { type: "h3", text: "הכנת צוות:" },
      { type: "list", items: [
        "תדרוך ממוני קומה/אזור",
        "הכנת צוות ספירה בנקודות התכנסות",
        "תיאום עם מוקד שמירה",
        "הכנת משקיפים לתיעוד"
      ]},

      { type: "h2", text: "שלב 3: ביצוע התרגיל" },
      { type: "h3", text: "רצף הפעולות:" },
      { type: "list", items: [
        "הפעלת התרעה (אזעקה/כריזה)",
        "עצירת עבודה והשבתת מכונות",
        "יציאה מסודרת ליציאות החירום",
        "התכנסות בנקודות שנקבעו",
        "ספירה ודיווח על חסרים",
        "הכרזה על סיום התרגיל",
        "חזרה מסודרת לעבודה"
      ]},
      { type: "h3", text: "מה לתעד:" },
      { type: "list", items: [
        "שעת תחילת התרעה",
        "שעת סיום פינוי כל אזור",
        "בעיות שנצפו (דלתות נעולות, מעברים חסומים)",
        "התנהגות עובדים",
        "זמן השלמת ספירה"
      ]},

      { type: "h2", text: "שלב 4: הפקת לקחים" },
      { type: "p", text: "מיד לאחר התרגיל, ערכו תחקיר:" },
      { type: "list", items: [
        "האם הושגו היעדים?",
        "מה עבד טוב?",
        "אילו בעיות התגלו?",
        "מה צריך לתקן/לשפר?",
        "מי אחראי על כל תיקון ומתי?"
      ]},

      { type: "h2", text: "טיפים להצלחה" },
      { type: "list", items: [
        "אל תודיעו מראש על המועד המדויק",
        "שלבו \"הפתעות\" כמו יציאה חסומה",
        "תרגלו גם מצב של נפגע/נעדר",
        "צלמו את התרגיל לתיעוד",
        "הציגו ממצאים להנהלה",
        "עקבו אחר ביצוע התיקונים"
      ]},

      { type: "h2", text: "שגיאות נפוצות להימנע" },
      { type: "list", items: [
        "תרגיל \"לסימון בלבד\" בלי רצינות",
        "אי-תיעוד הממצאים",
        "אי-תיקון הבעיות שהתגלו",
        "תרגיל רק בשעות נוחות",
        "אי-שיתוף כל העובדים"
      ]},
      
      { type: "cta", text: "צריכים עזרה בתכנון תרגיל פינוי? נשמח לסייע." }
    ]
  },
  {
    id: 16,
    slug: "safety-technology-trends-2024",
    title: "טכנולוגיות חדשות בניהול בטיחות 2024",
    metaTitle: "טרנדים טכנולוגיים בבטיחות 2024 | Safe-Group",
    metaDescription: "סקירת הטכנולוגיות החמות בניהול בטיחות ב-2024: AI, IoT, VR, ועוד. מה הטרנדים וכיצד הם משנים את התחום.",
    excerpt: "עולם הבטיחות עובר מהפכה טכנולוגית. הנה הטרנדים החמים ביותר של 2024 שכדאי להכיר.",
    category: "טכנולוגיה",
    date: "2024-11-25",
    readTime: "7 דקות קריאה",
    image: "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=800&auto=format&fit=crop",
    imageAlt: "טכנולוגיה מתקדמת וחדשנות",
    featured: true,
    keywords: ["טכנולוגיה 2024", "AI בטיחות", "IoT", "חדשנות", "דיגיטציה בטיחות"],
    content: [
      { type: "h2", text: "המהפכה הטכנולוגית בבטיחות" },
      { type: "p", text: "שנת 2024 מסמנת קפיצת מדרגה בשילוב טכנולוגיה בניהול בטיחות. מה שהיה נשמע כמו מדע בדיוני לפני חמש שנים הופך למציאות יומיומית במפעלים ואתרי עבודה. הנה הטרנדים המובילים שמשנים את התחום." },

      { type: "h2", text: "1. בינה מלאכותית (AI) לחיזוי סיכונים" },
      { type: "p", text: "מערכות AI מנתחות נתונים ממגוון מקורות כדי לחזות סיכונים לפני שהם מתממשים:" },
      { type: "list", items: [
        "ניתוח דיווחי בטיחות וזיהוי מגמות",
        "זיהוי אוטומטי של התנהגות מסוכנת מצילומי מצלמות",
        "חיזוי תאונות על בסיס נתונים היסטוריים",
        "אופטימיזציה של סידורי עבודה לצמצום סיכון",
        "ניתוח שפה טבעית של דיווחים לזיהוי אזהרות מוקדמות"
      ]},
      { type: "quote", text: "מחקר של McKinsey מצא שמערכות AI יכולות להפחית תאונות עבודה ב-20-30%." },

      { type: "h2", text: "2. ציוד מגן אישי חכם (Smart PPE)" },
      { type: "p", text: "הדור הבא של ציוד מגן כולל חיישנים וקישוריות:" },
      { type: "list", items: [
        "קסדות עם חיישני חום וזיהוי התחשמלות",
        "וסטים עם ניטור סימנים חיוניים",
        "נעליים שמתריעות על עומס יתר על הגב",
        "משקפי AR המציגים מידע בטיחותי בזמן אמת",
        "רתמות עם חיישני נפילה ומיקום GPS"
      ]},

      { type: "h2", text: "3. האינטרנט של הדברים (IoT)" },
      { type: "p", text: "חיישנים מחוברים מספקים תמונה בזמן אמת של סביבת העבודה:" },
      { type: "list", items: [
        "ניטור איכות אוויר ורמות גז",
        "מעקב אחר רעידות מכונות לזיהוי תקלות",
        "בקרת כניסה לאזורים מסוכנים",
        "ניטור עומסים על מדפים",
        "התראות אוטומטיות על חריגות"
      ]},

      { type: "h2", text: "4. מציאות מדומה (VR) להדרכות" },
      { type: "p", text: "VR מחולל מהפכה בהדרכות בטיחות:" },
      { type: "list", items: [
        "סימולציה של מצבי חירום בסביבה בטוחה",
        "תרגול על ציוד מסוכן ללא סיכון",
        "חוויה רגשית שמשאירה רושם",
        "אפשרות לחזור על תרחישים שוב ושוב",
        "מדידת ביצועים והתקדמות"
      ]},

      { type: "h2", text: "5. דרונים לבדיקות בטיחות" },
      { type: "p", text: "דרונים מאפשרים בדיקות במקומות מסוכנים:" },
      { type: "list", items: [
        "בדיקת גגות ומבנים גבוהים",
        "סריקת אתרי בנייה מהאוויר",
        "ניטור תשתיות (מגדלי חשמל, ארובות)",
        "צילום תרמי לזיהוי בעיות",
        "חיפוש והצלה באזורים מסוכנים"
      ]},

      { type: "h2", text: "6. פלטפורמות ניהול בטיחות ענניות" },
      { type: "p", text: "מערכות SaaS לניהול בטיחות מספקות:" },
      { type: "list", items: [
        "דיווח סיכונים מהנייד",
        "ניהול הדרכות ותעודות",
        "ניתוח נתונים ודשבורדים",
        "ניהול משימות ותיקונים",
        "ציות רגולטורי אוטומטי"
      ]},

      { type: "h2", text: "אתגרים ביישום" },
      { type: "list", items: [
        "עלויות הטמעה ראשוניות",
        "התנגדות לשינוי מעובדים",
        "חששות פרטיות (מעקב)",
        "תחזוקה ועדכונים שוטפים",
        "אינטגרציה עם מערכות קיימות"
      ]},

      { type: "h2", text: "איך להתחיל?" },
      { type: "list", items: [
        "התחילו עם פתרון אחד ממוקד",
        "בחרו טכנולוגיה שפותרת בעיה קיימת",
        "הריצו פיילוט קטן לפני הרחבה",
        "שתפו עובדים בתהליך",
        "מדדו תוצאות והציגו ROI"
      ]},
      
      { type: "cta", text: "מעוניינים לשמוע על טכנולוגיות מתאימות לארגון שלכם? צרו קשר לייעוץ." }
    ]
  },
  // ===== NEW POSTS - BATCH 1 (17-21): שירותי ממונה בטיחות =====
  {
    id: 17,
    slug: "safety-officer-industry-factories",
    title: "ממונה בטיחות במפעלים ותעשייה - מה החוק דורש",
    metaTitle: "ממונה בטיחות במפעלים ותעשייה | דרישות החוק | אדם בטיחות",
    metaDescription: "מדריך מקיף לדרישות החוק למינוי ממונה בטיחות במפעלים ובתעשייה. כל מה שצריך לדעת על הכשרה, תפקידים ואחריות.",
    excerpt: "מהן הדרישות החוקיות למינוי ממונה בטיחות במפעלים ותעשייה? מדריך מקיף לבעלי מפעלים ומנהלי תעשייה.",
    category: "רגולציה",
    date: "2025-01-15",
    readTime: "8 דקות קריאה",
    image: "https://images.unsplash.com/photo-1565793298595-6a879b1d9492?w=800&auto=format&fit=crop",
    imageAlt: "מפעל תעשייתי עם ציוד בטיחות",
    featured: true,
    keywords: ["ממונה בטיחות", "מפעלים", "תעשייה", "חוק בטיחות", "תקנות ארגון הפיקוח", "הכשרת ממונה בטיחות"],
    content: [
      { type: "h2", text: "מבוא: חובת מינוי ממונה בטיחות במפעלים" },
      { type: "p", text: "על פי תקנות ארגון הפיקוח על העבודה, כל מפעל תעשייתי בישראל מחויב למנות ממונה בטיחות בהתאם לגודל המפעל וסוג הפעילות. זוהי לא רק דרישה בירוקרטית - זו הגנה על חיי העובדים ועל העסק שלכם." },
      { type: "p", text: "בחברת אדם בטיחות, אנו מתמחים במפעלי ייצור, מכונות ומערכות תעשייתיות מורכבות. הניסיון שלנו בתחנות כוח, מנהור ומפעלים כבדים מעניק לנו פרספקטיבה ייחודית על אתגרי הבטיחות בתעשייה הישראלית." },

      { type: "h2", text: "מתי חייבים למנות ממונה בטיחות?" },
      { type: "p", text: "החוק מגדיר מספר קריטריונים ברורים:" },
      { type: "list", items: [
        "מפעל עם 50 עובדים ומעלה - חובה למנות ממונה בטיחות (תקנה 4)",
        "מפעלים בענפים מסוכנים (מהתוספת השנייה) - מפקח עבודה רשאי לחייב מינוי גם מתחת ל-50 עובדים",
        "מבצע בנייה עם 100 עובדים ומעלה - חובה למנות ממונה בטיחות",
        "היקף משרה מינימלי: 32 שעות חודשיות, בהתאם לגודל המפעל ורמת הסיכון"
      ]},
      { type: "quote", text: "תקנות ארגון הפיקוח על העבודה (ממונים על הבטיחות), התשנ\"ו-1996 מגדירות במפורש את חובות המעסיק בנושא." },

      { type: "h2", text: "תפקידי ממונה הבטיחות במפעל" },
      { type: "p", text: "ממונה הבטיחות אינו רק 'איש נייר' - הוא שומר הסף של בטיחות העובדים:" },
      { type: "list", items: [
        "ביצוע סקרי סיכונים תקופתיים בכל אזורי המפעל",
        "פיקוח על עבודה עם מכונות ותהליכי ייצור",
        "הדרכת עובדים וקיום תרגילי חירום",
        "חקירת תאונות והפקת לקחים",
        "ניהול מערך ציוד המגן האישי",
        "התממשקות עם גורמי פיקוח ורגולציה"
      ]},

      { type: "h2", text: "דרישות הכשרה לממונה בטיחות בתעשייה" },
      { type: "p", text: "לא כל אחד יכול לשמש כממונה בטיחות. הדרישות כוללות:" },
      { type: "list", items: [
        "קורס ממוני בטיחות מוכר - 180 שעות לימוד לפחות",
        "ניסיון מעשי בתחום הבטיחות או בתעשייה",
        "הכרת התקנות והחוקים הרלוונטיים",
        "יכולת עבודה עם מערכות ניהול בטיחות",
        "כישורי תקשורת והדרכה"
      ]},

      { type: "h3", text: "התמחויות נדרשות במפעלים מורכבים" },
      { type: "p", text: "במפעלים עם סיכונים מיוחדים, נדרשת התמחות נוספת:" },
      { type: "list", items: [
        "עבודה עם מכונות כבדות ותהליכי ייצור",
        "בטיחות חשמל ומערכות לחץ",
        "עבודה בחללים מוקפים",
        "טיפול בחומרים מסוכנים"
      ]},

      { type: "h2", text: "ממונה בטיחות פנימי או חיצוני?" },
      { type: "p", text: "החוק מאפשר למפעלים להעסיק ממונה בטיחות פנימי או לקבל שירותים מממונה בטיחות חיצוני. לכל אפשרות יתרונות:" },
      { type: "h3", text: "יתרונות ממונה בטיחות חיצוני:" },
      { type: "list", items: [
        "עין רעננה וחסרת פניות",
        "ניסיון ממגוון מפעלים ותעשיות",
        "חיסכון בעלויות העסקה קבועות",
        "גמישות בהיקף השירות",
        "עדכניות בשינויי רגולציה"
      ]},

      { type: "h2", text: "סיכונים ייחודיים במפעלי תעשייה" },
      { type: "p", text: "ממונה הבטיחות במפעל צריך להתמודד עם מגוון רחב של סיכונים:" },
      { type: "list", items: [
        "תאונות מכונות ותהליכי ייצור - הסיבה הראשונה לפציעות במפעלים",
        "נפילות וגלישות - בעיקר באזורי אחסנה ולוגיסטיקה",
        "חשיפה לחומרים מסוכנים - כימיקלים, אבק, רעש",
        "סיכוני אש ופיצוץ - במיוחד במפעלי כימיה ומזון",
        "עבודה בגובה - תחזוקת מערכות ותשתיות"
      ]},

      { type: "h2", text: "איך לבחור ממונה בטיחות למפעל שלכם?" },
      { type: "p", text: "בחירת ממונה בטיחות היא החלטה קריטית. שיקולים מרכזיים:" },
      { type: "list", items: [
        "ניסיון בענף הספציפי שלכם",
        "הכרת המכונות והתהליכים במפעל",
        "יכולת תקשורת עם עובדים ומנהלים",
        "זמינות ונגישות",
        "המלצות מלקוחות קודמים"
      ]},

      { type: "h2", text: "סיכום: בטיחות כהשקעה" },
      { type: "p", text: "מינוי ממונה בטיחות מקצועי הוא לא הוצאה - זו השקעה. מעבר לעמידה בדרישות החוק, ממונה בטיחות אפקטיבי יחסוך לכם עלויות תאונות, ימי מחלה, קנסות וביטוחים. והכי חשוב - ישמור על חיי העובדים שלכם." },

      { type: "cta", text: "צריכים ממונה בטיחות מנוסה למפעל שלכם? צרו קשר עם אדם בטיחות לייעוץ ראשוני ללא עלות." }
    ]
  },
  {
    id: 18,
    slug: "safety-consultant-vs-safety-officer",
    title: "יועץ בטיחות או ממונה בטיחות - ההבדלים שחשוב להבין",
    metaTitle: "יועץ בטיחות מול ממונה בטיחות | מה ההבדל? | אדם בטיחות",
    metaDescription: "מה ההבדל בין יועץ בטיחות לממונה בטיחות? מתי צריך כל אחד מהם? מדריך מקיף להבנת התפקידים והאחריות.",
    excerpt: "רבים מתבלבלים בין יועץ בטיחות לממונה בטיחות. הנה ההבדלים המרכזיים ומתי תצטרכו כל אחד מהם.",
    category: "מדריכים",
    date: "2025-01-14",
    readTime: "6 דקות קריאה",
    image: "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=800&auto=format&fit=crop",
    imageAlt: "פגישת ייעוץ עסקית",
    keywords: ["יועץ בטיחות", "ממונה בטיחות", "הבדלים", "תפקידים", "אחריות", "שירותי בטיחות"],
    content: [
      { type: "h2", text: "הבלבול הנפוץ: יועץ או ממונה?" },
      { type: "p", text: "בעולם הבטיחות התעסוקתית בישראל, שני מונחים נשמעים לעתים קרובות: 'יועץ בטיחות' ו'ממונה בטיחות'. רבים משתמשים בהם לסירוגין, אך למעשה מדובר בתפקידים שונים עם אחריות משפטית ומקצועית שונה." },
      { type: "p", text: "הבנת ההבדלים תעזור לכם לקבל את השירות הנכון ולעמוד בדרישות החוק." },

      { type: "h2", text: "ממונה בטיחות - הגדרה ואחריות" },
      { type: "p", text: "ממונה בטיחות הוא תפקיד המוגדר בחוק ובתקנות:" },
      { type: "list", items: [
        "מוגדר בתקנות ארגון הפיקוח על העבודה",
        "אחראי על ניהול שוטף של הבטיחות בארגון",
        "מחויב להיות מעודכן בנעשה במקום העבודה",
        "נושא אחריות משפטית ישירה",
        "מדווח למנהל הכללי ולמפקחי העבודה"
      ]},
      { type: "h3", text: "תפקידים עיקריים של ממונה בטיחות:" },
      { type: "list", items: [
        "ביצוע סקרי סיכונים תקופתיים",
        "הדרכת עובדים בנושאי בטיחות",
        "חקירת תאונות עבודה וכמעט-תאונות",
        "פיקוח על שימוש בציוד מגן אישי",
        "ניהול תוכנית בטיחות שנתית"
      ]},

      { type: "h2", text: "יועץ בטיחות - הגדרה ואחריות" },
      { type: "p", text: "יועץ בטיחות הוא מומחה חיצוני שמספק שירותי ייעוץ:" },
      { type: "list", items: [
        "מומחה עצמאי שאינו שכיר בארגון",
        "מספק ייעוץ מקצועי בנושאי בטיחות ספציפיים",
        "אינו נושא באחריות שוטפת על ניהול הבטיחות",
        "יכול לשמש כמומחה לנושאים מיוחדים",
        "לעתים משמש כממונה בטיחות חיצוני"
      ]},
      { type: "h3", text: "תחומי ייעוץ נפוצים:" },
      { type: "list", items: [
        "הכנה לביקורת מפקח עבודה",
        "הטמעת מערכת ISO 45001",
        "סקר סיכונים מקיף חד-פעמי",
        "ייעוץ לפרויקטים מיוחדים",
        "חוות דעת מומחה לבית משפט"
      ]},

      { type: "h2", text: "מתי צריך ממונה בטיחות?" },
      { type: "p", text: "על פי החוק, חובה למנות ממונה בטיחות במקרים הבאים:" },
      { type: "list", items: [
        "עסקים עם 50 עובדים ומעלה",
        "אתרי בנייה ופרויקטי תשתיות",
        "מפעלים עם סיכונים מיוחדים (חומרים מסוכנים, עבודה בגובה)",
        "מוסדות חינוך ומבני ציבור",
        "כל מקום עבודה שמפקח העבודה דורש"
      ]},

      { type: "h2", text: "מתי צריך יועץ בטיחות?" },
      { type: "p", text: "יועץ בטיחות מתאים במיוחד ל:" },
      { type: "list", items: [
        "ארגונים שרוצים לשפר את רמת הבטיחות מעבר לחובה",
        "הכנה להסמכות ותקנים בינלאומיים",
        "פרויקטים חד-פעמיים או מיוחדים",
        "חוות דעת מקצועית עצמאית",
        "ליווי בתהליכי שיפור ארגוניים"
      ]},

      { type: "h2", text: "האם יועץ יכול לשמש כממונה?" },
      { type: "p", text: "כן! זוהי אפשרות מוכרת בחוק הנקראת 'ממונה בטיחות חיצוני'. יועץ בטיחות מוסמך יכול לשמש כממונה בטיחות של הארגון, תוך מילוי כל החובות החוקיות." },
      { type: "h3", text: "יתרונות ממונה בטיחות חיצוני:" },
      { type: "list", items: [
        "ניסיון ממגוון ארגונים וענפים",
        "אובייקטיביות וחוסר תלות",
        "חיסכון בעלויות העסקה קבועות",
        "גמישות בהיקף השירות",
        "עדכניות בשינויי רגולציה"
      ]},

      { type: "h2", text: "כמה זה עולה?" },
      { type: "p", text: "העלויות משתנות בהתאם להיקף השירות:" },
      { type: "list", items: [
        "ממונה בטיחות פנימי: משכורת מלאה + הכשרות + זמן ניהול",
        "ממונה בטיחות חיצוני: תשלום חודשי/שנתי קבוע",
        "יועץ בטיחות: תשלום לפי שעה או פרויקט"
      ]},
      { type: "quote", text: "לרוב, ממונה בטיחות חיצוני יהיה הפתרון הכלכלי ביותר לעסקים קטנים ובינוניים." },

      { type: "h2", text: "סיכום: מה מתאים לכם?" },
      { type: "p", text: "הבחירה תלויה בגודל הארגון, בסיכונים הספציפיים ובתקציב:" },
      { type: "list", items: [
        "עסק קטן-בינוני: ממונה בטיחות חיצוני",
        "מפעל גדול: ממונה בטיחות פנימי + ייעוץ חיצוני לנושאים מיוחדים",
        "פרויקט חד-פעמי: יועץ בטיחות",
        "הכנה להסמכה: יועץ בטיחות מתמחה"
      ]},

      { type: "cta", text: "לא בטוחים מה מתאים לכם? צרו קשר לשיחת ייעוץ ללא התחייבות." }
    ]
  },
  {
    id: 19,
    slug: "external-safety-officer-service",
    title: "ממונה בטיחות חיצוני - הפתרון הגמיש לארגונים",
    metaTitle: "ממונה בטיחות חיצוני | שירות גמיש לעסקים | אדם בטיחות",
    metaDescription: "למה ארגונים רבים בוחרים בממונה בטיחות חיצוני? יתרונות, עלויות והשוואה לממונה פנימי. מדריך מקיף לבעלי עסקים.",
    excerpt: "ממונה בטיחות חיצוני מציע גמישות, מקצועיות וחיסכון לעסקים קטנים ובינוניים. הנה כל מה שצריך לדעת.",
    category: "מדריכים",
    date: "2025-01-13",
    readTime: "7 דקות קריאה",
    image: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=800&auto=format&fit=crop",
    imageAlt: "איש מקצוע בסביבה תעשייתית",
    keywords: ["ממונה בטיחות חיצוני", "שירותי בטיחות", "outsourcing", "עסקים קטנים", "חיסכון בעלויות"],
    content: [
      { type: "h2", text: "מהו ממונה בטיחות חיצוני?" },
      { type: "p", text: "ממונה בטיחות חיצוני הוא איש מקצוע מוסמך שמספק שירותי ממונה בטיחות לארגון בלי להיות עובד שכיר שלו. הוא ממלא את כל החובות החוקיות של ממונה בטיחות, אך עובד כנותן שירות עצמאי או דרך חברת ייעוץ." },
      { type: "p", text: "זהו פתרון חוקי ומוכר על ידי משרד העבודה, שהפך פופולרי במיוחד בקרב עסקים קטנים ובינוניים בישראל." },

      { type: "h2", text: "למי מתאים ממונה בטיחות חיצוני?" },
      { type: "list", items: [
        "עסקים עם 20-100 עובדים שלא מצדיקים משרה מלאה",
        "חברות עם מספר סניפים קטנים",
        "ארגונים שרוצים מומחיות גבוהה בעלות סבירה",
        "עסקים שמעדיפים גמישות על פני התחייבות קבועה",
        "חברות בצמיחה שהצרכים שלהן משתנים"
      ]},

      { type: "h2", text: "יתרונות ממונה בטיחות חיצוני" },
      { type: "h3", text: "1. מקצועיות גבוהה" },
      { type: "p", text: "ממונה בטיחות חיצוני עובד עם מגוון ארגונים וצובר ניסיון רחב. הוא מכיר פתרונות שעבדו במקומות אחרים ויכול ליישם אותם אצלכם." },

      { type: "h3", text: "2. אובייקטיביות" },
      { type: "p", text: "בניגוד לעובד פנימי, ממונה חיצוני לא נתון ללחצים ארגוניים פנימיים. הוא יכול להצביע על בעיות בלי חשש על מעמדו." },

      { type: "h3", text: "3. חיסכון כלכלי" },
      { type: "list", items: [
        "אין עלויות העסקה קבועות (ביטוח לאומי, פנסיה, חופשות)",
        "משלמים רק על השירות שמקבלים",
        "אין עלויות הכשרה והשתלמויות",
        "אין צורך בתשתיות ומשאבים פנימיים"
      ]},

      { type: "h3", text: "4. גמישות" },
      { type: "p", text: "אפשר להתאים את היקף השירות לצרכים המשתנים - להגדיל בתקופות עמוסות ולצמצם בתקופות שקטות." },

      { type: "h3", text: "5. עדכניות מקצועית" },
      { type: "p", text: "ממונה חיצוני מחויב להישאר מעודכן בתחום כדי לשרת את כל לקוחותיו. אתם נהנים מידע מעודכן בלי להשקיע בהכשרות." },

      { type: "h2", text: "מה כולל השירות?" },
      { type: "p", text: "שירות ממונה בטיחות חיצוני מקיף כולל:" },
      { type: "list", items: [
        "ביקורי שטח תקופתיים (תדירות לפי הסיכום)",
        "ביצוע סקרי סיכונים שנתיים",
        "הדרכות בטיחות לעובדים",
        "ניהול תוכנית בטיחות שנתית",
        "דיווח לגורמי פיקוח ורגולציה",
        "ייצוג מול מפקחי עבודה",
        "חקירת תאונות והפקת לקחים",
        "ייעוץ שוטף בנושאי בטיחות"
      ]},

      { type: "h2", text: "השוואת עלויות: פנימי מול חיצוני" },
      { type: "p", text: "ממונה חיצוני לרוב עולה פחות מממונה פנימי, במיוחד לעסקים קטנים ובינוניים. העלויות משתנות לפי היקף השירות וגודל הארגון - צרו קשר לקבלת הצעת מחיר מותאמת." },
      { type: "quote", text: "ממונה חיצוני יכול להוות פתרון חסכוני ומקצועי, תוך קבלת שירות איכותי." },

      { type: "h2", text: "איך לבחור ממונה בטיחות חיצוני?" },
      { type: "p", text: "קריטריונים מרכזיים לבחירה:" },
      { type: "list", items: [
        "הסמכות והכשרה מוכרות",
        "ניסיון בענף הספציפי שלכם",
        "זמינות ונגישות",
        "המלצות מלקוחות קיימים",
        "ביטוח אחריות מקצועית",
        "הסכם שירות ברור"
      ]},

      { type: "h2", text: "חששות נפוצים ותשובות" },
      { type: "h3", text: "\"האם זה חוקי?\"" },
      { type: "p", text: "בהחלט. החוק מאפשר מינוי ממונה בטיחות חיצוני, כל עוד הוא מוסמך וממלא את כל חובותיו." },

      { type: "h3", text: "\"האם הוא יכיר את הארגון שלי?\"" },
      { type: "p", text: "ממונה חיצוני טוב ישקיע זמן בהכרת הארגון, התהליכים והאנשים. הביקורים התקופתיים מאפשרים היכרות מעמיקה." },

      { type: "h3", text: "\"מה אם יש אירוע דחוף?\"" },
      { type: "p", text: "הסכם שירות טוב כולל זמינות לאירועים דחופים, לרבות הגעה לאתר בזמן קצר." },

      { type: "h2", text: "סיכום" },
      { type: "p", text: "ממונה בטיחות חיצוני הוא פתרון מצוין לארגונים שרוצים לעמוד בחוק, לשמור על בטיחות העובדים ולחסוך בעלויות. באדם בטיחות, אנו מציעים שירות ממונה בטיחות חיצוני עם התמחות במפעלים, תשתיות ופרויקטים מורכבים." },

      { type: "cta", text: "מעוניינים בהצעת מחיר לשירות ממונה בטיחות חיצוני? צרו קשר עוד היום." }
    ]
  },
  {
    id: 20,
    slug: "safety-officer-construction-sites",
    title: "מינוי ממונה בטיחות באתרי בנייה - מתי חייבים?",
    metaTitle: "ממונה בטיחות באתר בנייה | חובות ודרישות | אדם בטיחות",
    metaDescription: "כל מה שקבלנים צריכים לדעת על מינוי ממונה בטיחות באתרי בנייה. דרישות החוק, תפקידים ואחריות הקבלן.",
    excerpt: "ענף הבנייה הוא מהמסוכנים בישראל. מתי חייבים למנות ממונה בטיחות באתר? מה תפקידיו? הנה המדריך המלא.",
    category: "בנייה",
    date: "2025-01-12",
    readTime: "9 דקות קריאה",
    image: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800&auto=format&fit=crop",
    imageAlt: "אתר בנייה עם עובדים וציוד בטיחות",
    featured: true,
    keywords: ["ממונה בטיחות בנייה", "אתר בנייה", "קבלן ראשי", "תאונות בנייה", "תקנות הבנייה"],
    content: [
      { type: "h2", text: "ענף הבנייה: האתגר הבטיחותי הגדול ביותר" },
      { type: "p", text: "ענף הבנייה בישראל מוביל בסטטיסטיקה העצובה של תאונות עבודה קטלניות. כמעט מחצית ממקרי המוות בעבודה בישראל קורים באתרי בנייה. זו הסיבה שהרגולציה בענף זה מחמירה במיוחד." },
      { type: "quote", text: "על פי נתוני משרד העבודה, בשנת 2023 נהרגו 28 עובדים באתרי בנייה בישראל - כמעט 50% מכלל ההרוגים בתאונות עבודה." },

      { type: "h2", text: "מתי חובה למנות ממונה בטיחות באתר בנייה?" },
      { type: "p", text: "על פי תקנה 4 לתקנות ממונים על הבטיחות התשנ\"ו-1996, חובה למנות ממונה בטיחות באתר בנייה במקרים הבאים:" },
      { type: "list", items: [
        "מבצע בנייה (קבלן ראשי) עם 100 עובדים ומעלה - חובה למנות ממונה בטיחות",
        "אתר בנייה מעל 7 מטר גובה ומעל 1,000 מ\"ר שטח - חובה למנות עוזר בטיחות (סעיף 25ב לחוק)",
        "כל אתר בנייה חייב במנהל עבודה מוסמך",
        "כל אתר שמפקח העבודה דורש בו מינוי"
      ]},

      { type: "h2", text: "מי אחראי על מינוי ממונה הבטיחות?" },
      { type: "p", text: "האחריות מוטלת על מבצע הבנייה (הקבלן הראשי):" },
      { type: "list", items: [
        "הקבלן הראשי חייב למנות ממונה בטיחות מוסמך",
        "הממונה חייב להיות נוכח באתר בכל זמן העבודה",
        "קבלני משנה כפופים להנחיות ממונה הבטיחות של הקבלן הראשי",
        "המזמין (היזם) אחראי לוודא שהקבלן עומד בחובותיו"
      ]},

      { type: "h2", text: "תפקידי ממונה הבטיחות באתר בנייה" },
      { type: "h3", text: "תפקידים יומיומיים:" },
      { type: "list", items: [
        "נוכחות קבועה באתר בזמן ביצוע עבודות",
        "פיקוח על שימוש בציוד מגן אישי",
        "בדיקת תקינות ציוד ומכונות",
        "אישור עבודות מסוכנות (עבודה בגובה, חפירות עמוקות)",
        "הדרכת עובדים חדשים באתר"
      ]},

      { type: "h3", text: "תפקידים תקופתיים:" },
      { type: "list", items: [
        "עדכון תוכנית בטיחות האתר",
        "ביצוע סקר סיכונים לכל שלב בבנייה",
        "קיום תרגילי חירום",
        "בדיקת פיגומים ומערכות הרמה",
        "דיווח לגורמי פיקוח"
      ]},

      { type: "h2", text: "הכשרות נדרשות לממונה בטיחות בבנייה" },
      { type: "p", text: "ממונה בטיחות באתר בנייה צריך הכשרה ייחודית:" },
      { type: "list", items: [
        "קורס ממוני בטיחות בסיסי (180 שעות)",
        "הכשרה ייעודית לענף הבנייה",
        "קורס עבודה בגובה",
        "הכשרה לבדיקת מנופים ואמצעי הרמה",
        "עדכון שנתי של 16 שעות לפחות"
      ]},

      { type: "h2", text: "סיכונים עיקריים באתרי בנייה" },
      { type: "p", text: "הממונה צריך להתמודד עם מגוון רחב של סיכונים:" },
      { type: "list", items: [
        "נפילה מגובה - הסיבה מספר 1 לתאונות קטלניות בבנייה",
        "התמוטטות מבנים וחפירות",
        "פגיעה מחפצים נופלים",
        "תאונות מנופים ועגורנים",
        "התחשמלות וסיכוני חשמל",
        "חשיפה לאבק ורעש",
        "פגיעה מכלי רכב כבדים"
      ]},

      { type: "h2", text: "תוכנית בטיחות לאתר בנייה" },
      { type: "p", text: "כל אתר בנייה חייב תוכנית בטיחות מפורטת הכוללת:" },
      { type: "list", items: [
        "זיהוי כל הסיכונים בכל שלב של הפרויקט",
        "נהלי עבודה בטוחים לכל סוג עבודה",
        "דרישות ציוד מגן אישי",
        "תוכנית חירום ודרכי פינוי",
        "לוח זמנים להדרכות ותרגילים",
        "אחריות ותפקידים של כל הגורמים באתר"
      ]},

      { type: "h2", text: "אחריות משפטית ועונשים" },
      { type: "p", text: "הפרת תקנות הבטיחות בבנייה גוררת סנקציות חמורות:" },
      { type: "list", items: [
        "קנסות מנהליים של עשרות אלפי שקלים",
        "צו הפסקת עבודות",
        "אחריות פלילית אישית של הקבלן והממונה",
        "תביעות אזרחיות במקרה של תאונה",
        "פסילה מהשתתפות במכרזים ציבוריים"
      ]},

      { type: "h2", text: "ממונה בטיחות פנימי או חיצוני באתר בנייה?" },
      { type: "p", text: "באתרי בנייה גדולים, לרוב נדרש ממונה בטיחות במשרה מלאה. אולם יש מקרים שבהם ממונה חיצוני מתאים:" },
      { type: "list", items: [
        "פרויקטים קטנים יחסית",
        "שלבי גמר שבהם מספר העובדים קטן",
        "קבלנים עם מספר אתרים קטנים",
        "משימות ספציפיות (סקר סיכונים, הכנה לביקורת)"
      ]},

      { type: "h2", text: "סיכום: בטיחות בבנייה היא לא אופציה" },
      { type: "p", text: "בענף הבנייה, בטיחות היא עניין של חיים ומוות. ממונה בטיחות מקצועי ומנוסה יכול לעשות את ההבדל בין אתר בטוח לאסון. באדם בטיחות, יש לנו ניסיון עשיר בליווי אתרי בנייה ופרויקטי תשתיות מורכבים." },

      { type: "cta", text: "צריכים ממונה בטיחות מנוסה לאתר הבנייה שלכם? צרו קשר לתיאום פגישה." }
    ]
  },
  {
    id: 21,
    slug: "safety-officer-services-guide",
    title: "שירותי ממונה על הבטיחות - מדריך מקיף לבעלי עסקים",
    metaTitle: "שירותי ממונה בטיחות | מדריך לבעלי עסקים | אדם בטיחות",
    metaDescription: "מדריך מקיף לבעלי עסקים על שירותי ממונה בטיחות: מה כולל השירות, כמה עולה, איך לבחור ומה לצפות.",
    excerpt: "כל מה שבעלי עסקים צריכים לדעת על שירותי ממונה בטיחות: סוגי שירות, עלויות, ואיך לבחור נכון.",
    category: "מדריכים",
    date: "2025-01-11",
    readTime: "10 דקות קריאה",
    image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&auto=format&fit=crop",
    imageAlt: "בעל עסק בפגישת ייעוץ",
    keywords: ["שירותי ממונה בטיחות", "בעלי עסקים", "עלויות", "בחירת ספק", "מדריך"],
    content: [
      { type: "h2", text: "מבוא: למה בעלי עסקים צריכים ממונה בטיחות?" },
      { type: "p", text: "כבעל עסק, יש לכם אלף ואחד דברים לדאוג להם. בטיחות עובדים אולי לא נמצאת בראש סדר העדיפויות - עד שמשהו קורה. תאונת עבודה יכולה לעלות לעסק מאות אלפי שקלים, פגיעה במוניטין, ובמקרים חמורים - אחריות פלילית אישית." },
      { type: "p", text: "ממונה בטיחות הוא לא רק דרישה חוקית - הוא ההגנה שלכם." },

      { type: "h2", text: "האם אתם חייבים בממונה בטיחות?" },
      { type: "p", text: "בדקו אם העסק שלכם נכנס לאחת הקטגוריות הבאות:" },
      { type: "list", items: [
        "יש לכם 50 עובדים ומעלה - חובה",
        "אתם מבצע בנייה עם 100 עובדים ומעלה - חובה",
        "אתם עובדים עם חומרים מסוכנים (לפי התוספת השנייה לתקנות) - חובה ללא קשר למספר עובדים",
        "העבודה במפעל או באתר בנייה המופיעים בתוספת השנייה לתקנות - חובה",
        "מפקח עבודה דרש - חובה"
      ]},
      { type: "p", text: "גם אם אינכם חייבים על פי חוק, שירות ממונה בטיחות יכול לחסוך לכם הרבה כאב ראש וכסף." },

      { type: "h2", text: "סוגי שירותי ממונה בטיחות" },
      { type: "h3", text: "1. ממונה בטיחות פנימי" },
      { type: "p", text: "עובד שכיר במשרה מלאה או חלקית שתפקידו העיקרי הוא בטיחות." },
      { type: "list", items: [
        "מתאים לארגונים גדולים (100+ עובדים)",
        "נוכחות יומית קבועה",
        "היכרות מעמיקה עם הארגון",
        "עלות גבוהה (משכורת + זכויות סוציאליות)"
      ]},

      { type: "h3", text: "2. ממונה בטיחות חיצוני" },
      { type: "p", text: "איש מקצוע עצמאי או חברה שמספקים שירות ממונה בטיחות." },
      { type: "list", items: [
        "מתאים לעסקים קטנים ובינוניים",
        "ביקורים תקופתיים לפי צורך",
        "מקצועיות גבוהה וניסיון מגוון",
        "עלות נמוכה יותר ומשתנה"
      ]},

      { type: "h3", text: "3. שירותי ייעוץ בטיחות" },
      { type: "p", text: "שירותים נקודתיים ללא לקיחת תפקיד ממונה בטיחות." },
      { type: "list", items: [
        "סקר סיכונים חד-פעמי",
        "הכנה לביקורת מפקח עבודה",
        "הטמעת תקן ISO 45001",
        "חוות דעת מומחה"
      ]},

      { type: "h2", text: "מה כולל שירות ממונה בטיחות מקיף?" },
      { type: "p", text: "שירות איכותי כולל את כל המרכיבים הבאים:" },
      { type: "list", items: [
        "סקר סיכונים שנתי מקיף",
        "תוכנית עבודה שנתית לבטיחות",
        "הדרכות בטיחות לעובדים (כניסה, ריענון, ספציפיות)",
        "ביקורי שטח תקופתיים",
        "ניהול תיק בטיחות ותיעוד",
        "חקירת תאונות וכמעט-תאונות",
        "ייצוג מול מפקחי עבודה",
        "ייעוץ שוטף בנושאי בטיחות",
        "דיווחים תקופתיים להנהלה"
      ]},

      { type: "h2", text: "עלויות שירות ממונה בטיחות" },
      { type: "p", text: "העלויות משתנות בהתאם לגודל הארגון, רמת הסיכון והיקף השירות הנדרש. לקבלת הצעת מחיר מותאמת לעסק שלכם, צרו איתנו קשר לייעוץ חינם." },
      { type: "quote", text: "השקעה בממונה בטיחות משתלמת - עלות תאונת עבודה עלולה להיות גבוהה משמעותית מעלות השירות השנתית." },

      { type: "h2", text: "איך לבחור ספק שירותי בטיחות?" },
      { type: "p", text: "שאלות מפתח לשאול:" },
      { type: "list", items: [
        "מה הניסיון שלכם בענף שלי?",
        "האם יש לכם ביטוח אחריות מקצועית?",
        "מה כולל השירות ומה לא?",
        "מה הזמינות שלכם במקרי חירום?",
        "האם אתם מספקים דוחות תקופתיים?",
        "מה קורה אם לא שבע רצון?"
      ]},

      { type: "h3", text: "סימנים לספק איכותי:" },
      { type: "list", items: [
        "הסמכות ורישיונות מוכרים",
        "המלצות מלקוחות בענף שלכם",
        "הסכם שירות ברור ומפורט",
        "תקשורת שוטפת ונגישות",
        "גישה פרואקטיבית ולא רק תגובתית"
      ]},

      { type: "h2", text: "מה לצפות בתחילת העבודה?" },
      { type: "p", text: "תהליך עבודה תקין כולל:" },
      { type: "list", items: [
        "פגישת היכרות והבנת הצרכים",
        "סיור ראשוני במתקן",
        "סקר סיכונים מקיף",
        "הצגת ממצאים והמלצות",
        "בניית תוכנית עבודה שנתית",
        "התחלת ביצוע - הדרכות, שיפורים, תיעוד"
      ]},

      { type: "h2", text: "טיפים לעבודה אפקטיבית עם ממונה בטיחות" },
      { type: "list", items: [
        "היו שותפים - שתפו אותו בתהליכים ושינויים בארגון",
        "תנו גישה - לכל האזורים, המסמכים והאנשים",
        "הקצו זמן - לפגישות תקופתיות ולהדרכות",
        "קחו ברצינות - יישמו את ההמלצות",
        "תקשרו - דווחו על תאונות, כמעט-תאונות ובעיות"
      ]},

      { type: "h2", text: "סיכום" },
      { type: "p", text: "שירות ממונה בטיחות איכותי הוא השקעה שמחזירה את עצמה. מעבר לעמידה בחוק, ממונה בטיחות טוב יעזור לכם ליצור סביבת עבודה בטוחה יותר, לחסוך כסף ולישון טוב יותר בלילה." },
      { type: "p", text: "באדם בטיחות, אנו מציעים שירות ממונה בטיחות מותאם אישית לעסקים בכל הגדלים, עם התמחות במפעלים, בנייה ותשתיות." },

      { type: "cta", text: "מוכנים להתחיל? צרו קשר לשיחת ייעוץ ראשונית ללא עלות." }
    ]
  },
  // ===== NEW POSTS - BATCH 2 (22-26): אחריות פלילית + ענף הבנייה =====
  {
    id: 22,
    slug: "safety-officer-faq-complete",
    title: "שאלות ותשובות על ממונה בטיחות - המדריך המלא",
    metaTitle: "שאלות ותשובות על ממונה בטיחות | FAQ מקיף | אדם בטיחות",
    metaDescription: "התשובות לכל השאלות הנפוצות על ממונה בטיחות: מתי חייבים, מה התפקידים, כמה עולה ועוד. מדריך שאלות ותשובות מקיף.",
    excerpt: "כל השאלות הנפוצות על ממונה בטיחות במקום אחד - מתי חייבים, מה התפקידים, כמה עולה ועוד.",
    category: "מדריכים",
    date: "2025-01-10",
    readTime: "8 דקות קריאה",
    image: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&auto=format&fit=crop",
    imageAlt: "אדם קורא מסמכים ושואל שאלות",
    keywords: ["שאלות ותשובות", "ממונה בטיחות", "FAQ", "מדריך", "חוק בטיחות", "דרישות"],
    content: [
      { type: "h2", text: "שאלות בסיסיות על ממונה בטיחות" },
      
      { type: "h3", text: "מהו ממונה בטיחות?" },
      { type: "p", text: "ממונה בטיחות הוא איש מקצוע מוסמך שתפקידו לדאוג לבטיחות העובדים במקום העבודה. הוא אחראי על זיהוי סיכונים, הדרכת עובדים, פיקוח על נהלי בטיחות וייצוג מול גורמי רגולציה." },

      { type: "h3", text: "מתי חייבים למנות ממונה בטיחות?" },
      { type: "p", text: "החובה למנות ממונה בטיחות חלה במקרים הבאים:" },
      { type: "list", items: [
        "עסק עם 50 עובדים ומעלה",
        "עסק עם 20+ עובדים בענפים מסוכנים",
        "אתר בנייה עם 25 עובדים ומעלה",
        "עבודה עם חומרים מסוכנים",
        "כל מקום עבודה שמפקח העבודה דורש"
      ]},

      { type: "h3", text: "מה ההבדל בין ממונה בטיחות ליועץ בטיחות?" },
      { type: "p", text: "ממונה בטיחות נושא באחריות משפטית רשמית ומחויב לנהל את מערך הבטיחות באופן שוטף. יועץ בטיחות מספק ייעוץ מקצועי ללא אחריות ניהולית שוטפת." },

      { type: "h2", text: "שאלות על הכשרה ודרישות" },

      { type: "h3", text: "מה ההכשרה הנדרשת לממונה בטיחות?" },
      { type: "list", items: [
        "קורס ממוני בטיחות מוכר (180 שעות לפחות)",
        "השתלמות שנתית (32 שעות = 4 ימים)",
        "לענפים מסוימים - הכשרות נוספות ספציפיות"
      ]},

      { type: "h3", text: "האם כל אחד יכול להיות ממונה בטיחות?" },
      { type: "p", text: "לא. ממונה בטיחות חייב לעבור הכשרה מוכרת, להיות בעל ניסיון רלוונטי ולקבל אישור ממשרד העבודה. בנוסף, הוא צריך כישורים אישיים כמו יכולת תקשורת, תשומת לב לפרטים ויכולת הנחיה." },

      { type: "h3", text: "כמה זמן לוקח להכשיר ממונה בטיחות?" },
      { type: "p", text: "קורס ממוני בטיחות בסיסי אורך כ-180 שעות, בדרך כלל על פני 2-3 חודשים. הכשרות נוספות לענפים ספציפיים יכולות להוסיף עוד שבועות עד חודשים." },

      { type: "h2", text: "שאלות על תפקידים ואחריות" },

      { type: "h3", text: "מה תפקידי ממונה הבטיחות?" },
      { type: "list", items: [
        "ביצוע סקרי סיכונים תקופתיים",
        "הדרכת עובדים בנושאי בטיחות",
        "פיקוח על שימוש בציוד מגן",
        "חקירת תאונות וכמעט-תאונות",
        "ניהול תיק בטיחות ותיעוד",
        "ייצוג מול מפקחי עבודה",
        "עדכון נהלים ותוכנית בטיחות"
      ]},

      { type: "h3", text: "האם ממונה בטיחות יכול להיות אחראי גם על נושאים אחרים?" },
      { type: "p", text: "כן, בארגונים קטנים ממונה בטיחות לעתים משלב תפקידים נוספים. אולם חשוב שיהיה לו מספיק זמן ומשאבים לטפל בבטיחות כראוי." },

      { type: "h3", text: "מה האחריות המשפטית של ממונה בטיחות?" },
      { type: "p", text: "ממונה בטיחות נושא באחריות אישית למילוי תפקידו. במקרה של תאונה, יכול להיחקר ואף להיות מואשם פלילית אם התרשל בתפקידו. לכן חשוב לתעד את כל הפעילות ולעבוד לפי נהלים." },

      { type: "h2", text: "שאלות על עלויות" },

      { type: "h3", text: "כמה עולה ממונה בטיחות?" },
      { type: "p", text: "העלות משתנה לפי היקף המשרה, הניסיון וגודל הארגון. צרו קשר לקבלת הצעת מחיר מותאמת לצרכים שלכם." },

      { type: "h3", text: "האם עדיף ממונה פנימי או חיצוני?" },
      { type: "p", text: "לארגונים קטנים ובינוניים - בדרך כלל ממונה חיצוני יהיה פתרון חסכוני ומקצועי. לארגונים גדולים או עם סיכונים גבוהים - ממונה פנימי עשוי להיות הכרחי. צרו קשר לייעוץ מותאם." },

      { type: "h2", text: "שאלות על ביקורות ופיקוח" },

      { type: "h3", text: "מה קורה בביקורת מפקח עבודה?" },
      { type: "p", text: "מפקח עבודה בודק את מצב הבטיחות במקום העבודה: תיעוד, הדרכות, ציוד מגן, תנאי עבודה ועוד. הממונה צריך להיות זמין לליווי הביקורת ולהציג את כל התיעוד הנדרש." },

      { type: "h3", text: "מה הקנסות על אי-מינוי ממונה בטיחות?" },
      { type: "p", text: "קנסות מנהליים יכולים להגיע לעשרות אלפי שקלים. במקרה של תאונה - האחריות הפלילית והאזרחית יכולה להיות הרבה יותר כבדה." },

      { type: "h2", text: "שאלות נוספות" },

      { type: "h3", text: "כמה פעמים בשנה ממונה בטיחות צריך להגיע?" },
      { type: "p", text: "תלוי בגודל הארגון ורמת הסיכון. לרוב - ביקור חודשי עד שבועי. באתרי בנייה - נוכחות יומית." },

      { type: "h3", text: "האם צריך ממונה בטיחות גם במשרדים?" },
      { type: "p", text: "אם יש 50 עובדים ומעלה - כן. גם במשרדים יש סיכוני בטיחות (ארגונומיה, בטיחות אש, חשמל) שצריך לנהל." },

      { type: "cta", text: "יש לכם שאלות נוספות? צרו קשר ונשמח לענות." }
    ]
  },
  {
    id: 23,
    slug: "management-criminal-liability-work-accidents",
    title: "האחריות הפלילית של מנהלים בתאונות עבודה",
    metaTitle: "אחריות פלילית מנהלים תאונות עבודה | אדם בטיחות",
    metaDescription: "מתי מנהלים יכולים להיות מואשמים פלילית בעקבות תאונת עבודה? מדריך משפטי לאחריות מנהלים ואיך להתגונן.",
    excerpt: "תאונת עבודה קשה יכולה להוביל לכתב אישום פלילי נגד מנהלים. הנה מה שכל מנהל צריך לדעת.",
    category: "רגולציה",
    date: "2025-01-09",
    readTime: "10 דקות קריאה",
    image: "https://images.unsplash.com/photo-1479142506502-19b3a3b7ff33?w=800&auto=format&fit=crop",
    imageAlt: "מסמכים משפטיים ופטיש שופט",
    featured: true,
    keywords: ["אחריות פלילית", "מנהלים", "תאונות עבודה", "משפט פלילי", "רשלנות", "הגנה משפטית"],
    content: [
      { type: "h2", text: "מבוא: כשתאונת עבודה הופכת לפלילית" },
      { type: "p", text: "תאונת עבודה קשה - במיוחד כזו שגרמה למוות או לנכות - מפעילה מנגנון של חקירה פלילית. בישראל, מנהלים ובעלי עסקים יכולים למצוא את עצמם מואשמים בעבירות פליליות בעקבות תאונה במקום העבודה." },
      { type: "quote", text: "בעשור האחרון הוגשו עשרות כתבי אישום נגד מנהלים ובעלי עסקים בעקבות תאונות עבודה קטלניות, חלקם הסתיימו בעונשי מאסר בפועל." },

      { type: "h2", text: "הבסיס המשפטי לאחריות פלילית" },
      { type: "p", text: "האחריות הפלילית של מנהלים מבוססת על מספר חוקים:" },
      { type: "list", items: [
        "פקודת הבטיחות בעבודה - עבירות ספציפיות על תקנות בטיחות",
        "חוק העונשין - עבירות של גרימת מוות או חבלה ברשלנות",
        "חוק ארגון הפיקוח על העבודה - אחריות אישית של מעסיקים"
      ]},

      { type: "h3", text: "סעיף 304 לחוק העונשין - גרימת מוות ברשלנות" },
      { type: "p", text: "העונש המקסימלי: 3 שנות מאסר. עבירה זו מיוחסת למנהלים כאשר מוכח כי התרשלו בחובת הזהירות שלהם." },

      { type: "h3", text: "סעיף 338 לחוק העונשין - גרימת חבלה ברשלנות" },
      { type: "p", text: "העונש המקסימלי: שנת מאסר. חל במקרים של פציעות קשות שלא הסתיימו במוות." },

      { type: "h2", text: "מתי מנהל יואשם פלילית?" },
      { type: "p", text: "לא כל תאונת עבודה מובילה לאישום פלילי. התביעה צריכה להוכיח:" },
      { type: "list", items: [
        "קיימת חובת זהירות - המנהל היה אחראי על בטיחות העובדים",
        "הופרה חובת הזהירות - המנהל התרשל או פעל שלא כראוי",
        "קשר סיבתי - הרשלנות גרמה או תרמה לתאונה",
        "יסוד נפשי - המנהל היה מודע או היה צריך להיות מודע לסיכון"
      ]},

      { type: "h2", text: "מי יכול להיות מואשם?" },
      { type: "list", items: [
        "מנכ\"ל ובעלי החברה",
        "מנהלי מפעל ומנהלי אתר",
        "ממונה בטיחות",
        "מנהלי עבודה ומפקחים",
        "קבלנים וקבלני משנה"
      ]},
      { type: "p", text: "האחריות יכולה לחול על מספר אנשים במקביל - כל אחד לפי תפקידו ומידת מעורבותו." },

      { type: "h2", text: "דוגמאות מהפסיקה הישראלית" },
      
      { type: "h3", text: "מקרה 1: נפילה מגובה באתר בנייה" },
      { type: "p", text: "מנהל אתר הורשע בגרימת מוות ברשלנות לאחר שעובד נפל מפיגום לא תקני. העונש: 18 חודשי מאסר בפועל." },

      { type: "h3", text: "מקרה 2: תאונה במכונה תעשייתית" },
      { type: "p", text: "מנכ\"ל מפעל הורשע לאחר שעובד נפגע ממכונה ללא מגן בטיחות. הוכח שהמנכ\"ל ידע על הליקוי ולא תיקן. העונש: קנס כספי כבד + מאסר על תנאי." },

      { type: "h3", text: "מקרה 3: חשיפה לחומרים מסוכנים" },
      { type: "p", text: "בעל מפעל הורשע לאחר שעובדים נחשפו לחומרים מסוכנים ללא ציוד מגן מתאים. חלק מהעובדים פיתחו מחלות כרוניות." },

      { type: "h2", text: "איך להתגונן מאחריות פלילית?" },
      { type: "p", text: "ההגנה הטובה ביותר היא מניעה. הנה הצעדים שיגנו עליכם:" },

      { type: "h3", text: "1. מינוי ממונה בטיחות מוסמך" },
      { type: "p", text: "ממונה בטיחות מקצועי מוודא עמידה בתקנות ומתעד את כל הפעילות. זה מראה שעשיתם את המקסימום למנוע תאונות." },

      { type: "h3", text: "2. תיעוד מקיף" },
      { type: "list", items: [
        "תעדו כל הדרכת בטיחות",
        "שמרו רישום של סקרי סיכונים",
        "תעדו תיקון ליקויים",
        "שמרו פרוטוקולים של ועדות בטיחות"
      ]},

      { type: "h3", text: "3. הדרכות שוטפות" },
      { type: "p", text: "הדרכות בטיחות מתועדות מוכיחות שהעובדים ידעו על הסיכונים ואיך להתמודד איתם." },

      { type: "h3", text: "4. אכיפה פנימית" },
      { type: "p", text: "לא מספיק ליצור נהלים - צריך גם לאכוף אותם. תעדו מקרים של אכיפה ומתן אזהרות לעובדים שמפרים נהלים." },

      { type: "h3", text: "5. ביטוח אחריות מקצועית" },
      { type: "p", text: "ביטוח לא ימנע אישום פלילי, אבל יכסה עלויות הגנה משפטית ותביעות אזרחיות." },

      { type: "h2", text: "מה לעשות אחרי תאונה?" },
      { type: "list", items: [
        "טפלו בנפגע ודווחו לגורמים הרלוונטיים",
        "אל תשנו את זירת התאונה",
        "תעדו הכל - תמונות, עדויות, מסמכים",
        "התייעצו עם עורך דין מתמחה",
        "שתפו פעולה עם החקירה אבל אל תודו באשמה",
        "בצעו חקירה פנימית והפיקו לקחים"
      ]},

      { type: "h2", text: "סיכום" },
      { type: "p", text: "אחריות פלילית על תאונות עבודה היא סיכון אמיתי לכל מנהל. ההגנה הטובה ביותר היא ניהול בטיחות מקצועי ומתועד. השקעה בבטיחות היום יכולה למנוע מאסר מחר." },

      { type: "cta", text: "רוצים לוודא שאתם מוגנים? צרו קשר לסקירת ציות בטיחותית." }
    ]
  },
  {
    id: 24,
    slug: "construction-site-organization-plan",
    title: "תוכנית התארגנות אתר בנייה - מדריך מקיף",
    metaTitle: "תוכנית התארגנות אתר בנייה | מדריך לקבלנים | אדם בטיחות",
    metaDescription: "איך לבנות תוכנית התארגנות לאתר בנייה? המדריך המלא לקבלנים על דרישות, תכנון ויישום תוכנית בטיחות באתר.",
    excerpt: "תוכנית התארגנות היא מסמך חובה לכל אתר בנייה. הנה איך לבנות תוכנית שתעמוד בדרישות ותשמור על העובדים.",
    category: "בנייה",
    date: "2025-01-08",
    readTime: "12 דקות קריאה",
    image: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&auto=format&fit=crop",
    imageAlt: "תכנון אתר בנייה עם שרטוטים",
    keywords: ["תוכנית התארגנות", "אתר בנייה", "קבלן", "תכנון בטיחות", "דרישות חוק"],
    content: [
      { type: "h2", text: "מהי תוכנית התארגנות אתר בנייה?" },
      { type: "p", text: "תוכנית התארגנות אתר בנייה היא מסמך מקיף שמתאר את כל ההיבטים הארגוניים והבטיחותיים של פרויקט בנייה. היא מהווה 'מפת דרכים' לניהול האתר ונדרשת על פי חוק לפני תחילת העבודות." },
      { type: "quote", text: "תקנות הבטיחות בעבודות בנייה מחייבות הכנת תוכנית התארגנות לכל אתר בנייה שבו עובדים 25 עובדים ומעלה או שמתבצעות בו עבודות מסוכנות." },

      { type: "h2", text: "למה צריך תוכנית התארגנות?" },
      { type: "list", items: [
        "דרישה חוקית - ללא תוכנית אי אפשר להתחיל בעבודות",
        "מניעת תאונות - תכנון מראש מזהה סיכונים",
        "יעילות תפעולית - סדר וארגון חוסכים זמן וכסף",
        "אחריות משפטית - מסמך מתועד מגן במקרה של תאונה",
        "תיאום קבלנים - כל הגורמים באתר יודעים מה התפקיד שלהם"
      ]},

      { type: "h2", text: "מרכיבי תוכנית ההתארגנות" },

      { type: "h3", text: "1. פרטי הפרויקט" },
      { type: "list", items: [
        "שם הפרויקט ומיקום",
        "פרטי המזמין (היזם)",
        "פרטי הקבלן הראשי",
        "פרטי קבלני המשנה",
        "לוח זמנים צפוי"
      ]},

      { type: "h3", text: "2. מבנה ארגוני" },
      { type: "list", items: [
        "מנהל הפרויקט ותפקידיו",
        "ממונה בטיחות - שם, כשירות, זמינות",
        "מנהלי עבודה ואחריותם",
        "גורמי חירום - רופא, עזרה ראשונה",
        "נציגי קבלני משנה"
      ]},

      { type: "h3", text: "3. תכנון האתר" },
      { type: "p", text: "שרטוט מפורט של האתר הכולל:" },
      { type: "list", items: [
        "גבולות האתר וגידור",
        "דרכי גישה ופינוי",
        "אזורי עבודה ואחסון",
        "מיקום מנופים ועגורנים",
        "מערכות חשמל ומים",
        "מתקני רווחה (שירותים, חדר אוכל)"
      ]},

      { type: "h3", text: "4. זיהוי סיכונים ואמצעי מניעה" },
      { type: "p", text: "פירוט כל הסיכונים הצפויים בכל שלב של הפרויקט:" },
      { type: "list", items: [
        "עבודה בגובה - פיגומים, מעקות, רתמות",
        "חפירות ויסודות - דיפון, תמיכות",
        "מנופים והרמה - אזורי סכנה, אותות",
        "חשמל - נעילה, הארקה, בידוד",
        "חומרים מסוכנים - אחסון, טיפול, פינוי"
      ]},

      { type: "h3", text: "5. נהלי חירום" },
      { type: "list", items: [
        "תוכנית פינוי - מסלולים, נקודות התכנסות",
        "גורמי חירום - טלפונים, מיקומים",
        "ציוד כיבוי אש",
        "ערכות עזרה ראשונה",
        "תרגילים תקופתיים"
      ]},

      { type: "h3", text: "6. הדרכות ובקרה" },
      { type: "list", items: [
        "הדרכת כניסה לאתר לכל עובד",
        "הדרכות ספציפיות (גובה, מנופים, חומ\"ס)",
        "לוח הדרכות שנתי",
        "בקרות שטח ותיעוד",
        "דיווח על ליקויים ותיקונם"
      ]},

      { type: "h2", text: "איך לכתוב תוכנית התארגנות?" },

      { type: "h3", text: "שלב 1: איסוף מידע" },
      { type: "p", text: "אספו את כל המידע על הפרויקט - תוכניות, לוחות זמנים, רשימת קבלנים, מפרטים טכניים." },

      { type: "h3", text: "שלב 2: סיור באתר" },
      { type: "p", text: "בצעו סיור מקדים באתר לזיהוי מגבלות פיזיות, סיכונים סביבתיים ותנאים מיוחדים." },

      { type: "h3", text: "שלב 3: זיהוי סיכונים" },
      { type: "p", text: "נתחו כל שלב בפרויקט וזהו את הסיכונים הצפויים. השתמשו בניסיון מפרויקטים קודמים." },

      { type: "h3", text: "שלב 4: תכנון אמצעי מניעה" },
      { type: "p", text: "לכל סיכון - הגדירו אמצעי מניעה ספציפיים, אחראים וללו\"ז." },

      { type: "h3", text: "שלב 5: כתיבת המסמך" },
      { type: "p", text: "כתבו את התוכנית בשפה ברורה, עם שרטוטים וטבלאות. ודאו שהמסמך קריא ונגיש." },

      { type: "h3", text: "שלב 6: אישור והפצה" },
      { type: "p", text: "קבלו אישור מהמזמין ומגורמי הפיקוח. הפיצו לכל בעלי התפקידים באתר." },

      { type: "h2", text: "עדכון התוכנית" },
      { type: "p", text: "תוכנית ההתארגנות היא מסמך חי שצריך להתעדכן:" },
      { type: "list", items: [
        "בכל שינוי מהותי בפרויקט",
        "לפני התחלת שלב חדש",
        "לאחר תאונה או כמעט-תאונה",
        "כאשר נכנס קבלן משנה חדש",
        "לפי דרישת מפקח עבודה"
      ]},

      { type: "h2", text: "טעויות נפוצות" },
      { type: "list", items: [
        "תוכנית גנרית שלא מותאמת לפרויקט",
        "חוסר עדכון לאורך הפרויקט",
        "תוכנית על הנייר בלבד - ללא יישום בשטח",
        "היעדר תיאום עם קבלני משנה",
        "תיעוד לקוי של שינויים והחלטות"
      ]},

      { type: "h2", text: "סיכום" },
      { type: "p", text: "תוכנית התארגנות איכותית היא הבסיס לאתר בנייה בטוח ויעיל. השקעה בתכנון מקדים תחסוך לכם תאונות, עיכובים ובעיות משפטיות." },

      { type: "cta", text: "צריכים עזרה בהכנת תוכנית התארגנות? צרו קשר עם המומחים שלנו." }
    ]
  },
  {
    id: 25,
    slug: "construction-industry-occupational-safety",
    title: "בטיחות תעסוקתית בענף הבנייה - סיכונים ופתרונות",
    metaTitle: "בטיחות תעסוקתית בבנייה | סיכונים ופתרונות | אדם בטיחות",
    metaDescription: "ענף הבנייה מוביל בתאונות עבודה בישראל. מדריך מקיף לסיכוני הבטיחות בבנייה ואיך למנוע תאונות.",
    excerpt: "ענף הבנייה מוביל בתאונות עבודה קטלניות. הכירו את הסיכונים העיקריים ואיך למנוע אותם.",
    category: "בנייה",
    date: "2025-01-07",
    readTime: "11 דקות קריאה",
    image: "https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?w=800&auto=format&fit=crop",
    imageAlt: "עובדי בניין עם ציוד בטיחות",
    featured: true,
    keywords: ["בטיחות בבנייה", "תאונות עבודה", "סיכונים", "ענף הבנייה", "מניעת תאונות"],
    content: [
      { type: "h2", text: "ענף הבנייה - האתגר הבטיחותי הגדול" },
      { type: "p", text: "ענף הבנייה בישראל הוא אחד הענפים הכלכליים החשובים ביותר, אך גם המסוכנים ביותר. כמעט מחצית מכלל ההרוגים בתאונות עבודה בישראל - עובדי בנייה." },
      { type: "quote", text: "בשנת 2023 נהרגו 28 עובדים באתרי בנייה בישראל. מאחורי כל מספר - משפחה שאיבדה את יקירה." },

      { type: "h2", text: "הסיכונים העיקריים בענף הבנייה" },

      { type: "h3", text: "1. נפילה מגובה - הסיבה מספר 1" },
      { type: "p", text: "נפילות מגובה אחראיות ליותר מ-50% ממקרי המוות בבנייה:" },
      { type: "list", items: [
        "נפילה מפיגומים - הסיבה הנפוצה ביותר",
        "נפילה מסולמות",
        "נפילה מגגות ומרפסות",
        "נפילה לתוך פירי מעליות וחללים פתוחים",
        "נפילה מעגורנים וסלים"
      ]},

      { type: "h3", text: "2. התמוטטות מבנים וחפירות" },
      { type: "list", items: [
        "קריסת קירות ותקרות במהלך בנייה",
        "התמוטטות תבניות יציקה",
        "קריסת דפנות חפירות",
        "התמוטטות מבנים ישנים בהריסה"
      ]},

      { type: "h3", text: "3. פגיעה מחפצים נופלים" },
      { type: "list", items: [
        "נפילת חומרי בנייה מקומות",
        "נפילת כלי עבודה",
        "התנתקות משאות ממנופים",
        "נפילת פסולת בנייה"
      ]},

      { type: "h3", text: "4. תאונות מנופים ועגורנים" },
      { type: "list", items: [
        "התנגשות זרוע מנוף בעובדים",
        "התהפכות מנופים",
        "התנתקות משאות",
        "התחשמלות מקווי חשמל"
      ]},

      { type: "h3", text: "5. סיכונים נוספים" },
      { type: "list", items: [
        "התחשמלות - עבודה ליד קווי חשמל, חיבורים לקויים",
        "פגיעה מכלי רכב כבדים - משאיות, טרקטורים",
        "חשיפה לרעש - נזק לשמיעה",
        "חשיפה לאבק - מחלות ריאה",
        "עבודה בתנאי חום קיצוניים"
      ]},

      { type: "h2", text: "למה ענף הבנייה כל כך מסוכן?" },
      { type: "list", items: [
        "סביבת עבודה משתנה - כל יום נראה אחרת",
        "עבודה בגובה רב",
        "שימוש במכונות כבדות",
        "לחצי לוחות זמנים ותקציבים",
        "כוח עבודה לא מיומן או זמני",
        "עבודה בתנאי מזג אוויר קשים",
        "ריבוי קבלני משנה וחוסר תיאום"
      ]},

      { type: "h2", text: "אמצעי מניעה לתאונות בבנייה" },

      { type: "h3", text: "מניעת נפילות מגובה" },
      { type: "list", items: [
        "פיגומים תקניים עם מעקות ורשתות",
        "רתמות בטיחות לכל עובד בגובה",
        "קווי חיים ונקודות עיגון",
        "הדרכת עבודה בגובה לכל עובד",
        "בדיקות תקופתיות של ציוד"
      ]},

      { type: "h3", text: "מניעת התמוטטויות" },
      { type: "list", items: [
        "תכנון הנדסי מקצועי",
        "דיפון וחיזוק חפירות",
        "בדיקות יציבות לפני כל שלב",
        "איסור כניסה לאזורים לא יציבים",
        "פיקוח הנדסי צמוד"
      ]},

      { type: "h3", text: "מניעת פגיעה מחפצים נופלים" },
      { type: "list", items: [
        "קסדות בטיחות לכל עובד",
        "רשתות לכידה מתחת לאזורי עבודה",
        "אזורי סכנה מגודרים",
        "אחסון בטוח של חומרים",
        "איסור השלכת פסולת מגובה"
      ]},

      { type: "h3", text: "בטיחות מנופים" },
      { type: "list", items: [
        "הפעלה על ידי מפעילים מוסמכים בלבד",
        "בדיקות תקופתיות",
        "אזורי סכנה מסומנים",
        "תקשורת ברורה בין מפעיל לעובדים",
        "בדיקת קרקע ויציבות"
      ]},

      { type: "h2", text: "תפקיד ממונה הבטיחות באתר" },
      { type: "p", text: "ממונה בטיחות מנוסה הוא קריטי להצלחת האתר:" },
      { type: "list", items: [
        "נוכחות יומית באתר",
        "סיורי בטיחות שוטפים",
        "הדרכת עובדים חדשים",
        "פיקוח על ציוד מגן",
        "עצירת עבודות מסוכנות",
        "דיווח והפקת לקחים"
      ]},

      { type: "h2", text: "הרגולציה בענף הבנייה" },
      { type: "p", text: "התקנות העיקריות שחלות על אתרי בנייה:" },
      { type: "list", items: [
        "תקנות הבטיחות בעבודות בנייה",
        "תקנות עבודה בגובה",
        "תקנות כלי הרמה ומנופים",
        "תקנות חפירות וחציבה",
        "צו הבטיחות בעבודה (עבודות בנייה)"
      ]},

      { type: "h2", text: "מגמות לעתיד" },
      { type: "list", items: [
        "בנייה תעשייתית מראש - פחות עבודה בשטח",
        "טכנולוגיות חדשות - דרונים, חיישנים, AI",
        "רגולציה מחמירה והגברת אכיפה",
        "הדרכות מתקדמות עם מציאות מדומה",
        "דגש על תרבות בטיחות"
      ]},

      { type: "h2", text: "סיכום" },
      { type: "p", text: "בטיחות בבנייה היא לא יעד בלתי אפשרי. עם תכנון נכון, ציוד מתאים והנהגה מחויבת - אפשר להוריד משמעותית את מספר התאונות. באדם בטיחות, אנו מלווים אתרי בנייה עם ניסיון של שנים בפרויקטים מורכבים." },

      { type: "cta", text: "צריכים ליווי בטיחותי לאתר הבנייה שלכם? צרו קשר עוד היום." }
    ]
  },
  {
    id: 26,
    slug: "crane-safety-construction-sites",
    title: "בטיחות מנופים ועגורנים באתרי בנייה",
    metaTitle: "בטיחות מנופים ועגורנים | אתרי בנייה | אדם בטיחות",
    metaDescription: "מדריך מקיף לבטיחות מנופים ועגורנים באתרי בנייה. דרישות חוק, סיכונים, הדרכות ונהלי עבודה בטוחים.",
    excerpt: "מנופים ועגורנים הם מהציוד המסוכן ביותר באתרי בנייה. הנה כל מה שצריך לדעת על עבודה בטוחה איתם.",
    category: "בנייה",
    date: "2025-01-06",
    readTime: "9 דקות קריאה",
    image: "https://images.unsplash.com/photo-1531834685032-c34bf0d84c77?w=800&auto=format&fit=crop",
    imageAlt: "עגורן באתר בנייה",
    keywords: ["מנופים", "עגורנים", "בטיחות", "אתר בנייה", "הרמה", "הפעלת מנוף"],
    content: [
      { type: "h2", text: "מנופים ועגורנים - כוח וסיכון" },
      { type: "p", text: "מנופים ועגורנים הם חלק בלתי נפרד מכל אתר בנייה מודרני. הם מאפשרים להרים ולהזיז משקלים של עשרות טונות, אך הכוח הזה בא עם סיכון משמעותי. תאונות מנופים הן מהקטלניות בענף הבנייה." },
      { type: "quote", text: "כ-15% מתאונות העבודה הקטלניות בבנייה קשורות למנופים ולציוד הרמה." },

      { type: "h2", text: "סוגי מנופים נפוצים באתרי בנייה" },
      { type: "list", items: [
        "עגורן צריח (Tower Crane) - הנפוץ ביותר באתרי בנייה גדולים",
        "מנוף נייד (Mobile Crane) - על גלגלים או זחלים",
        "מנוף צמוד למבנה (Luffing Crane)",
        "מנוף עצמי הרמה (Self-Erecting Crane)",
        "מנוף הידראולי על משאית"
      ]},

      { type: "h2", text: "הסיכונים העיקריים" },

      { type: "h3", text: "1. התהפכות המנוף" },
      { type: "list", items: [
        "עומס יתר מעבר לכושר הנשיאה",
        "רוחות חזקות",
        "קרקע לא יציבה",
        "תנועה פתאומית"
      ]},

      { type: "h3", text: "2. התנתקות משאות" },
      { type: "list", items: [
        "קשירה לקויה",
        "ציוד תלייה פגום",
        "עומס לא מאוזן",
        "תנועה חדה מדי"
      ]},

      { type: "h3", text: "3. התנגשות" },
      { type: "list", items: [
        "פגיעה בעובדים על הקרקע",
        "התנגשות בין מנופים",
        "פגיעה במבנים או מתקנים",
        "התנגשות עם קווי חשמל"
      ]},

      { type: "h3", text: "4. התחשמלות" },
      { type: "p", text: "מגע בקווי חשמל עיליים הוא מהסיכונים הקטלניים ביותר. הזרוע הארוכה של המנוף יכולה להגיע למקומות מסוכנים." },

      { type: "h2", text: "דרישות החוק למפעילי מנופים" },
      { type: "p", text: "החוק בישראל מחייב:" },
      { type: "list", items: [
        "רישיון הפעלה תקף לסוג המנוף הספציפי",
        "בדיקות רפואיות תקופתיות",
        "הכשרה מעודכנת",
        "ניסיון מינימלי לפני הפעלה עצמאית",
        "גיל מינימלי 18"
      ]},

      { type: "h2", text: "בדיקות תקופתיות למנופים" },
      { type: "p", text: "כל מנוף חייב לעבור בדיקות:" },
      { type: "list", items: [
        "בדיקה יומית לפני תחילת עבודה (על ידי המפעיל)",
        "בדיקה שבועית מקיפה",
        "בדיקה חודשית על ידי טכנאי מוסמך",
        "בדיקה שנתית מלאה על ידי בודק מוסמך",
        "בדיקה לאחר כל תקלה או תאונה"
      ]},

      { type: "h2", text: "נהלי עבודה בטוחים" },

      { type: "h3", text: "לפני ההפעלה" },
      { type: "list", items: [
        "בדיקה ויזואלית של כל המנוף",
        "בדיקת ציוד התלייה והסלינגים",
        "וידוא יציבות הקרקע והמסד",
        "בדיקת מזג אוויר (רוח!)",
        "תיאום עם עובדים אחרים באתר"
      ]},

      { type: "h3", text: "במהלך ההפעלה" },
      { type: "list", items: [
        "לעולם לא לחרוג מכושר הנשיאה",
        "תנועות איטיות וזהירות",
        "תקשורת רצופה עם הסבל/מנחה",
        "אזור סכנה פנוי מאנשים",
        "עיניים על המשא בכל רגע"
      ]},

      { type: "h3", text: "סיום העבודה" },
      { type: "list", items: [
        "הנחת המשא בבטחה",
        "נעילת המנוף",
        "הרחקת הזרוע לכיוון בטוח",
        "דיווח על כל תקלה או בעיה",
        "רישום ביומן"
      ]},

      { type: "h2", text: "תפקיד הסבל (מנחה מנוף)" },
      { type: "p", text: "הסבל הוא שותף קריטי לבטיחות:" },
      { type: "list", items: [
        "מוודא קשירה נכונה של המשא",
        "מנחה את המפעיל באותות יד או רדיו",
        "מפנה אנשים מאזור הסכנה",
        "עוצר את העבודה בזיהוי סיכון",
        "חייב להיות מוסמך ומאושר"
      ]},

      { type: "h2", text: "אותות תקשורת" },
      { type: "p", text: "קיימת שפת אותות אוניברסלית לתקשורת עם מפעיל המנוף:" },
      { type: "list", items: [
        "הרמה - אגרוף כלפי מעלה עם תנועה מעגלית",
        "הורדה - אגרוף כלפי מטה עם תנועה מעגלית",
        "עצור - כף יד פרושה אופקית",
        "חירום - שתי ידיים מונפות בזווית"
      ]},

      { type: "h2", text: "עבודה ליד קווי חשמל" },
      { type: "p", text: "מרחקי בטיחות מינימליים מקווי חשמל:" },
      { type: "list", items: [
        "עד 50 קילו-וולט: 3 מטרים לפחות",
        "50-200 קילו-וולט: 5 מטרים לפחות",
        "מעל 200 קילו-וולט: 7 מטרים לפחות",
        "אם לא ניתן לשמור מרחק - יש לנתק את החשמל"
      ]},

      { type: "h2", text: "עבודה בתנאי מזג אוויר" },
      { type: "list", items: [
        "רוח מעל 60 קמ\"ש - להפסיק עבודה עם עגורן צריח",
        "סופה או ברקים - להפסיק כל עבודה",
        "ראות לקויה - זהירות מוגברת",
        "גשם כבד - זהירות מהחלקה ומראות"
      ]},

      { type: "h2", text: "תאונות נפוצות ולקחים" },
      { type: "p", text: "לקחים מתאונות מנופים בישראל:" },
      { type: "list", items: [
        "חריגה מכושר נשיאה - תמיד לבדוק לפי טבלאות",
        "קשירה לקויה - להשתמש רק בציוד תקין",
        "חוסר תקשורת - תמיד לוודא קשר עם הסבל",
        "התעלמות ממזג אוויר - לעצור כשצריך",
        "לחץ לסיים - בטיחות קודמת ללו\"ז"
      ]},

      { type: "h2", text: "סיכום" },
      { type: "p", text: "עבודה בטוחה עם מנופים דורשת הכשרה, ציוד תקין, נהלים ברורים ותקשורת טובה. כל תקלה במנוף יכולה להוביל לאסון. באדם בטיחות, אנו מספקים הדרכות מנופים והכשרות למפעילים ולסבלים." },

      { type: "cta", text: "צריכים הדרכת מנופים לצוות שלכם? צרו קשר לתיאום." }
    ]
  },
  // ===== NEW POSTS - BATCH 3 (27-31): אחריות קבלן + מערכות ניהול בטיחות =====
  {
    id: 27,
    slug: "main-contractor-safety-responsibility",
    title: "אחריות קבלן ראשי על בטיחות באתר הבנייה",
    metaTitle: "אחריות קבלן ראשי על בטיחות | אתר בנייה | אדם בטיחות",
    metaDescription: "מהי האחריות המשפטית של הקבלן הראשי על בטיחות באתר הבנייה? מדריך מקיף לאחריות, חובות ודרכי הגנה.",
    excerpt: "הקבלן הראשי נושא באחריות כוללת על בטיחות כל העובדים באתר - כולל קבלני משנה. הנה מה שצריך לדעת.",
    category: "בנייה",
    date: "2025-01-05",
    readTime: "9 דקות קריאה",
    image: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=800&auto=format&fit=crop",
    imageAlt: "מנהל פרויקט באתר בנייה",
    keywords: ["קבלן ראשי", "אחריות בטיחות", "אתר בנייה", "קבלני משנה", "חוק"],
    content: [
      { type: "h2", text: "הקבלן הראשי - האחראי על הכל" },
      { type: "p", text: "בעולם הבנייה, הקבלן הראשי (מבצע הבנייה) נושא באחריות כוללת על בטיחות כל העובדים באתר - לא רק העובדים שלו, אלא גם עובדי קבלני המשנה. זוהי אחריות משפטית כבדה שרבים מהקבלנים לא מודעים לה במלואה." },
      { type: "quote", text: "תקנות הבטיחות בעבודות בנייה קובעות במפורש: 'מבצע הבנייה אחראי לבטיחות ולבריאות של כל אדם הנמצא באתר הבנייה'." },

      { type: "h2", text: "מה כולל הגדרת 'מבצע בנייה'?" },
      { type: "p", text: "על פי החוק, מבצע הבנייה הוא:" },
      { type: "list", items: [
        "קבלן ראשי שזכה במכרז",
        "קבלן שמבצע עבודות בנייה עבור יזם",
        "יזם שמבצע עבודות בעצמו",
        "כל מי שאחראי על ביצוע עבודות הבנייה בפועל"
      ]},

      { type: "h2", text: "חובות הקבלן הראשי בנושא בטיחות" },

      { type: "h3", text: "1. מינוי ממונה בטיחות" },
      { type: "list", items: [
        "מינוי ממונה בטיחות מוסמך לאתר",
        "וידוא נוכחות הממונה בזמן העבודות",
        "מתן סמכויות לממונה לעצור עבודות מסוכנות"
      ]},

      { type: "h3", text: "2. תכנון ותיעוד" },
      { type: "list", items: [
        "הכנת תוכנית התארגנות אתר",
        "ביצוע סקרי סיכונים לכל שלב",
        "תיעוד כל הפעילות הבטיחותית",
        "ניהול תיק בטיחות מסודר"
      ]},

      { type: "h3", text: "3. ציוד ותשתיות" },
      { type: "list", items: [
        "הספקת ציוד בטיחות תקני",
        "הקמת תשתיות בטיחות (פיגומים, מעקות, רשתות)",
        "תחזוקת ציוד ומכונות",
        "סימון אזורי סכנה"
      ]},

      { type: "h3", text: "4. הדרכה ופיקוח" },
      { type: "list", items: [
        "הדרכת כניסה לאתר לכל עובד",
        "פיקוח על קיום נהלי בטיחות",
        "אכיפת שימוש בציוד מגן",
        "עצירת עבודות מסוכנות"
      ]},

      { type: "h2", text: "אחריות על קבלני משנה" },
      { type: "p", text: "זה החלק הכי קריטי: הקבלן הראשי אחראי גם על בטיחות עובדי קבלני המשנה:" },
      { type: "list", items: [
        "חובה לוודא שקבלני משנה עומדים בדרישות בטיחות",
        "חובה להדריך את עובדי קבלני המשנה",
        "חובה לפקח על עבודתם",
        "חובה לספק להם סביבת עבודה בטוחה",
        "אחריות משפטית על תאונות של עובדיהם"
      ]},
      { type: "p", text: "העברת עבודה לקבלן משנה לא מעבירה את האחריות!" },

      { type: "h2", text: "אחריות משפטית - פלילית ואזרחית" },

      { type: "h3", text: "אחריות פלילית" },
      { type: "p", text: "במקרה של תאונה קשה, הקבלן הראשי יכול להיות מואשם ב:" },
      { type: "list", items: [
        "גרימת מוות ברשלנות (עד 3 שנות מאסר)",
        "גרימת חבלה ברשלנות (עד שנת מאסר)",
        "הפרת תקנות בטיחות (קנסות כבדים)"
      ]},

      { type: "h3", text: "אחריות אזרחית" },
      { type: "list", items: [
        "פיצויים לנפגעים ומשפחותיהם",
        "תביעות שיפוי מחברות ביטוח",
        "נזקי עיכוב לפרויקט"
      ]},

      { type: "h2", text: "איך להתגונן?" },

      { type: "h3", text: "1. תיעוד מקיף" },
      { type: "p", text: "תעדו הכל: הדרכות, בדיקות, ליקויים ותיקונם, פרוטוקולים, התראות." },

      { type: "h3", text: "2. הסכמים עם קבלני משנה" },
      { type: "list", items: [
        "כללו סעיפי בטיחות מפורטים בחוזה",
        "דרשו אישורי הכשרה מעובדיהם",
        "קבעו סנקציות על הפרות בטיחות",
        "דרשו ביטוחים מתאימים"
      ]},

      { type: "h3", text: "3. פיקוח פעיל" },
      { type: "p", text: "אל תסתמכו על קבלני המשנה. פקחו באופן פעיל על עבודתם." },

      { type: "h3", text: "4. ממונה בטיחות מנוסה" },
      { type: "p", text: "ממונה בטיחות מקצועי הוא קו ההגנה הראשון שלכם." },

      { type: "h2", text: "תפקיד המזמין (היזם)" },
      { type: "p", text: "גם למזמין העבודות יש אחריות:" },
      { type: "list", items: [
        "לוודא שהקבלן הראשי עומד בדרישות",
        "לא להפעיל לחץ שמפר בטיחות",
        "להקצות זמן ותקציב לבטיחות",
        "לדרוש דיווחי בטיחות שוטפים"
      ]},

      { type: "h2", text: "סיכום" },
      { type: "p", text: "האחריות של הקבלן הראשי על בטיחות באתר היא מקיפה ומחייבת. היא לא נעלמת כשמעבירים עבודה לקבלני משנה. ניהול בטיחות מקצועי ומתועד הוא לא רק חובה חוקית - הוא הגנה על חייהם של עובדים ועל העסק שלכם." },

      { type: "cta", text: "צריכים ליווי בטיחותי לפרויקט הבנייה שלכם? צרו קשר." }
    ]
  },
  {
    id: 28,
    slug: "construction-work-accidents-statistics",
    title: "תאונות עבודה בענף הבנייה - עובדות ונתונים",
    metaTitle: "סטטיסטיקה תאונות עבודה בבנייה | נתונים 2024 | אדם בטיחות",
    metaDescription: "נתונים וסטטיסטיקות על תאונות עבודה בענף הבנייה בישראל. מספרים, מגמות וניתוח הסיבות העיקריות.",
    excerpt: "ענף הבנייה מוביל בתאונות עבודה קטלניות. הנה הנתונים העדכניים והניתוח שלהם.",
    category: "בנייה",
    date: "2025-01-04",
    readTime: "7 דקות קריאה",
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&auto=format&fit=crop",
    imageAlt: "אתר בנייה",
    keywords: ["תאונות עבודה", "סטטיסטיקה", "בנייה", "נפגעים", "מגמות"],
    content: [
      { type: "h2", text: "המצב בישראל - תמונת מצב" },
      { type: "p", text: "ענף הבנייה בישראל הוא מהמסוכנים בעולם המערבי. למרות שיפורים בשנים האחרונות, עדיין מדובר בבעיה חמורה שדורשת התייחסות." },
      { type: "quote", text: "ענף הבנייה מהווה כ-7% מסך המועסקים בישראל, אך אחראי לכמעט 50% מכלל ההרוגים בתאונות עבודה." },

      { type: "h2", text: "נתונים עדכניים" },
      { type: "h3", text: "מקרי מוות" },
      { type: "list", items: [
        "2023: 28 הרוגים באתרי בנייה",
        "2022: 31 הרוגים באתרי בנייה",
        "2021: 25 הרוגים באתרי בנייה",
        "מגמה: יציבות יחסית עם ירידה קלה"
      ]},

      { type: "h3", text: "פציעות" },
      { type: "list", items: [
        "אלפי פציעות בשנה בענף הבנייה",
        "מאות פציעות קשות המובילות לנכות",
        "רבים מהמקרים לא מדווחים"
      ]},

      { type: "h2", text: "סיבות התאונות העיקריות" },

      { type: "h3", text: "1. נפילה מגובה - 50%+" },
      { type: "p", text: "הסיבה מספר 1 לתאונות קטלניות בבנייה:" },
      { type: "list", items: [
        "נפילה מפיגומים (הנפוצה ביותר)",
        "נפילה מסולמות",
        "נפילה מגגות",
        "נפילה מפתחים לא מוגנים"
      ]},

      { type: "h3", text: "2. התמוטטות - 15%" },
      { type: "list", items: [
        "קריסת קירות ותקרות",
        "התמוטטות חפירות",
        "קריסת תבניות יציקה"
      ]},

      { type: "h3", text: "3. פגיעה מחפצים - 12%" },
      { type: "list", items: [
        "נפילת חומרי בנייה",
        "נפילת כלי עבודה",
        "התנתקות משאות ממנופים"
      ]},

      { type: "h3", text: "4. תאונות כלי רכב ומכונות - 10%" },
      { type: "list", items: [
        "תאונות מנופים ועגורנים",
        "פגיעה מכלי רכב כבדים",
        "תאונות בובקטים וטרקטורים"
      ]},

      { type: "h3", text: "5. סיבות נוספות - 13%" },
      { type: "list", items: [
        "התחשמלות",
        "חשיפה לחומרים מסוכנים",
        "לכידה במכונות"
      ]},

      { type: "h2", text: "מי הנפגעים?" },
      { type: "list", items: [
        "עובדים זרים - אחוז גבוה מהנפגעים",
        "עובדים צעירים וחסרי ניסיון",
        "עובדי קבלני משנה",
        "עובדים בפרויקטים קטנים עם פיקוח מופחת"
      ]},

      { type: "h2", text: "גורמים תורמים" },
      { type: "list", items: [
        "לחץ לוחות זמנים - 'לסיים מהר'",
        "חיסכון בעלויות בטיחות",
        "כוח עבודה לא מיומן",
        "פיקוח לקוי",
        "תרבות בטיחות חלשה",
        "ריבוי קבלני משנה",
        "בעיות שפה ותקשורת"
      ]},

      { type: "h2", text: "השוואה בינלאומית" },
      { type: "p", text: "ישראל נמצאת במקום גבוה בשיעור תאונות הבנייה בהשוואה למדינות מפותחות:" },
      { type: "list", items: [
        "גרמניה: שיעור נמוך משמעותית",
        "בריטניה: שיעור נמוך בכ-40%",
        "ארה\"ב: שיעור דומה לישראל",
        "טורקיה: שיעור גבוה מישראל"
      ]},

      { type: "h2", text: "מה עושה המדינה?" },
      { type: "list", items: [
        "הגברת אכיפה ופיקוח",
        "הכפלת קנסות על הפרות בטיחות",
        "חובת ממונה בטיחות מוסמך",
        "דרישות הכשרה מחמירות",
        "חובת תוכנית התארגנות"
      ]},

      { type: "h2", text: "מה אפשר לעשות?" },
      { type: "list", items: [
        "השקעה בהדרכות איכותיות",
        "פיקוח צמוד על עבודות מסוכנות",
        "ציוד בטיחות תקני ומתאים",
        "תרבות בטיחות מההנהלה למטה",
        "דיווח על כמעט-תאונות",
        "למידה מתאונות קודמות"
      ]},

      { type: "h2", text: "סיכום" },
      { type: "p", text: "המספרים מדברים בעד עצמם: ענף הבנייה הוא מסוכן ודורש התייחסות רצינית לבטיחות. ההשקעה בבטיחות משתלמת - גם כלכלית וגם מבחינה אנושית." },

      { type: "cta", text: "רוצים לשפר את הבטיחות באתר שלכם? צרו קשר לייעוץ." }
    ]
  },
  {
    id: 29,
    slug: "building-safety-health-system",
    title: "בניית מערך בטיחות ובריאות בארגון - מדריך מקיף",
    metaTitle: "בניית מערך בטיחות בארגון | מדריך למנהלים | אדם בטיחות",
    metaDescription: "איך בונים מערך בטיחות ובריאות תעסוקתית בארגון? מדריך שלב אחר שלב למנהלים שרוצים לעשות את זה נכון.",
    excerpt: "מערך בטיחות מקצועי הוא הבסיס לארגון בטוח. הנה איך לבנות אותו נכון מההתחלה.",
    category: "ניהול",
    date: "2025-01-03",
    readTime: "12 דקות קריאה",
    image: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&auto=format&fit=crop",
    imageAlt: "צוות ניהול בפגישה",
    keywords: ["מערך בטיחות", "בריאות תעסוקתית", "ניהול בטיחות", "מדיניות", "תכנון"],
    content: [
      { type: "h2", text: "מהו מערך בטיחות ובריאות?" },
      { type: "p", text: "מערך בטיחות ובריאות הוא המערכת הארגונית שאחראית על שמירת חייהם ובריאותם של העובדים. הוא כולל מדיניות, נהלים, אנשים, תהליכים וכלים שיחד יוצרים סביבת עבודה בטוחה." },
      { type: "p", text: "מערך בטיחות טוב הוא לא רק 'לסמן וי' מול הרגולציה - הוא מייצר ערך אמיתי לארגון." },

      { type: "h2", text: "למה צריך מערך בטיחות מובנה?" },
      { type: "list", items: [
        "עמידה בדרישות החוק - מניעת קנסות ותביעות",
        "הגנה על העובדים - מניעת תאונות ומחלות מקצוע",
        "חיסכון כלכלי - פחות ימי מחלה, פחות תאונות",
        "שימור עובדים - סביבת עבודה בטוחה משפרת מורל",
        "מוניטין - ארגון בטוח נתפס כמקצועי ואמין"
      ]},

      { type: "h2", text: "שלבי בניית מערך בטיחות" },

      { type: "h3", text: "שלב 1: מחויבות הנהלה" },
      { type: "p", text: "הכל מתחיל מלמעלה. ההנהלה הבכירה צריכה:" },
      { type: "list", items: [
        "להצהיר על מחויבות לבטיחות",
        "להקצות משאבים (תקציב, כוח אדם)",
        "לשמש דוגמה אישית",
        "לכלול בטיחות בסדר היום של ישיבות הנהלה"
      ]},

      { type: "h3", text: "שלב 2: מיפוי מצב קיים" },
      { type: "list", items: [
        "סקר סיכונים מקיף",
        "זיהוי פערים מול דרישות החוק",
        "ניתוח תאונות וכמעט-תאונות מהעבר",
        "מיפוי משאבים קיימים"
      ]},

      { type: "h3", text: "שלב 3: הגדרת מדיניות" },
      { type: "p", text: "כתבו מדיניות בטיחות ברורה שמגדירה:" },
      { type: "list", items: [
        "מטרות ויעדים",
        "אחריות של כל דרג בארגון",
        "עקרונות מנחים",
        "מנגנוני דיווח ובקרה"
      ]},

      { type: "h3", text: "שלב 4: מבנה ארגוני" },
      { type: "list", items: [
        "מינוי ממונה בטיחות",
        "הקמת ועדת בטיחות",
        "הגדרת תפקידים ואחריות",
        "קביעת קווי דיווח"
      ]},

      { type: "h3", text: "שלב 5: נהלים ותהליכים" },
      { type: "p", text: "פתחו נהלים ברורים ל:" },
      { type: "list", items: [
        "עבודות מסוכנות (גובה, חשמל, חומ\"ס)",
        "הדרכות בטיחות",
        "דיווח וחקירת תאונות",
        "תחזוקת ציוד ומכונות",
        "ניהול חירום"
      ]},

      { type: "h3", text: "שלב 6: הדרכות" },
      { type: "list", items: [
        "תוכנית הדרכות שנתית",
        "הדרכת כניסה לכל עובד חדש",
        "הדרכות ספציפיות לפי תפקיד",
        "הדרכות ריענון תקופתיות"
      ]},

      { type: "h3", text: "שלב 7: מערכת מדידה ובקרה" },
      { type: "list", items: [
        "הגדרת מדדי ביצוע (KPIs)",
        "ביקורות תקופתיות",
        "ניתוח נתונים ומגמות",
        "דוחות להנהלה"
      ]},

      { type: "h3", text: "שלב 8: שיפור מתמיד" },
      { type: "list", items: [
        "למידה מתאונות וכמעט-תאונות",
        "עדכון נהלים לפי צורך",
        "יישום שיטות חדשות",
        "השוואה לארגונים דומים"
      ]},

      { type: "h2", text: "מרכיבי מערך בטיחות מצליח" },

      { type: "h3", text: "אנשים" },
      { type: "list", items: [
        "ממונה בטיחות מוסמך ומנוסה",
        "ועדת בטיחות פעילה",
        "מנהלים מחויבים",
        "עובדים מודעים ומעורבים"
      ]},

      { type: "h3", text: "תהליכים" },
      { type: "list", items: [
        "נהלים ברורים ומעודכנים",
        "תהליכי דיווח יעילים",
        "מנגנוני בקרה",
        "תגובה לחירום"
      ]},

      { type: "h3", text: "כלים" },
      { type: "list", items: [
        "ציוד בטיחות תקני",
        "מערכת לניהול בטיחות (תוכנה)",
        "ציוד לבדיקות ומדידות",
        "חומרי הדרכה"
      ]},

      { type: "h2", text: "טעויות נפוצות" },
      { type: "list", items: [
        "בטיחות על הנייר בלבד - בלי יישום בשטח",
        "חוסר מחויבות הנהלה",
        "תקציב לא מספק",
        "ממונה בטיחות ללא סמכויות",
        "התמקדות בציות ולא בתרבות",
        "חוסר מעקב ומדידה"
      ]},

      { type: "h2", text: "סיכום" },
      { type: "p", text: "בניית מערך בטיחות היא תהליך מתמשך שדורש מחויבות, משאבים וסבלנות. אבל ההשקעה משתלמת - גם בהגנה על העובדים וגם בשורה התחתונה של העסק." },

      { type: "cta", text: "צריכים עזרה בבניית מערך בטיחות? צרו קשר לייעוץ." }
    ]
  },
  {
    id: 30,
    slug: "managing-organizational-safety-system",
    title: "ניהול מערך הבטיחות בארגון - שגרות ותהליכים",
    metaTitle: "ניהול מערך בטיחות בארגון | שגרות ניהול | אדם בטיחות",
    metaDescription: "איך מנהלים מערך בטיחות בארגון ביום-יום? מדריך לשגרות, תהליכים וכלים לניהול בטיחות אפקטיבי.",
    excerpt: "אחרי שבנינו מערך בטיחות - איך מנהלים אותו ביום-יום? הנה השגרות והתהליכים שעובדים.",
    category: "ניהול",
    date: "2025-01-02",
    readTime: "10 דקות קריאה",
    image: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&auto=format&fit=crop",
    imageAlt: "צוות בפגישת ניהול",
    keywords: ["ניהול בטיחות", "שגרות", "תהליכים", "מדדים", "בקרה"],
    content: [
      { type: "h2", text: "מניהול פרויקט לניהול שוטף" },
      { type: "p", text: "בניית מערך בטיחות היא רק ההתחלה. האתגר האמיתי הוא לנהל אותו לאורך זמן כך שיישאר אפקטיבי. הנה איך עושים את זה." },

      { type: "h2", text: "שגרות ניהול בטיחות" },

      { type: "h3", text: "שגרות יומיות" },
      { type: "list", items: [
        "סיור בטיחות בשטח (ממונה הבטיחות)",
        "בדיקות ציוד לפני עבודה",
        "תדריכי בטיחות בתחילת משמרת",
        "דיווחי מצב ותקלות"
      ]},

      { type: "h3", text: "שגרות שבועיות" },
      { type: "list", items: [
        "סיור בטיחות מנהלים",
        "ישיבת תיאום בטיחות",
        "סקירת דיווחי השבוע",
        "תיעדוף משימות תיקון"
      ]},

      { type: "h3", text: "שגרות חודשיות" },
      { type: "list", items: [
        "ישיבת ועדת בטיחות",
        "ניתוח מדדי בטיחות",
        "סקירת הדרכות",
        "בדיקות ציוד תקופתיות"
      ]},

      { type: "h3", text: "שגרות שנתיות" },
      { type: "list", items: [
        "סקר סיכונים מקיף",
        "עדכון תוכנית בטיחות שנתית",
        "סקירת מדיניות ונהלים",
        "תרגיל חירום מלא",
        "הערכת ספקים"
      ]},

      { type: "h2", text: "תהליכי ליבה בניהול בטיחות" },

      { type: "h3", text: "1. ניהול סיכונים" },
      { type: "list", items: [
        "זיהוי סיכונים חדשים באופן שוטף",
        "הערכת חומרה והסתברות",
        "קביעת אמצעי מניעה",
        "מעקב אחר יישום",
        "עדכון מפת הסיכונים"
      ]},

      { type: "h3", text: "2. ניהול הדרכות" },
      { type: "list", items: [
        "זיהוי צרכי הדרכה",
        "תכנון לוח הדרכות",
        "ביצוע ותיעוד",
        "מעקב תוקף הסמכות",
        "הערכת אפקטיביות"
      ]},

      { type: "h3", text: "3. ניהול תקריות" },
      { type: "list", items: [
        "קבלת דיווח ראשוני",
        "טיפול מיידי בנפגע ובזירה",
        "חקירה וזיהוי שורשים",
        "הפקת לקחים",
        "יישום ומעקב"
      ]},

      { type: "h3", text: "4. ניהול ציות" },
      { type: "list", items: [
        "מעקב אחר שינויי רגולציה",
        "הערכת פערי ציות",
        "תכנון התאמות",
        "יישום ותיעוד"
      ]},

      { type: "h2", text: "מדדי ביצוע (KPIs)" },
      { type: "p", text: "מה שלא נמדד - לא מנוהל. מדדים מומלצים:" },

      { type: "h3", text: "מדדי תוצאה (Lagging)" },
      { type: "list", items: [
        "מספר תאונות",
        "ימי אובדן עקב תאונות",
        "עלות תאונות",
        "שיעור תחלופה עקב בטיחות"
      ]},

      { type: "h3", text: "מדדי תהליך (Leading)" },
      { type: "list", items: [
        "אחוז השלמת הדרכות",
        "מספר דיווחי כמעט-תאונות",
        "ממצאי ביקורות ותיקונם",
        "סיורי בטיחות שבוצעו",
        "ציון מבדקי בטיחות"
      ]},

      { type: "h2", text: "כלים לניהול בטיחות" },
      { type: "list", items: [
        "תוכנת ניהול בטיחות (EHS software)",
        "אפליקציות לדיווח מהשטח",
        "דשבורד מדדים",
        "מערכת ניהול מסמכים",
        "מערכת לניהול הדרכות"
      ]},

      { type: "h2", text: "אתגרים נפוצים ופתרונות" },

      { type: "h3", text: "אתגר: שחיקה ושאננות" },
      { type: "p", text: "פתרון: לחדש ולגוון - אירועי בטיחות, תחרויות, תגמולים." },

      { type: "h3", text: "אתגר: התנגדות לשינוי" },
      { type: "p", text: "פתרון: לשתף עובדים, להסביר את ה'למה', לתת דוגמה אישית." },

      { type: "h3", text: "אתגר: חוסר משאבים" },
      { type: "p", text: "פתרון: להראות ROI, לתעדף לפי סיכון, לחפש פתרונות יצירתיים." },

      { type: "h3", text: "אתגר: ניתוק בין משרד לשטח" },
      { type: "p", text: "פתרון: נוכחות מנהלים בשטח, תקשורת דו-כיוונית, משוב מהעובדים." },

      { type: "h2", text: "סיכום" },
      { type: "p", text: "ניהול מערך בטיחות הוא עבודה מתמשכת שדורשת עקביות, מדידה ושיפור מתמיד. השגרות הנכונות הופכות בטיחות מ'פרויקט' ל'דרך חיים' ארגונית." },

      { type: "cta", text: "צריכים עזרה בניהול מערך הבטיחות? צרו קשר." }
    ]
  },
  {
    id: 31,
    slug: "safety-management-plan",
    title: "תוכנית לניהול הבטיחות - מדריך לכתיבה ויישום",
    metaTitle: "תוכנית ניהול בטיחות | מדריך כתיבה | אדם בטיחות",
    metaDescription: "איך כותבים תוכנית ניהול בטיחות אפקטיבית? מדריך שלב אחר שלב עם דוגמאות ותבניות.",
    excerpt: "תוכנית ניהול בטיחות היא מפת הדרכים שלכם לשנה הקרובה. הנה איך לכתוב אחת שבאמת עובדת.",
    category: "ניהול",
    date: "2025-01-01",
    readTime: "11 דקות קריאה",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&auto=format&fit=crop",
    imageAlt: "תכנון ועבודה על מסמכים",
    keywords: ["תוכנית בטיחות", "ניהול", "תכנון", "יעדים", "יישום"],
    content: [
      { type: "h2", text: "מהי תוכנית ניהול בטיחות?" },
      { type: "p", text: "תוכנית ניהול בטיחות היא מסמך אסטרטגי שמגדיר את יעדי הבטיחות של הארגון לתקופה מוגדרת (בדרך כלל שנה), את הפעילויות הנדרשות להשגתם, ואת המשאבים הדרושים." },
      { type: "p", text: "זהו לא 'עוד מסמך' - זו מפת הדרכים שלכם לשנה בטוחה יותר." },

      { type: "h2", text: "למה צריך תוכנית?" },
      { type: "list", items: [
        "מעבר מתגובה לפרואקטיביות",
        "תיעדוף והקצאת משאבים חכמה",
        "מדידת התקדמות והצלחה",
        "יצירת מחויבות ארגונית",
        "עמידה בדרישות רגולציה"
      ]},

      { type: "h2", text: "מבנה תוכנית ניהול בטיחות" },

      { type: "h3", text: "1. סקירת מצב קיים" },
      { type: "list", items: [
        "סיכום ביצועי השנה הקודמת",
        "ניתוח תאונות וכמעט-תאונות",
        "ממצאי סקר סיכונים אחרון",
        "פערי ציות מזוהים",
        "משוב מעובדים ומנהלים"
      ]},

      { type: "h3", text: "2. יעדים שנתיים" },
      { type: "p", text: "יעדים טובים הם SMART:" },
      { type: "list", items: [
        "ספציפיים - מוגדרים בבירור",
        "מדידים - עם מספרים",
        "אפשריים - ריאליים",
        "רלוונטיים - חשובים לארגון",
        "תחומים בזמן - עם תאריך יעד"
      ]},
      { type: "p", text: "דוגמאות ליעדים:" },
      { type: "list", items: [
        "הפחתת תאונות עבודה ב-20%",
        "100% השלמת הדרכות חובה",
        "סגירת 90% מממצאי הביקורות תוך 30 יום",
        "ביצוע 12 סיורי בטיחות ניהוליים"
      ]},

      { type: "h3", text: "3. תוכנית פעילות" },
      { type: "p", text: "לכל יעד - פעילויות ספציפיות:" },
      { type: "list", items: [
        "מה יבוצע",
        "מי אחראי",
        "עד מתי",
        "משאבים נדרשים",
        "אבני דרך"
      ]},

      { type: "h3", text: "4. לוח הדרכות" },
      { type: "list", items: [
        "הדרכות חובה על פי חוק",
        "הדרכות ספציפיות לתפקידים",
        "הדרכות ריענון",
        "תאריכים ומשתתפים",
        "ספקים/מדריכים"
      ]},

      { type: "h3", text: "5. לוח ביקורות ובדיקות" },
      { type: "list", items: [
        "ביקורות שטח תקופתיות",
        "בדיקות ציוד ומכונות",
        "סקרי סיכונים",
        "מבדקים פנימיים",
        "ביקורות חיצוניות"
      ]},

      { type: "h3", text: "6. תקציב" },
      { type: "list", items: [
        "ציוד בטיחות",
        "הדרכות",
        "ייעוץ חיצוני",
        "שיפורים תשתיתיים",
        "עלויות ביטוח"
      ]},

      { type: "h3", text: "7. מדדים ומעקב" },
      { type: "list", items: [
        "KPIs לכל יעד",
        "תדירות מדידה",
        "אחראים על מדידה",
        "פורום לסקירה"
      ]},

      { type: "h2", text: "תהליך הכנת התוכנית" },

      { type: "h3", text: "שלב 1: איסוף נתונים" },
      { type: "p", text: "אספו את כל המידע הרלוונטי: דוחות תאונות, ממצאי ביקורות, משוב עובדים, שינויי רגולציה." },

      { type: "h3", text: "שלב 2: ניתוח וזיהוי פערים" },
      { type: "p", text: "זהו את האזורים שדורשים שיפור ותעדפו לפי סיכון והשפעה." },

      { type: "h3", text: "שלב 3: גיבוש יעדים ופעילויות" },
      { type: "p", text: "הגדירו יעדים ריאליים ותכננו את הפעילויות להשגתם." },

      { type: "h3", text: "שלב 4: תקצוב" },
      { type: "p", text: "העריכו עלויות וקבלו אישור תקציבי." },

      { type: "h3", text: "שלב 5: אישור הנהלה" },
      { type: "p", text: "הציגו את התוכנית להנהלה וקבלו אישור ומחויבות." },

      { type: "h3", text: "שלב 6: הפצה ותקשור" },
      { type: "p", text: "שתפו את התוכנית עם כל בעלי העניין הרלוונטיים." },

      { type: "h2", text: "מעקב ועדכון" },
      { type: "list", items: [
        "סקירה רבעונית מול יעדים",
        "עדכון לפי שינויים בארגון",
        "גמישות לאירועים בלתי צפויים",
        "תיעוד סטיות והתאמות",
        "דוח סיכום שנתי"
      ]},

      { type: "h2", text: "טעויות נפוצות" },
      { type: "list", items: [
        "תוכנית שאפתנית מדי - לא ריאלית",
        "יעדים לא מדידים",
        "חוסר תקציב ליישום",
        "אין מעקב ובקרה",
        "התוכנית נשארת במגירה"
      ]},

      { type: "h2", text: "סיכום" },
      { type: "p", text: "תוכנית ניהול בטיחות טובה היא מסמך חי שמנחה את הפעילות היומיומית. היא הופכת רצון ל'לעבוד בטוח' לפעולות מדידות וברות השגה." },

      { type: "cta", text: "צריכים עזרה בהכנת תוכנית ניהול בטיחות? צרו קשר." }
    ]
  },
  // ===== NEW POSTS - BATCH 4 (32-36): מדידת אקלים + הדרכות =====
  {
    id: 32,
    slug: "measuring-safety-climate",
    title: "מדידת אקלים בטיחות בארגון - כלים ושיטות",
    metaTitle: "מדידת אקלים בטיחות | סקרים וכלים | אדם בטיחות",
    metaDescription: "איך מודדים את אקלים הבטיחות בארגון? מדריך לשיטות מדידה, סקרים וניתוח תוצאות לשיפור תרבות הבטיחות.",
    excerpt: "אקלים בטיחות הוא מדד חשוב להצלחת מערך הבטיחות. הנה איך למדוד אותו ולהשתמש בתוצאות.",
    category: "ניהול",
    date: "2024-12-31",
    readTime: "9 דקות קריאה",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&auto=format&fit=crop",
    imageAlt: "גרפים וניתוח נתונים",
    keywords: ["אקלים בטיחות", "תרבות בטיחות", "סקר", "מדידה", "שיפור"],
    content: [
      { type: "h2", text: "מהו אקלים בטיחות?" },
      { type: "p", text: "אקלים בטיחות מתאר את התפיסות והעמדות של העובדים כלפי בטיחות בארגון. זהו 'מד החום' של תרבות הבטיחות - הוא משקף איך העובדים תופסים את המחויבות של הארגון לבטיחות ואת הנורמות הקיימות." },
      { type: "quote", text: "מחקרים מראים קשר ישיר בין אקלים בטיחות חיובי לירידה בשיעור התאונות." },

      { type: "h2", text: "למה למדוד אקלים בטיחות?" },
      { type: "list", items: [
        "זיהוי בעיות לפני שהן הופכות לתאונות",
        "הבנת הפער בין מדיניות ליישום",
        "מדידת אפקטיביות של יוזמות בטיחות",
        "זיהוי אזורים או צוותים בסיכון",
        "יצירת בסיס להשוואה לאורך זמן",
        "שיתוף עובדים בתהליך השיפור"
      ]},

      { type: "h2", text: "ממדי אקלים בטיחות" },
      { type: "p", text: "אקלים בטיחות מורכב ממספר ממדים:" },

      { type: "h3", text: "1. מחויבות הנהלה" },
      { type: "p", text: "האם העובדים מרגישים שההנהלה באמת מחויבת לבטיחות?" },

      { type: "h3", text: "2. תקשורת" },
      { type: "p", text: "האם יש תקשורת פתוחה בנושאי בטיחות?" },

      { type: "h3", text: "3. מעורבות עובדים" },
      { type: "p", text: "האם העובדים מרגישים שותפים לתהליך?" },

      { type: "h3", text: "4. אכיפה והוגנות" },
      { type: "p", text: "האם כללי הבטיחות נאכפים בצורה הוגנת?" },

      { type: "h3", text: "5. הדרכה ומשאבים" },
      { type: "p", text: "האם העובדים מרגישים שיש להם את הכלים והידע הנדרשים?" },

      { type: "h3", text: "6. לחץ עבודה" },
      { type: "p", text: "האם יש לחץ לפגוע בבטיחות לטובת פרודוקטיביות?" },

      { type: "h2", text: "שיטות מדידה" },

      { type: "h3", text: "1. סקר אקלים בטיחות" },
      { type: "p", text: "שאלון אנונימי לעובדים - השיטה הנפוצה ביותר:" },
      { type: "list", items: [
        "שאלות סגורות בסולם ליקרט (1-5)",
        "שאלות פתוחות להערות",
        "מילוי אנונימי לקבלת תשובות כנות",
        "ביצוע תקופתי (שנתי או חצי-שנתי)"
      ]},

      { type: "h3", text: "2. קבוצות מיקוד" },
      { type: "p", text: "שיחות קבוצתיות מונחות לקבלת תובנות מעמיקות." },

      { type: "h3", text: "3. ראיונות אישיים" },
      { type: "p", text: "שיחות אחד-על-אחד עם עובדים ומנהלים." },

      { type: "h3", text: "4. תצפיות" },
      { type: "p", text: "צפייה בהתנהגות עובדים בשטח." },

      { type: "h3", text: "5. ניתוח נתונים קיימים" },
      { type: "p", text: "דיווחי כמעט-תאונות, תלונות, תחלופה." },

      { type: "h2", text: "דוגמאות לשאלות סקר" },
      { type: "list", items: [
        "ההנהלה מתייחסת ברצינות לנושא הבטיחות (1-5)",
        "אני מרגיש בנוח לדווח על בעיות בטיחות (1-5)",
        "יש לי את הציוד הנדרש לעבודה בטוחה (1-5)",
        "ההדרכות שקיבלתי עוזרות לי לעבוד בבטיחות (1-5)",
        "לעתים אני מרגיש לחץ לפגוע בבטיחות כדי לעמוד ביעדים (1-5)",
        "עמיתיי לעבודה מקפידים על כללי בטיחות (1-5)"
      ]},

      { type: "h2", text: "ניתוח תוצאות" },
      { type: "list", items: [
        "חישוב ממוצעים לכל ממד",
        "זיהוי ממדים חלשים",
        "השוואה בין צוותים/מחלקות",
        "השוואה לסקרים קודמים (מגמות)",
        "ניתוח שאלות פתוחות לנושאים חוזרים"
      ]},

      { type: "h2", text: "מהתוצאות לפעולה" },
      { type: "p", text: "הסקר הוא רק ההתחלה. השלב החשוב הוא לפעול:" },
      { type: "list", items: [
        "שתפו את התוצאות עם העובדים",
        "הגדירו אזורים לשיפור",
        "בנו תוכנית פעולה עם יעדים ולו\"ז",
        "הקצו משאבים ואחראים",
        "עקבו אחרי התקדמות",
        "מדדו שוב בסקר הבא"
      ]},

      { type: "h2", text: "טעויות נפוצות" },
      { type: "list", items: [
        "סקר ללא מעקב - פוגע באמון",
        "סקר לא אנונימי - תשובות לא כנות",
        "שאלות מוטות - תוצאות לא אמינות",
        "התעלמות מתוצאות שליליות",
        "סקר חד-פעמי - צריך מגמות"
      ]},

      { type: "h2", text: "סיכום" },
      { type: "p", text: "מדידת אקלים בטיחות היא כלי רב-עוצמה לשיפור הבטיחות בארגון. היא מאפשרת לכם לשמוע את קול העובדים ולזהות בעיות לפני שהן הופכות לתאונות." },

      { type: "cta", text: "רוצים לבצע סקר אקלים בטיחות בארגון שלכם? צרו קשר." }
    ]
  },
  {
    id: 33,
    slug: "safety-committee-organization",
    title: "ועדת בטיחות בארגון - הקמה, תפקידים ויעילות",
    metaTitle: "ועדת בטיחות בארגון | מדריך להקמה | אדם בטיחות",
    metaDescription: "כל מה שצריך לדעת על ועדת בטיחות: מתי חובה להקים, מי חברי הוועדה, מה התפקידים ואיך להפוך אותה לאפקטיבית.",
    excerpt: "ועדת בטיחות היא פורום מרכזי לניהול בטיחות בארגון. הנה איך להקים ולנהל ועדה אפקטיבית.",
    category: "ניהול",
    date: "2024-12-30",
    readTime: "8 דקות קריאה",
    image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&auto=format&fit=crop",
    imageAlt: "ישיבת צוות",
    keywords: ["ועדת בטיחות", "ניהול בטיחות", "ארגון", "חוק", "יעילות"],
    content: [
      { type: "h2", text: "מהי ועדת בטיחות?" },
      { type: "p", text: "ועדת בטיחות היא פורום משותף להנהלה ולעובדים לדיון ולקבלת החלטות בנושאי בטיחות ובריאות תעסוקתית. היא מהווה גשר בין ההנהלה לשטח ומאפשרת שיתוף עובדים בניהול הבטיחות." },

      { type: "h2", text: "מתי חובה להקים ועדת בטיחות?" },
      { type: "p", text: "על פי תקנות ארגון הפיקוח על העבודה:" },
      { type: "list", items: [
        "מפעל עם 25 עובדים ומעלה - חובה",
        "מפעל עם פחות מ-25 עובדים - לפי דרישת מפקח עבודה",
        "מומלץ לכל ארגון ללא קשר לגודל"
      ]},

      { type: "h2", text: "הרכב הוועדה" },
      { type: "h3", text: "נציגי הנהלה" },
      { type: "list", items: [
        "יו\"ר הוועדה - לרוב מנהל בכיר",
        "ממונה בטיחות",
        "מנהל משאבי אנוש",
        "מנהלי מחלקות רלוונטיים"
      ]},

      { type: "h3", text: "נציגי עובדים" },
      { type: "list", items: [
        "נציגי ועד עובדים (אם קיים)",
        "נציגים נבחרים מהמחלקות השונות",
        "יש לוודא ייצוג הולם של כל אזורי הארגון"
      ]},
      { type: "p", text: "מספר נציגי העובדים צריך להיות שווה לפחות למספר נציגי ההנהלה." },

      { type: "h2", text: "תפקידי הוועדה" },
      { type: "list", items: [
        "דיון בנושאי בטיחות ובריאות",
        "סקירת תאונות וכמעט-תאונות",
        "בחינת ממצאי ביקורות וסקרי סיכונים",
        "המלצות לשיפור הבטיחות",
        "מעקב אחר יישום החלטות קודמות",
        "דיון בתלונות ופניות עובדים",
        "אישור נהלים ותוכניות בטיחות"
      ]},

      { type: "h2", text: "תדירות ישיבות" },
      { type: "list", items: [
        "לפחות אחת לרבעון (4 פעמים בשנה)",
        "מומלץ: חודשי בארגונים גדולים",
        "ישיבה מיוחדת לאחר תאונה חמורה",
        "ישיבות חריגות לפי צורך"
      ]},

      { type: "h2", text: "סדר יום טיפוסי" },
      { type: "list", items: [
        "סקירת מעקב החלטות קודמות",
        "דיווח ממונה בטיחות על מדדים ואירועים",
        "דיון בתאונות/כמעט-תאונות מהתקופה",
        "סקירת ממצאי ביקורות",
        "נושאים שהועלו על ידי חברי הוועדה",
        "קבלת החלטות והמלצות",
        "קביעת מועד לישיבה הבאה"
      ]},

      { type: "h2", text: "איך להפוך את הוועדה לאפקטיבית?" },

      { type: "h3", text: "1. הכנה לישיבה" },
      { type: "list", items: [
        "שליחת סדר יום מראש",
        "הכנת חומרים רלוונטיים",
        "איסוף נושאים מחברי הוועדה"
      ]},

      { type: "h3", text: "2. ניהול הישיבה" },
      { type: "list", items: [
        "התחלה וסיום בזמן",
        "עמידה בסדר היום",
        "עידוד השתתפות כל החברים",
        "קבלת החלטות ברורות עם אחראים ולו\"ז"
      ]},

      { type: "h3", text: "3. מעקב" },
      { type: "list", items: [
        "הפצת פרוטוקול תוך שבוע",
        "מעקב אחר ביצוע החלטות",
        "דיווח על התקדמות בישיבה הבאה"
      ]},

      { type: "h3", text: "4. מעורבות" },
      { type: "list", items: [
        "עידוד חברי הוועדה להעלות נושאים",
        "סיורים משותפים בשטח",
        "הזמנת אורחים רלוונטיים לפי נושא"
      ]},

      { type: "h2", text: "מלכודות נפוצות" },
      { type: "list", items: [
        "ועדה על הנייר בלבד - ללא פעילות אמיתית",
        "דומיננטיות הנהלה - עובדים לא מרגישים בנוח להשתתף",
        "דיון ללא החלטות",
        "החלטות ללא מעקב",
        "התמקדות בניירת במקום בתוכן"
      ]},

      { type: "h2", text: "תיעוד" },
      { type: "p", text: "יש לתעד כל ישיבה בפרוטוקול הכולל:" },
      { type: "list", items: [
        "תאריך, משתתפים, נעדרים",
        "נושאים שנדונו",
        "החלטות שהתקבלו",
        "אחראים ולוחות זמנים",
        "נושאים לישיבה הבאה"
      ]},
      { type: "p", text: "יש לשמור פרוטוקולים לפחות 7 שנים." },

      { type: "h2", text: "סיכום" },
      { type: "p", text: "ועדת בטיחות אפקטיבית היא נכס לארגון. היא מאפשרת שיתוף פעולה בין הנהלה לעובדים, זיהוי בעיות ופתרונן. ההשקעה בניהול נכון של הוועדה משתלמת בשיפור אמיתי של הבטיחות." },

      { type: "cta", text: "צריכים עזרה בהקמה או הפעלת ועדת בטיחות? צרו קשר." }
    ]
  },
  {
    id: 34,
    slug: "forklift-refresher-training",
    title: "הדרכת ריענון למלגזנים - חובה חוקית ומציל חיים",
    metaTitle: "הדרכת ריענון מלגזנים | חובה חוקית | אדם בטיחות",
    metaDescription: "הדרכת ריענון למלגזנים היא חובה חוקית. מה כוללת ההדרכה? כמה פעמים צריך לבצע? מדריך מקיף למעסיקים.",
    excerpt: "מפעילי מלגזות חייבים לעבור הדרכת ריענון תקופתית. הנה כל מה שצריך לדעת על החובה הזו.",
    category: "הדרכות",
    date: "2024-12-29",
    readTime: "7 דקות קריאה",
    image: "https://images.unsplash.com/photo-1553413077-190dd305871c?w=800&auto=format&fit=crop",
    imageAlt: "מלגזה במחסן",
    keywords: ["מלגזה", "הדרכת ריענון", "מלגזנים", "בטיחות", "חובה"],
    content: [
      { type: "h2", text: "למה חשובה הדרכת ריענון?" },
      { type: "p", text: "מלגזות הן מכלי העבודה המסוכנים ביותר במפעלים ומחסנים. תאונות מלגזות גורמות מדי שנה לפציעות קשות ואף למוות. הדרכת ריענון מוודאת שהמפעילים זוכרים את כללי הבטיחות ומעדכנים את הידע שלהם." },
      { type: "quote", text: "כ-20% מתאונות העבודה במחסנים קשורות למלגזות." },

      { type: "h2", text: "הדרישה החוקית" },
      { type: "p", text: "על פי תקנות הבטיחות בעבודה (עגורנאים, מפעילי מכונות הרמה אחרות ואתתים):" },
      { type: "list", items: [
        "כל מפעיל מלגזה חייב רישיון הפעלה",
        "הדרכת ריענון חובה לפחות אחת ל-3 שנים",
        "מעסיק חייב לוודא שהעובדים עברו הדרכה",
        "תיעוד ההדרכות חייב להישמר"
      ]},

      { type: "h2", text: "מתי נדרשת הדרכת ריענון?" },
      { type: "list", items: [
        "כל 3 שנים - חובה לכל מלגזן",
        "לאחר תאונה או כמעט-תאונה",
        "לאחר היעדרות ממושכת (מעל 6 חודשים)",
        "בעת שינוי סוג המלגזה",
        "כאשר נצפות הרגלי נהיגה לא בטוחים"
      ]},

      { type: "h2", text: "תוכן הדרכת הריענון" },

      { type: "h3", text: "חלק תיאורטי" },
      { type: "list", items: [
        "סקירת תקנות ונהלים מעודכנים",
        "ניתוח תאונות - לקחים",
        "עקרונות יציבות ומרכז כובד",
        "בדיקות לפני נסיעה",
        "נהלי טעינה ופריקה",
        "עבודה בטוחה ליד הולכי רגל"
      ]},

      { type: "h3", text: "חלק מעשי" },
      { type: "list", items: [
        "תרגול נהיגה בסיסית",
        "תמרון במרחבים צרים",
        "טעינה ופריקה נכונה",
        "עבודה על מדרגות ומשטחים",
        "התמודדות עם מצבים חריגים"
      ]},

      { type: "h2", text: "משך ההדרכה" },
      { type: "p", text: "הדרכת ריענון אורכת בדרך כלל 4-8 שעות, כולל:" },
      { type: "list", items: [
        "2-3 שעות תיאוריה",
        "2-4 שעות תרגול מעשי",
        "מבחן עיוני ומעשי"
      ]},

      { type: "h2", text: "מי יכול להעביר את ההדרכה?" },
      { type: "list", items: [
        "מדריך מלגזות מוסמך",
        "גוף הכשרה מאושר",
        "יצרן המלגזות (לדגמים ספציפיים)",
        "ממונה בטיחות עם הכשרה מתאימה"
      ]},

      { type: "h2", text: "נושאים חשובים להדגיש" },

      { type: "h3", text: "סיבות נפוצות לתאונות" },
      { type: "list", items: [
        "מהירות מופרזת",
        "עומס יתר",
        "נהיגה עם מטען מורם",
        "חוסר ראות",
        "התעלמות מהולכי רגל"
      ]},

      { type: "h3", text: "כללי בטיחות קריטיים" },
      { type: "list", items: [
        "בדיקה יומית לפני הפעלה",
        "חגורת בטיחות תמיד",
        "מהירות מותאמת לתנאים",
        "מטען נמוך בנסיעה",
        "צפירה בפינות ובצמתים",
        "איסור הסעת נוסעים (למעט מלגזות מאושרות)"
      ]},

      { type: "h2", text: "תיעוד נדרש" },
      { type: "list", items: [
        "רשימת משתתפים עם חתימות",
        "תוכן ההדרכה",
        "תאריך ומשך ההדרכה",
        "שם המדריך",
        "תוצאות מבחנים",
        "תוקף ההדרכה הבאה"
      ]},

      { type: "h2", text: "עלויות והשקעה" },
      { type: "p", text: "עלות הדרכת ריענון נעה בין 300-600 ₪ למשתתף. זוהי השקעה קטנה לעומת:" },
      { type: "list", items: [
        "עלות תאונה (עשרות אלפי שקלים ויותר)",
        "קנסות על אי-עמידה בחוק",
        "ימי מחלה וחוסר פרודוקטיביות"
      ]},

      { type: "h2", text: "סיכום" },
      { type: "p", text: "הדרכת ריענון למלגזנים היא לא רק חובה חוקית - היא הכרחית לבטיחות. מלגזן שמרענן את הידע שלו הוא מלגזן בטוח יותר." },

      { type: "cta", text: "צריכים לארגן הדרכת ריענון למלגזנים? צרו קשר." }
    ]
  },
  {
    id: 35,
    slug: "working-at-height-course",
    title: "קורס עבודה בגובה - כל מה שצריך לדעת",
    metaTitle: "קורס עבודה בגובה | הדרכה מוסמכת | אדם בטיחות",
    metaDescription: "קורס עבודה בגובה הוא חובה לכל עובד בגובה. מה לומדים? כמה זמן? מי חייב? מדריך מקיף לעובדים ומעסיקים.",
    excerpt: "עבודה בגובה היא מהסיבות המובילות לתאונות קטלניות. קורס עבודה בגובה מכין עובדים לעבודה בטוחה.",
    category: "הדרכות",
    date: "2024-12-28",
    readTime: "9 דקות קריאה",
    image: "https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=800&auto=format&fit=crop",
    imageAlt: "עובד בגובה עם ציוד בטיחות",
    featured: true,
    keywords: ["עבודה בגובה", "קורס", "הדרכה", "רתמות", "בטיחות"],
    content: [
      { type: "h2", text: "למה עבודה בגובה כל כך מסוכנת?" },
      { type: "p", text: "נפילה מגובה היא הסיבה מספר 1 לתאונות עבודה קטלניות בישראל. יותר מ-50% מההרוגים בענף הבנייה ואחוז משמעותי בתעשייה - נפגעו מנפילה מגובה." },
      { type: "quote", text: "על פי התקנות, 'עבודה בגובה' מוגדרת כעבודה בגובה של 2 מטרים ומעלה מעל פני השטח." },

      { type: "h2", text: "מי חייב לעבור קורס?" },
      { type: "list", items: [
        "כל עובד שעובד בגובה 2 מטר ומעלה",
        "עובדי בנייה, תחזוקה, חשמל",
        "מתקיני מזגנים ולוחות שמש",
        "עובדי ניקיון חלונות",
        "טכנאי תקשורת ואנטנות",
        "כל עובד שעולה על פיגום, סולם או גג"
      ]},

      { type: "h2", text: "סוגי קורסים" },

      { type: "h3", text: "קורס בסיסי (8 שעות)" },
      { type: "p", text: "לעובדים שעובדים בגובה עד 6 מטר עם ציוד פשוט." },

      { type: "h3", text: "קורס מתקדם (16 שעות)" },
      { type: "p", text: "לעובדים בגבהים גדולים או עם ציוד מורכב." },

      { type: "h3", text: "קורס מנהלי עבודה" },
      { type: "p", text: "למנהלים האחראים על עבודות בגובה." },

      { type: "h3", text: "הדרכת ריענון (4 שעות)" },
      { type: "p", text: "ריענון תקופתי לבעלי תעודה." },

      { type: "h2", text: "תוכן הקורס" },

      { type: "h3", text: "חלק תיאורטי" },
      { type: "list", items: [
        "חקיקה ותקנות עבודה בגובה",
        "זיהוי סיכונים בעבודה בגובה",
        "עקרונות מניעת נפילות",
        "סוגי ציוד הגנה (רתמות, קווי חיים, נקודות עיגון)",
        "בדיקת ציוד לפני שימוש",
        "תכנון עבודה בטוחה בגובה"
      ]},

      { type: "h3", text: "חלק מעשי" },
      { type: "list", items: [
        "לבישה נכונה של רתמה",
        "בדיקת ציוד",
        "חיבור לנקודות עיגון",
        "שימוש בקווי חיים",
        "עלייה וירידה בטוחה",
        "תרגול חילוץ"
      ]},

      { type: "h2", text: "ציוד הגנה בעבודה בגובה" },

      { type: "h3", text: "רתמת גוף מלאה" },
      { type: "p", text: "חובה בכל עבודה בגובה. מחלקת את כוח הנפילה על כל הגוף." },

      { type: "h3", text: "קו חיים (Lanyard)" },
      { type: "p", text: "מחבר בין הרתמה לנקודת העיגון. יש עם בולם נפילה ויש קבועים." },

      { type: "h3", text: "נקודות עיגון" },
      { type: "p", text: "מוקדות מאושרים לחיבור קו החיים. חייבות לשאת 15 קילו-ניוטון לפחות." },

      { type: "h3", text: "קסדה" },
      { type: "p", text: "קסדה עם רצועת סנטר לעבודה בגובה." },

      { type: "h2", text: "טעויות נפוצות" },
      { type: "list", items: [
        "רתמה לא מכוונת נכון לגוף",
        "חיבור לנקודת עיגון לא מאושרת",
        "קו חיים ארוך מדי",
        "אי-בדיקת ציוד לפני שימוש",
        "עבודה ללא גיבוי",
        "זלזול בסיכון - 'זה רק 2 מטר'"
      ]},

      { type: "h2", text: "תוקף התעודה" },
      { type: "list", items: [
        "תעודה בסיסית: 3 שנים",
        "לאחר מכן: הדרכת ריענון",
        "יש לשמור את התעודה ולהציג לפי דרישה"
      ]},

      { type: "h2", text: "אחריות המעסיק" },
      { type: "list", items: [
        "לוודא שכל עובד עבר הדרכה מתאימה",
        "לספק ציוד הגנה תקני",
        "לפקח על שימוש נכון בציוד",
        "לתעד הדרכות ותוקף תעודות",
        "לבצע הדרכת ריענון במועד"
      ]},

      { type: "h2", text: "סיכום" },
      { type: "p", text: "קורס עבודה בגובה הוא השקעה בחיים. עובד מודרך יודע לזהות סיכונים, להשתמש נכון בציוד ולעבוד בבטחה. אל תחכו לתאונה." },

      { type: "cta", text: "צריכים לארגן קורס עבודה בגובה לעובדים? צרו קשר." }
    ]
  },
  {
    id: 36,
    slug: "new-employee-safety-training",
    title: "הכשרת בטיחות לעובד חדש - מדריך מקיף",
    metaTitle: "הדרכת בטיחות לעובד חדש | מדריך למעסיקים | אדם בטיחות",
    metaDescription: "הדרכת בטיחות לעובד חדש היא חובה חוקית. מה צריכה לכלול? מתי לבצע? מדריך מקיף למעסיקים ולממוני בטיחות.",
    excerpt: "עובד חדש הוא עובד בסיכון. הדרכת כניסה נכונה יכולה למנוע תאונות ולהציל חיים.",
    category: "הדרכות",
    date: "2024-12-27",
    readTime: "8 דקות קריאה",
    image: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=800&auto=format&fit=crop",
    imageAlt: "הדרכת עובד חדש",
    keywords: ["עובד חדש", "הדרכת כניסה", "הכשרה", "בטיחות", "מעסיק"],
    content: [
      { type: "h2", text: "למה עובדים חדשים בסיכון גבוה?" },
      { type: "p", text: "סטטיסטיקות מראות שעובדים חדשים נפגעים בשיעור גבוה יותר מעובדים ותיקים. הם לא מכירים את הסביבה, הסיכונים והנהלים. הדרכת כניסה טובה יכולה להפחית משמעותית את הסיכון." },
      { type: "quote", text: "כ-40% מתאונות העבודה קורות לעובדים בשנה הראשונה לעבודתם." },

      { type: "h2", text: "הדרישה החוקית" },
      { type: "p", text: "על פי פקודת הבטיחות בעבודה וחוק ארגון הפיקוח:" },
      { type: "list", items: [
        "כל עובד חדש חייב לעבור הדרכת בטיחות",
        "ההדרכה צריכה להיות לפני תחילת העבודה",
        "ההדרכה צריכה להיות בשפה שהעובד מבין",
        "יש לתעד את ההדרכה",
        "יש להתאים את ההדרכה לתפקיד הספציפי"
      ]},

      { type: "h2", text: "מתי לבצע את ההדרכה?" },
      { type: "list", items: [
        "ביום הראשון לעבודה - הדרכה כללית",
        "לפני כניסה לאזורי עבודה - הדרכה ספציפית",
        "לפני הפעלת מכונות - הדרכה על המכונה",
        "בשבועות הראשונים - הדרכות משלימות"
      ]},

      { type: "h2", text: "תוכן הדרכת הכניסה" },

      { type: "h3", text: "1. מידע כללי על הארגון" },
      { type: "list", items: [
        "מדיניות הבטיחות של הארגון",
        "מבנה ארגוני בטיחותי",
        "מי הממונה על הבטיחות",
        "איך לדווח על בעיות"
      ]},

      { type: "h3", text: "2. סיכונים כלליים" },
      { type: "list", items: [
        "סיכוני אש ונהלי פינוי",
        "עזרה ראשונה - מיקום ציוד ואנשי קשר",
        "סיכונים סביבתיים (רעש, חומרים)",
        "תנועה ברחבי המתקן"
      ]},

      { type: "h3", text: "3. ציוד מגן אישי" },
      { type: "list", items: [
        "אילו פריטי ציוד נדרשים",
        "איך להשתמש נכון",
        "איפה לקבל ציוד",
        "מתי להחליף"
      ]},

      { type: "h3", text: "4. סיכונים ספציפיים לתפקיד" },
      { type: "list", items: [
        "מכונות וציוד ספציפיים",
        "חומרים מסוכנים רלוונטיים",
        "נהלי עבודה בטוחים",
        "אזורים מותרים ואסורים"
      ]},

      { type: "h3", text: "5. נהלי חירום" },
      { type: "list", items: [
        "דרכי פינוי ונקודות התכנסות",
        "אותות אזעקה",
        "מיקום ציוד כיבוי אש",
        "מספרי חירום"
      ]},

      { type: "h2", text: "שיטות הדרכה" },
      { type: "list", items: [
        "הרצאה פרונטלית - לנושאים כלליים",
        "סיור במתקן - להכרת הסביבה",
        "הדגמה מעשית - לציוד ומכונות",
        "צפייה בסרטונים - להמחשה",
        "ליווי עובד מנוסה - בימים הראשונים",
        "מבחן ידע - לוידוא הבנה"
      ]},

      { type: "h2", text: "משך ההדרכה" },
      { type: "p", text: "משך ההדרכה משתנה לפי סוג העבודה:" },
      { type: "list", items: [
        "עבודה משרדית: 1-2 שעות",
        "עבודה תעשייתית: 4-8 שעות",
        "עבודה עם סיכונים גבוהים: יום מלא ויותר",
        "עבודה בבנייה: הדרכה מורחבת + ליווי"
      ]},

      { type: "h2", text: "תיעוד נדרש" },
      { type: "list", items: [
        "טופס הדרכה חתום על ידי העובד",
        "תוכן ההדרכה שהועברה",
        "תאריך ושעה",
        "שם המדריך",
        "תוצאות מבחן (אם בוצע)"
      ]},

      { type: "h2", text: "טיפים להדרכה אפקטיבית" },
      { type: "list", items: [
        "אל תציפו במידע - התמקדו בעיקר",
        "השתמשו בדוגמאות מהשטח",
        "עודדו שאלות",
        "וודאו הבנה - אל רק תרצו",
        "עקבו אחרי העובד בימים הראשונים",
        "התאימו לשפה ולרמת ההבנה"
      ]},

      { type: "h2", text: "מעקב לאחר ההדרכה" },
      { type: "list", items: [
        "בדיקה בשבוע הראשון - האם יש שאלות?",
        "תצפית על עבודה - האם מיישם?",
        "שיחה עם מנהל ישיר - משוב",
        "הדרכות משלימות לפי צורך"
      ]},

      { type: "h2", text: "סיכום" },
      { type: "p", text: "הדרכת כניסה טובה היא ההשקעה הראשונה והחשובה ביותר בבטיחות העובד. היא מגנה על העובד, על הארגון ועל כולם סביב." },

      { type: "cta", text: "צריכים עזרה בבניית תוכנית הדרכה לעובדים חדשים? צרו קשר." }
    ]
  },
  // ===== NEW POSTS - BATCH 5 (37-41): הדרכות + גיהות תעסוקתית =====
  {
    id: 37,
    slug: "fire-safety-course",
    title: "קורס בטיחות באש - הדרכות וכיבוי שריפות",
    metaTitle: "קורס בטיחות אש | הדרכת כיבוי שריפות | אדם בטיחות",
    metaDescription: "קורס בטיחות באש מכין עובדים להתמודדות עם שריפות. מה לומדים? מי חייב? כמה זמן? מדריך מקיף.",
    excerpt: "שריפה במקום העבודה יכולה לגרום לאסון. קורס בטיחות אש מכין את העובדים להתמודד ולמנוע.",
    category: "הדרכות",
    date: "2024-12-26",
    readTime: "8 דקות קריאה",
    image: "https://images.unsplash.com/photo-1486551937199-baf066858de7?w=800&auto=format&fit=crop",
    imageAlt: "ציוד כיבוי אש",
    keywords: ["בטיחות אש", "כיבוי שריפות", "קורס", "הדרכה", "מטפה"],
    content: [
      { type: "h2", text: "למה הדרכת בטיחות אש חשובה?" },
      { type: "p", text: "שריפה במקום העבודה יכולה להתפתח תוך דקות ולגרום לאובדן חיים, פציעות קשות ונזק רכושי עצום. הדרכה נכונה מאפשרת לעובדים לזהות סיכונים, למנוע שריפות ולהגיב נכון כשהן פורצות." },
      { type: "quote", text: "רוב השריפות ניתנות למניעה, ורוב השריפות שכן פורצות - ניתנות לכיבוי בשלב מוקדם עם תגובה נכונה." },

      { type: "h2", text: "הדרישה החוקית" },
      { type: "list", items: [
        "כל מקום עבודה חייב בתוכנית בטיחות אש",
        "הדרכת בטיחות אש לכל עובד - חובה",
        "הדרכת כיבוי בסיסית - מומלצת לכולם",
        "צוות כיבוי ראשוני - נדרש במפעלים",
        "תרגיל פינוי שנתי - חובה"
      ]},

      { type: "h2", text: "סוגי הדרכות בטיחות אש" },

      { type: "h3", text: "הדרכה בסיסית (2-4 שעות)" },
      { type: "p", text: "לכל עובד בארגון:" },
      { type: "list", items: [
        "סיכוני אש במקום העבודה",
        "מניעת שריפות",
        "זיהוי עשן ואש",
        "נהלי פינוי",
        "שימוש בסיסי במטפה"
      ]},

      { type: "h3", text: "הדרכת צוות כיבוי ראשוני (8 שעות)" },
      { type: "p", text: "לעובדים שמהווים צוות כיבוי:" },
      { type: "list", items: [
        "סוגי שריפות וחומרי כיבוי",
        "הפעלת מטפים מסוגים שונים",
        "שימוש בזרנוקי כיבוי",
        "טקטיקות כיבוי בסיסיות",
        "תיאום עם כוחות כיבוי"
      ]},

      { type: "h3", text: "הדרכת אחראי בטיחות אש" },
      { type: "p", text: "לממונים על בטיחות אש בארגון:" },
      { type: "list", items: [
        "ניהול מערך בטיחות אש",
        "עריכת סקר סיכוני אש",
        "תחזוקת ציוד כיבוי",
        "הכנת תוכנית פינוי",
        "ניהול תרגילים"
      ]},

      { type: "h2", text: "תוכן הדרכה בסיסית" },

      { type: "h3", text: "משולש האש" },
      { type: "p", text: "הבנת שלושת הגורמים לשריפה: חום, דלק וחמצן. הסרת אחד מהם מכבה את האש." },

      { type: "h3", text: "סיווג שריפות" },
      { type: "list", items: [
        "סוג A - חומרים מוצקים (עץ, נייר, בד)",
        "סוג B - נוזלים דליקים (דלק, שמן, צבע)",
        "סוג C - גזים דליקים",
        "סוג D - מתכות דליקות",
        "סוג E/F - שריפות חשמל ושמן בישול"
      ]},

      { type: "h3", text: "סוגי מטפים" },
      { type: "list", items: [
        "אבקה - מתאים לרוב השריפות",
        "CO2 - לשריפות חשמל ונוזלים",
        "קצף - לנוזלים דליקים",
        "מים - לחומרים מוצקים בלבד"
      ]},

      { type: "h3", text: "שימוש במטפה - שיטת PASS" },
      { type: "list", items: [
        "Pull - משוך את הנעילה",
        "Aim - כוון לבסיס הלהבות",
        "Squeeze - לחץ על הידית",
        "Sweep - סרוק מצד לצד"
      ]},

      { type: "h2", text: "נהלי פינוי" },
      { type: "list", items: [
        "הכרת דרכי הפינוי ויציאות חירום",
        "זיהוי אותות האזעקה",
        "פינוי מסודר ומהיר",
        "נקודות התכנסות",
        "ספירת עובדים",
        "איסור שימוש במעליות"
      ]},

      { type: "h2", text: "מניעת שריפות" },
      { type: "list", items: [
        "סדר וניקיון - הרחקת חומרים דליקים",
        "איסור עישון באזורים אסורים",
        "זהירות עם ציוד חשמלי",
        "אחסון נכון של חומרים דליקים",
        "תחזוקת ציוד כיבוי"
      ]},

      { type: "h2", text: "תרגיל פינוי" },
      { type: "p", text: "כל ארגון חייב לקיים תרגיל פינוי לפחות פעם בשנה:" },
      { type: "list", items: [
        "תכנון מראש עם גורמי בטיחות",
        "הפעלת מערכת האזעקה",
        "פינוי כל העובדים",
        "ספירה בנקודת התכנסות",
        "הפקת לקחים ושיפור"
      ]},

      { type: "h2", text: "תיעוד נדרש" },
      { type: "list", items: [
        "רשימת משתתפים בהדרכות",
        "תאריכי הדרכה ותוכן",
        "פרוטוקולים של תרגילי פינוי",
        "בדיקות תקופתיות של ציוד"
      ]},

      { type: "h2", text: "סיכום" },
      { type: "p", text: "הדרכת בטיחות אש היא השקעה במניעה. עובדים מודרכים יודעים למנוע שריפות ולהגיב נכון כשהן קורות." },

      { type: "cta", text: "צריכים לארגן הדרכת בטיחות אש לארגון? צרו קשר." }
    ]
  },
  {
    id: 38,
    slug: "safety-training-faq",
    title: "שאלות ותשובות על הדרכות בטיחות",
    metaTitle: "שאלות נפוצות הדרכות בטיחות | FAQ | אדם בטיחות",
    metaDescription: "תשובות לשאלות הנפוצות על הדרכות בטיחות: מי חייב? כמה פעמים? מה התוכן? כל מה שמעסיקים צריכים לדעת.",
    excerpt: "כל התשובות לשאלות הנפוצות על הדרכות בטיחות במקום אחד.",
    category: "הדרכות",
    date: "2024-12-25",
    readTime: "7 דקות קריאה",
    image: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=800&auto=format&fit=crop",
    imageAlt: "שאלות ותשובות",
    keywords: ["הדרכות בטיחות", "שאלות נפוצות", "FAQ", "חובה", "מעסיקים"],
    content: [
      { type: "h2", text: "שאלות כלליות" },

      { type: "h3", text: "האם הדרכות בטיחות הן חובה?" },
      { type: "p", text: "כן. על פי פקודת הבטיחות בעבודה וחוק ארגון הפיקוח, כל מעסיק חייב להדריך את עובדיו בנושאי בטיחות. זו לא המלצה - זו חובה חוקית." },

      { type: "h3", text: "מי אחראי על ההדרכות?" },
      { type: "p", text: "המעסיק נושא באחריות החוקית. בפועל, ההדרכות מבוצעות על ידי ממונה בטיחות, מדריכים פנימיים או גופי הדרכה חיצוניים." },

      { type: "h3", text: "מה קורה אם לא מבצעים הדרכות?" },
      { type: "list", items: [
        "קנסות ממפקח עבודה",
        "אחריות פלילית במקרה של תאונה",
        "תביעות אזרחיות מנפגעים",
        "פגיעה בביטוח"
      ]},

      { type: "h2", text: "שאלות על סוגי הדרכות" },

      { type: "h3", text: "אילו הדרכות חובה על כל עובד?" },
      { type: "list", items: [
        "הדרכת כניסה לעובד חדש",
        "הדרכת בטיחות אש ופינוי",
        "הדרכת שימוש בציוד מגן אישי",
        "הדרכות ספציפיות לתפקיד"
      ]},

      { type: "h3", text: "מה זה הדרכת ריענון?" },
      { type: "p", text: "הדרכה שמטרתה לרענן ולעדכן ידע קיים. חובה לבצע הדרכות ריענון תקופתיות - בדרך כלל פעם בשנה או לפי דרישות ספציפיות." },

      { type: "h3", text: "מה ההבדל בין הדרכה כללית לספציפית?" },
      { type: "p", text: "הדרכה כללית מכסה נושאים שרלוונטיים לכל עובד (אש, פינוי, עזרה ראשונה). הדרכה ספציפית מתמקדת בסיכונים הקשורים לתפקיד מסוים (מכונות, חומרים, גובה)." },

      { type: "h2", text: "שאלות על תוכן ומשך" },

      { type: "h3", text: "כמה זמן צריכה להימשך הדרכה?" },
      { type: "list", items: [
        "הדרכת כניסה: 1-4 שעות (תלוי בסיכונים)",
        "הדרכת ריענון: 1-2 שעות",
        "הדרכות מיוחדות (גובה, מלגזה): 8-16 שעות",
        "תרגיל פינוי: 30-60 דקות"
      ]},

      { type: "h3", text: "מה חייב להיות בכל הדרכה?" },
      { type: "list", items: [
        "סיכונים רלוונטיים לעבודה",
        "נהלי עבודה בטוחים",
        "שימוש בציוד מגן",
        "נהלי חירום ופינוי",
        "איך לדווח על בעיות"
      ]},

      { type: "h3", text: "האם צריך מבחן בסוף ההדרכה?" },
      { type: "p", text: "לא תמיד חובה, אבל מאוד מומלץ. מבחן מוודא שהעובדים הבינו את החומר ומהווה תיעוד להבנה." },

      { type: "h2", text: "שאלות על תיעוד" },

      { type: "h3", text: "מה צריך לתעד?" },
      { type: "list", items: [
        "שם העובד וחתימתו",
        "תאריך ומשך ההדרכה",
        "נושאי ההדרכה",
        "שם המדריך",
        "תוצאות מבחן (אם בוצע)"
      ]},

      { type: "h3", text: "כמה זמן לשמור את התיעוד?" },
      { type: "p", text: "לפחות 7 שנים. מומלץ לשמור לכל תקופת העסקת העובד ולפחות 3 שנים אחרי סיומה." },

      { type: "h3", text: "האם תיעוד דיגיטלי מספיק?" },
      { type: "p", text: "כן, בתנאי שהוא מאובטח ונגיש. מערכות דיגיטליות אפילו עדיפות כי הן מקלות על מעקב ותזכורות." },

      { type: "h2", text: "שאלות על מקרים מיוחדים" },

      { type: "h3", text: "מה עם עובדים זרים או דוברי שפות אחרות?" },
      { type: "p", text: "ההדרכה חייבת להיות בשפה שהעובד מבין. יש לספק תרגום או הדרכה בשפת האם של העובד." },

      { type: "h3", text: "מה עם עובדי קבלן?" },
      { type: "p", text: "גם עובדי קבלן שעובדים אצלכם צריכים הדרכה. האחריות מתחלקת בין הקבלן למזמין העבודה." },

      { type: "h3", text: "מה עם עובדים זמניים?" },
      { type: "p", text: "כל עובד - גם זמני - חייב לעבור הדרכה לפני תחילת העבודה. אין פטור לעבודה קצרה." },

      { type: "h2", text: "שאלות על עלויות" },

      { type: "h3", text: "כמה עולה הדרכת בטיחות?" },
      { type: "p", text: "עלויות משתנות לפי סוג ההדרכה והספק. טווח מחירים:" },
      { type: "list", items: [
        "הדרכת כניסה: 100-300 ₪ לעובד",
        "הדרכת ריענון: 50-150 ₪ לעובד",
        "קורס עבודה בגובה: 400-800 ₪",
        "קורס מלגזות: 800-1,500 ₪"
      ]},

      { type: "h3", text: "האם ההדרכה בזמן העבודה?" },
      { type: "p", text: "כן. הדרכות בטיחות צריכות להתקיים בשעות העבודה ועל חשבון המעסיק." },

      { type: "h2", text: "סיכום" },
      { type: "p", text: "הדרכות בטיחות הן חובה חוקית ומקצועית. השקעה בהדרכות איכותיות מגנה על העובדים ועל העסק." },

      { type: "cta", text: "יש לכם שאלות נוספות? צרו קשר ונשמח לעזור." }
    ]
  },
  {
    id: 39,
    slug: "occupational-diseases-health",
    title: "מחלות מקצוע ובריאות תעסוקתית - מדריך מקיף",
    metaTitle: "מחלות מקצוע ובריאות תעסוקתית | מדריך | אדם בטיחות",
    metaDescription: "מהן מחלות מקצוע? איך מזהים ומונעים אותן? מדריך מקיף לבריאות תעסוקתית לעובדים ומעסיקים.",
    excerpt: "מחלות מקצוע מתפתחות לאט אבל יכולות להיות קטלניות. הנה איך לזהות ולמנוע אותן.",
    category: "גיהות",
    date: "2024-12-24",
    readTime: "10 דקות קריאה",
    image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800&auto=format&fit=crop",
    imageAlt: "בדיקה רפואית תעסוקתית",
    featured: true,
    keywords: ["מחלות מקצוע", "בריאות תעסוקתית", "גיהות", "מניעה", "חשיפה"],
    content: [
      { type: "h2", text: "מהי מחלת מקצוע?" },
      { type: "p", text: "מחלת מקצוע היא מחלה שנגרמת או מחמירה כתוצאה מחשיפה לגורמים מזיקים בסביבת העבודה. בניגוד לתאונות עבודה שקורות ברגע, מחלות מקצוע מתפתחות לאורך זמן - לפעמים שנים." },
      { type: "quote", text: "לפי הערכות ארגון העבודה הבינלאומי, מחלות מקצוע גורמות ליותר מקרי מוות מתאונות עבודה." },

      { type: "h2", text: "סוגי חשיפות מזיקות" },

      { type: "h3", text: "חשיפה כימית" },
      { type: "list", items: [
        "נשימת אדים וגזים רעילים",
        "מגע עם חומרים מסוכנים",
        "חשיפה לאבק תעשייתי",
        "עבודה עם ממסים ודלקים"
      ]},

      { type: "h3", text: "חשיפה פיזיקלית" },
      { type: "list", items: [
        "רעש חזק",
        "רטט",
        "קרינה (יונית ולא-יונית)",
        "חום או קור קיצוניים",
        "לחץ אטמוספרי חריג"
      ]},

      { type: "h3", text: "חשיפה ביולוגית" },
      { type: "list", items: [
        "חיידקים ונגיפים",
        "פטריות ועובש",
        "אלרגנים"
      ]},

      { type: "h3", text: "חשיפה ארגונומית" },
      { type: "list", items: [
        "תנועות חוזרות",
        "תנוחות לא נכונות",
        "הרמת משאות",
        "ישיבה ממושכת"
      ]},

      { type: "h2", text: "מחלות מקצוע נפוצות" },

      { type: "h3", text: "מחלות ריאה" },
      { type: "list", items: [
        "סיליקוזיס (מחשיפה לאבק סיליקה)",
        "אסבסטוזיס (מחשיפה לאסבסט)",
        "אסתמה תעסוקתית",
        "מחלת ריאות חסימתית כרונית (COPD)"
      ]},

      { type: "h3", text: "פגיעות שמיעה" },
      { type: "list", items: [
        "ירידה בשמיעה מרעש",
        "טינטון (צלצולים באוזניים)"
      ]},

      { type: "h3", text: "מחלות עור" },
      { type: "list", items: [
        "דרמטיטיס ממגע",
        "אלרגיות עוריות",
        "סרטן עור (מחשיפה לשמש)"
      ]},

      { type: "h3", text: "הפרעות שלד-שריר" },
      { type: "list", items: [
        "תסמונת התעלה הקרפלית",
        "כאבי גב תחתון",
        "דלקות גידים"
      ]},

      { type: "h3", text: "סרטן תעסוקתי" },
      { type: "list", items: [
        "סרטן ריאות (אסבסט, סיליקה)",
        "מזותליומה (אסבסט)",
        "סרטן שלפוחית השתן (צבענים)",
        "לוקמיה (בנזן)"
      ]},

      { type: "h2", text: "מניעת מחלות מקצוע" },

      { type: "h3", text: "היררכיית הבקרה" },
      { type: "list", items: [
        "סילוק - הסרת הגורם המזיק",
        "החלפה - שימוש בחומר פחות מסוכן",
        "בקרה הנדסית - אוורור, מיגון",
        "בקרה מנהלית - הגבלת זמן חשיפה, נהלים",
        "ציוד מגן אישי - קו הגנה אחרון"
      ]},

      { type: "h3", text: "ניטור סביבתי" },
      { type: "p", text: "מדידות תקופתיות של גורמי סיכון בסביבת העבודה: רעש, אבק, חומרים כימיים." },

      { type: "h3", text: "בדיקות רפואיות תעסוקתיות" },
      { type: "list", items: [
        "בדיקה לפני קבלה לעבודה",
        "בדיקות תקופתיות (שנתיות)",
        "בדיקות מעקב לאחר חשיפה"
      ]},

      { type: "h2", text: "זכויות עובדים" },
      { type: "list", items: [
        "זכות לידע על סיכונים בעבודה",
        "זכות לציוד מגן מתאים",
        "זכות לבדיקות רפואיות",
        "זכות להכרה במחלת מקצוע ופיצוי",
        "זכות לסרב לעבודה מסוכנת"
      ]},

      { type: "h2", text: "הכרה במחלת מקצוע" },
      { type: "p", text: "להכרה במחלת מקצוע על ידי ביטוח לאומי:" },
      { type: "list", items: [
        "יש להוכיח קשר בין המחלה לעבודה",
        "המחלה צריכה להופיע ברשימת מחלות המקצוע",
        "יש לפנות למחלקת נפגעי עבודה בביטוח לאומי",
        "נדרש תיעוד רפואי ותעסוקתי"
      ]},

      { type: "h2", text: "תפקיד המעסיק" },
      { type: "list", items: [
        "זיהוי סיכונים בריאותיים",
        "ביצוע מדידות סביבתיות",
        "יישום אמצעי מניעה",
        "הספקת ציוד מגן מתאים",
        "ביצוע בדיקות רפואיות",
        "הדרכת עובדים על סיכונים"
      ]},

      { type: "h2", text: "סיכום" },
      { type: "p", text: "מחלות מקצוע הן סיכון אמיתי שניתן ברובו למניעה. זיהוי מוקדם, ניטור ומניעה יכולים להציל חיים ולשמור על בריאות העובדים לאורך שנים." },

      { type: "cta", text: "צריכים ייעוץ בנושא גיהות תעסוקתית? צרו קשר." }
    ]
  },
  {
    id: 40,
    slug: "noisy-work-environment-safety",
    title: "בטיחות בסביבת עבודה רועשת - הגנה על השמיעה",
    metaTitle: "בטיחות בסביבה רועשת | הגנת שמיעה | אדם בטיחות",
    metaDescription: "רעש בעבודה גורם לפגיעה בלתי הפיכה בשמיעה. מדריך מקיף להגנה על שמיעת העובדים.",
    excerpt: "ירידה בשמיעה מרעש היא מחלת המקצוע הנפוצה ביותר - והיא בלתי הפיכה. הנה איך להגן על השמיעה.",
    category: "גיהות",
    date: "2024-12-23",
    readTime: "8 דקות קריאה",
    image: "https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=800&auto=format&fit=crop",
    imageAlt: "עובד עם מגני אוזניים",
    keywords: ["רעש", "שמיעה", "מגני אוזניים", "גיהות", "מחלת מקצוע"],
    content: [
      { type: "h2", text: "הסכנה שבחשיפה לרעש" },
      { type: "p", text: "חשיפה ממושכת לרעש חזק גורמת לפגיעה בלתי הפיכה בשמיעה. זוהי מחלת המקצוע הנפוצה ביותר בעולם, והעצוב הוא שהיא ניתנת למניעה מלאה." },
      { type: "quote", text: "לפי משרד העבודה, כ-20% מעובדי התעשייה בישראל חשופים לרמות רעש מזיקות." },

      { type: "h2", text: "מהו רעש מזיק?" },
      { type: "p", text: "עוצמת הרעש נמדדת בדציבלים (dB). רמות חשיפה:" },
      { type: "list", items: [
        "שיחה רגילה: 60-70 dB",
        "תנועה כבדה: 80-85 dB",
        "מכונות תעשייתיות: 90-100 dB",
        "מסור חשמלי: 100-110 dB",
        "מטוס ממריא: 120+ dB"
      ]},
      { type: "p", text: "על פי התקנות, הגבול המותר הוא 85 dB ל-8 שעות עבודה. כל עלייה של 3 dB מקצצת את הזמן המותר בחצי." },

      { type: "h2", text: "השפעות הרעש על הבריאות" },

      { type: "h3", text: "פגיעה בשמיעה" },
      { type: "list", items: [
        "ירידה הדרגתית בשמיעה",
        "טינטון (צלצולים באוזניים)",
        "קושי להבין דיבור",
        "רגישות יתר לרעש"
      ]},

      { type: "h3", text: "השפעות נוספות" },
      { type: "list", items: [
        "לחץ ומתח",
        "הפרעות שינה",
        "עייפות",
        "ירידה בריכוז",
        "סיכון מוגבר לתאונות (לא שומעים אזהרות)"
      ]},

      { type: "h2", text: "מדידת רעש" },
      { type: "p", text: "יש לבצע מדידות רעש במקומות עבודה רועשים:" },
      { type: "list", items: [
        "מדידה סביבתית - רמת הרעש באזור",
        "מדידה אישית (דוסימטריה) - חשיפת העובד",
        "תדירות: לפחות פעם בשנה או בכל שינוי"
      ]},

      { type: "h2", text: "אמצעי הגנה" },

      { type: "h3", text: "1. בקרה הנדסית (עדיפה)" },
      { type: "list", items: [
        "בידוד מכונות רועשות",
        "תחזוקה נכונה של ציוד",
        "החלפת ציוד ישן ברועש פחות",
        "הרחקת עמדות עבודה ממקור הרעש",
        "קירות ומחיצות אקוסטיות"
      ]},

      { type: "h3", text: "2. בקרה מנהלית" },
      { type: "list", items: [
        "הגבלת זמן חשיפה",
        "רוטציה בין עמדות",
        "הפסקות באזורים שקטים",
        "תזמון עבודות רועשות"
      ]},

      { type: "h3", text: "3. ציוד מגן אישי" },
      { type: "list", items: [
        "אטמי אוזניים (Ear Plugs) - הפחתה של 15-30 dB",
        "כיסויי אוזניים (Ear Muffs) - הפחתה של 20-35 dB",
        "שילוב של שניהם לרעש חזק במיוחד"
      ]},

      { type: "h2", text: "בחירת מגן שמיעה" },
      { type: "list", items: [
        "התאמה לרמת הרעש (NRR מתאים)",
        "נוחות לשימוש ממושך",
        "התאמה לתנאי העבודה (חום, לכלוך)",
        "אפשרות לתקשורת אם נדרש",
        "התאמה אישית לעובד"
      ]},

      { type: "h2", text: "בדיקות שמיעה" },
      { type: "p", text: "עובדים החשופים לרעש צריכים לעבור בדיקות שמיעה (אודיומטריה):" },
      { type: "list", items: [
        "בדיקה בסיסית לפני תחילת עבודה",
        "בדיקות מעקב שנתיות",
        "בדיקה לאחר חשיפה חריגה"
      ]},

      { type: "h2", text: "סימנים לפגיעה בשמיעה" },
      { type: "list", items: [
        "קושי לשמוע שיחה בסביבה רועשת",
        "צורך להגביר טלוויזיה או רדיו",
        "צלצולים או זמזום באוזניים",
        "תחושה של 'אוזניים סתומות'",
        "קושי לשמוע צלילים גבוהים"
      ]},

      { type: "h2", text: "אחריות המעסיק" },
      { type: "list", items: [
        "ביצוע מדידות רעש",
        "יישום אמצעי הפחתה",
        "הספקת ציוד מגן מתאים",
        "הדרכה על סיכוני רעש ושימוש במגנים",
        "ביצוע בדיקות שמיעה",
        "ניהול רישום ומעקב"
      ]},

      { type: "h2", text: "סיכום" },
      { type: "p", text: "ירידה בשמיעה מרעש היא בלתי הפיכה, אבל ניתנת למניעה מלאה. שילוב של בקרה הנדסית, מנהלית וציוד מגן יכול לשמור על שמיעת העובדים לאורך כל הקריירה." },

      { type: "cta", text: "צריכים בדיקת רעש או ייעוץ בנושא? צרו קשר." }
    ]
  },
  {
    id: 41,
    slug: "workplace-ergonomics",
    title: "ארגונומיה בעבודה - עקרונות ויישום",
    metaTitle: "ארגונומיה בעבודה | מניעת פגיעות | אדם בטיחות",
    metaDescription: "ארגונומיה נכונה מונעת כאבי גב, פציעות ומחלות מקצוע. מדריך מקיף ליישום עקרונות ארגונומיים בעבודה.",
    excerpt: "כאבי גב, צוואר וידיים הם מהתלונות הנפוצות ביותר בעבודה. ארגונומיה נכונה יכולה למנוע אותם.",
    category: "גיהות",
    date: "2024-12-22",
    readTime: "9 דקות קריאה",
    image: "https://images.unsplash.com/photo-1524758631624-e2822e304c36?w=800&auto=format&fit=crop",
    imageAlt: "עמדת עבודה ארגונומית",
    keywords: ["ארגונומיה", "כאבי גב", "עמדת עבודה", "ישיבה", "הרמת משאות"],
    content: [
      { type: "h2", text: "מהי ארגונומיה?" },
      { type: "p", text: "ארגונומיה היא המדע של התאמת העבודה לאדם. היא עוסקת בעיצוב סביבת העבודה, הכלים והתהליכים כך שיתאימו ליכולות ולמגבלות הגוף האנושי." },
      { type: "quote", text: "הפרעות שלד-שריר (MSD) אחראיות ליותר מ-30% מימי המחלה בתעשייה." },

      { type: "h2", text: "למה ארגונומיה חשובה?" },
      { type: "list", items: [
        "מניעת כאבים ופציעות",
        "הפחתת ימי מחלה והיעדרות",
        "שיפור פרודוקטיביות ואיכות",
        "שימור עובדים",
        "צמצום עלויות רפואיות ופיצויים"
      ]},

      { type: "h2", text: "גורמי סיכון ארגונומיים" },

      { type: "h3", text: "תנוחות לא נכונות" },
      { type: "list", items: [
        "ישיבה ממושכת",
        "כיפוף גב חוזר",
        "הרמת ידיים מעל הכתפיים",
        "סיבוב הגו תוך כדי הרמה"
      ]},

      { type: "h3", text: "תנועות חוזרות" },
      { type: "list", items: [
        "הקלדה ממושכת",
        "עבודה עם כלים ידניים",
        "אריזה ומיון"
      ]},

      { type: "h3", text: "הפעלת כוח" },
      { type: "list", items: [
        "הרמת משאות כבדים",
        "דחיפה ומשיכה",
        "אחיזה חזקה ממושכת"
      ]},

      { type: "h3", text: "גורמים סביבתיים" },
      { type: "list", items: [
        "תאורה לא מספקת",
        "טמפרטורה קיצונית",
        "רטט"
      ]},

      { type: "h2", text: "ארגונומיה בעבודה משרדית" },

      { type: "h3", text: "כיסא" },
      { type: "list", items: [
        "גובה מתכוונן - רגליים על הרצפה",
        "תמיכה בגב התחתון",
        "משענות ידיים בגובה שולחן",
        "מושב מרופד ונוח"
      ]},

      { type: "h3", text: "שולחן" },
      { type: "list", items: [
        "גובה המאפשר מרפקים בזווית 90°",
        "מספיק מקום לעבודה",
        "אפשרות לעמידה (שולחן מתכוונן - מומלץ)"
      ]},

      { type: "h3", text: "מסך מחשב" },
      { type: "list", items: [
        "גובה - קו העיניים בשליש העליון",
        "מרחק - אורך זרוע",
        "זווית - ישר מול הפנים",
        "תאורה - ללא סנוור"
      ]},

      { type: "h3", text: "מקלדת ועכבר" },
      { type: "list", items: [
        "מקלדת בגובה מרפקים",
        "עכבר קרוב למקלדת",
        "תמיכה לפרקי ידיים"
      ]},

      { type: "h2", text: "ארגונומיה בעבודה פיזית" },

      { type: "h3", text: "הרמת משאות" },
      { type: "list", items: [
        "להתקרב למשא לפני הרמה",
        "לכופף ברכיים, לא בגב",
        "להחזיק המשא קרוב לגוף",
        "לא לסובב הגו בזמן הרמה",
        "להשתמש באמצעי עזר כשאפשר"
      ]},

      { type: "h3", text: "עבודה בעמידה" },
      { type: "list", items: [
        "משטח עבודה בגובה מרפקים",
        "משטח מונע עייפות לרגליים",
        "הפסקות ישיבה",
        "החלפת משקל בין הרגליים"
      ]},

      { type: "h3", text: "עבודה עם כלים" },
      { type: "list", items: [
        "כלים ארגונומיים מותאמים",
        "אחיזה נוחה",
        "הפחתת רטט",
        "הפסקות קצרות"
      ]},

      { type: "h2", text: "הפסקות והתאוששות" },
      { type: "list", items: [
        "הפסקה קצרה כל 30-60 דקות",
        "שינוי תנוחה באופן קבוע",
        "מתיחות ותנועה",
        "הפסקות ארוכות יותר כל כמה שעות"
      ]},

      { type: "h2", text: "סימנים לבעיות ארגונומיות" },
      { type: "list", items: [
        "כאבים בגב, צוואר או כתפיים",
        "כאבים או נימול בידיים",
        "עייפות מהירה",
        "כאבי ראש",
        "עייפות עיניים"
      ]},

      { type: "h2", text: "ביצוע הערכה ארגונומית" },
      { type: "p", text: "צעדים להערכה ושיפור:" },
      { type: "list", items: [
        "תצפית על תנוחות ותנועות עבודה",
        "ראיון עובדים על תלונות וקשיים",
        "זיהוי גורמי סיכון",
        "תכנון ויישום שיפורים",
        "מעקב והערכה מחודשת"
      ]},

      { type: "h2", text: "סיכום" },
      { type: "p", text: "ארגונומיה טובה היא השקעה בבריאות העובדים ובפרודוקטיביות. שינויים קטנים בעמדת העבודה או בשיטות העבודה יכולים למנוע כאבים ופציעות." },

      { type: "cta", text: "צריכים הערכה ארגונומית לארגון? צרו קשר." }
    ]
  },
  {
    id: 42,
    title: "ציוד מגן אישי - סוגים, בחירה ושימוש נכון",
    slug: "personal-protective-equipment-guide",
    excerpt: "מדריך מקיף על ציוד מגן אישי (PPE) - סוגי הציוד, איך לבחור את הציוד המתאים, ואיך להשתמש בו נכון.",
    date: "2024-04-08",
    category: "מדריכים",
    metaTitle: "ציוד מגן אישי (PPE) - מדריך לבחירה ושימוש נכון | אדם בטיחות",
    metaDescription: "מדריך מקיף על ציוד מגן אישי - קסדות, משקפיים, כפפות, נעליים ועוד. איך לבחור ולהשתמש נכון בציוד מגן.",
    keywords: ["ציוד מגן אישי", "PPE", "קסדת בטיחות", "כפפות עבודה", "נעלי בטיחות"],
    imageAlt: "עובד עם ציוד מגן אישי מלא",
    image: "/portfolio/industrial-safety.jpg",
    readTime: "11 דקות קריאה",
    content: [
      { type: "h2", text: "מהו ציוד מגן אישי?" },
      { type: "p", text: "ציוד מגן אישי (Personal Protective Equipment - PPE) הוא ציוד שנועד להגן על העובד מפני סיכונים במקום העבודה. הציוד מהווה קו הגנה אחרון לאחר שכל האמצעים ההנדסיים והניהוליים מוצו." },

      { type: "h2", text: "הגנת ראש" },

      { type: "h3", text: "קסדות בטיחות" },
      { type: "list", items: [
        "קסדה סטנדרטית - הגנה מנפילת חפצים",
        "קסדה עם מגן פנים - הגנה מהתזות",
        "קסדה בידודית - הגנה מפגיעת חשמל",
        "קסדה עם מגן צוואר - הגנה מהשמש"
      ]},

      { type: "h3", text: "כללים לשימוש" },
      { type: "list", items: [
        "בדיקה יומית של שלמות הקסדה",
        "החלפה לאחר מכה או כל 5 שנים",
        "התאמה נכונה לגודל הראש",
        "שימוש ברצועת סנטר כשנדרש"
      ]},

      { type: "h2", text: "הגנת עיניים" },

      { type: "h3", text: "סוגי הגנה" },
      { type: "list", items: [
        "משקפי בטיחות - הגנה מהתזות ושבבים",
        "משקפי ריתוך - הגנה מקרינה",
        "מסכת פנים - הגנה מלאה",
        "משקפי מגן מכימיקלים - הגנה מנוזלים"
      ]},

      { type: "h3", text: "בחירה נכונה" },
      { type: "list", items: [
        "התאמה לסוג הסיכון",
        "נוחות לשימוש ממושך",
        "תאימות לציוד מגן אחר",
        "עמידות בתקן"
      ]},

      { type: "h2", text: "הגנת שמיעה" },

      { type: "h3", text: "סוגי אטמים" },
      { type: "list", items: [
        "אטמי אוזניים - קלים ונוחים",
        "אוזניות מגן - הגנה גבוהה",
        "אטמים מותאמים אישית - נוחות מרבית"
      ]},

      { type: "h3", text: "מתי נדרשת הגנה?" },
      { type: "list", items: [
        "חשיפה לרעש מעל 80dB",
        "עבודה ממושכת בסביבה רועשת",
        "חשיפה לרעשים פתאומיים חזקים"
      ]},

      { type: "h2", text: "הגנת נשימה" },

      { type: "h3", text: "סוגי מסננים" },
      { type: "list", items: [
        "מסכת אבק (P1, P2, P3) - הגנה מחלקיקים",
        "מסכה עם פילטר כימי - הגנה מגזים",
        "מערכת אספקת אוויר - סביבה מסוכנת"
      ]},

      { type: "h3", text: "כללים לשימוש" },
      { type: "list", items: [
        "בדיקת אטימות לפני כל שימוש",
        "החלפת פילטרים בזמן",
        "אחסון נכון",
        "גילוח פנים לאטימות"
      ]},

      { type: "h2", text: "הגנת ידיים" },

      { type: "h3", text: "סוגי כפפות" },
      { type: "list", items: [
        "כפפות עור - הגנה מחתכים ושריטות",
        "כפפות ניטריל - הגנה מכימיקלים",
        "כפפות מבודדות - הגנה מחשמל",
        "כפפות נגד חום - הגנה מטמפרטורות"
      ]},

      { type: "h3", text: "בחירה נכונה" },
      { type: "list", items: [
        "התאמה לסוג העבודה",
        "גודל מתאים",
        "זמינות לשימוש חוזר או חד-פעמי",
        "תאימות לחומרים"
      ]},

      { type: "h2", text: "הגנת רגליים" },

      { type: "h3", text: "סוגי נעליים" },
      { type: "list", items: [
        "נעלי בטיחות עם כיפת פלדה",
        "נעליים מונעות החלקה",
        "נעליים בידודיות",
        "מגפי גומי לעבודה רטובה"
      ]},

      { type: "h2", text: "ביגוד מגן" },
      { type: "list", items: [
        "סרבל עבודה - הגנה כללית",
        "אפוד זוהר - נראות",
        "ביגוד נגד אש - עבודה עם חום",
        "ביגוד כימי - הגנה מחומרים מסוכנים"
      ]},

      { type: "h2", text: "תחזוקה ואחסון" },
      { type: "list", items: [
        "ניקוי יומי של הציוד",
        "בדיקה תקופתית של שלמות",
        "אחסון במקום יבש ונקי",
        "החלפה בזמן על פי הוראות יצרן"
      ]},

      { type: "h2", text: "אחריות המעסיק" },
      { type: "list", items: [
        "לספק ציוד מגן מתאים ללא עלות",
        "להדריך על שימוש נכון",
        "לוודא שימוש בפועל",
        "להחליף ציוד בלוי או פגום"
      ]},

      { type: "h2", text: "סיכום" },
      { type: "p", text: "ציוד מגן אישי הוא קו הגנה קריטי. בחירה נכונה, שימוש נכון ותחזוקה שוטפת מבטיחים הגנה אפקטיבית על העובדים." },

      { type: "cta", text: "צריכים ייעוץ לבחירת ציוד מגן? צרו קשר." }
    ]
  },
  {
    id: 43,
    title: "בטיחות בעבודה במשמרות - סיכונים ומניעה",
    slug: "shift-work-safety-risks",
    excerpt: "עבודה במשמרות מציבה אתגרי בטיחות ייחודיים. מדריך מקיף על הסיכונים ואיך להתמודד איתם.",
    date: "2024-04-11",
    category: "גיהות",
    metaTitle: "בטיחות בעבודה במשמרות - סיכונים ופתרונות | אדם בטיחות",
    metaDescription: "מדריך לבטיחות בעבודה במשמרות - סיכוני עייפות, ניהול משמרות, והמלצות לעובדים ומעסיקים.",
    keywords: ["עבודה במשמרות", "משמרות לילה", "עייפות בעבודה", "בטיחות עובדים"],
    imageAlt: "עובד במשמרת לילה במפעל",
    image: "/portfolio/factory-worker.jpg",
    readTime: "10 דקות קריאה",
    content: [
      { type: "h2", text: "עבודה במשמרות בישראל" },
      { type: "p", text: "כ-15% מהעובדים בישראל עובדים במשמרות. עבודה בשעות לא שגרתיות משפיעה על בריאות, ערנות ובטיחות העובדים." },

      { type: "h2", text: "סיכוני בטיחות בעבודה במשמרות" },

      { type: "h3", text: "ירידה בערנות" },
      { type: "list", items: [
        "עייפות מצטברת",
        "ירידה בזמן תגובה",
        "טעויות שיפוט",
        "קשיי ריכוז"
      ]},

      { type: "h3", text: "בעיות בריאותיות" },
      { type: "list", items: [
        "הפרעות שינה",
        "בעיות עיכול",
        "מחלות לב וכלי דם",
        "בעיות נפשיות - חרדה, דיכאון"
      ]},

      { type: "h3", text: "סיכונים חברתיים" },
      { type: "list", items: [
        "קשיים במערכות יחסים",
        "בידוד חברתי",
        "קושי בהפרדה בית-עבודה"
      ]},

      { type: "h2", text: "השעות הקריטיות" },
      { type: "p", text: "המחקר מצביע על שעות עם סיכון מוגבר:" },
      { type: "list", items: [
        "02:00-06:00 - שפל ביולוגי טבעי",
        "14:00-16:00 - ירידה טבעית בערנות",
        "סוף משמרת - עייפות מצטברת"
      ]},

      { type: "h2", text: "ניהול סיכונים - רמת הארגון" },

      { type: "h3", text: "תכנון משמרות" },
      { type: "list", items: [
        "העדפת רוטציה קדימה (בוקר→צהריים→לילה)",
        "הגבלת רצף משמרות לילה",
        "מתן מספיק זמן מנוחה בין משמרות",
        "הימנעות ממשמרות 12+ שעות"
      ]},

      { type: "h3", text: "סביבת עבודה" },
      { type: "list", items: [
        "תאורה מספקת",
        "טמפרטורה נעימה",
        "זמינות מזון ושתייה",
        "אזורי מנוחה"
      ]},

      { type: "h3", text: "פיקוח והתנהלות" },
      { type: "list", items: [
        "פיקוח מוגבר בשעות קריטיות",
        "תחלופה בתפקידים מונוטוניים",
        "הפסקות מסודרות",
        "מערכת דיווח על עייפות"
      ]},

      { type: "h2", text: "המלצות לעובדים" },

      { type: "h3", text: "לפני משמרת לילה" },
      { type: "list", items: [
        "לנוח לפני המשמרת",
        "לאכול ארוחה קלה",
        "להימנע מאלכוהול",
        "לשתות קפה בתחילת המשמרת בלבד"
      ]},

      { type: "h3", text: "במהלך המשמרת" },
      { type: "list", items: [
        "לשמור על ערנות פעילה",
        "לקחת הפסקות קצרות",
        "לשתות מים",
        "להימנע ממזון כבד בלילה"
      ]},

      { type: "h3", text: "אחרי המשמרת" },
      { type: "list", items: [
        "לנהוג בזהירות - סכנת הירדמות",
        "לישון מיד כשמגיעים הביתה",
        "ליצור סביבה חשוכה ושקטה לשינה",
        "להימנע מקפאין לפני השינה"
      ]},

      { type: "h2", text: "זכויות עובדי משמרות" },
      { type: "list", items: [
        "תוספת שכר על משמרות לילה",
        "הגבלת שעות עבודה",
        "זכות לבדיקות רפואיות",
        "זכות להחליף סוג משמרת"
      ]},

      { type: "h2", text: "סיכום" },
      { type: "p", text: "עבודה במשמרות דורשת התייחסות מיוחדת לבטיחות. ניהול נכון ברמת הארגון והתנהגות נכונה של העובד יכולים להפחית משמעותית את הסיכונים." },

      { type: "cta", text: "מעסיקים עובדי משמרות? צרו קשר לייעוץ בטיחות." }
    ]
  },
  {
    id: 44,
    title: "עומס חום בעבודה - סכנות, מניעה וטיפול",
    slug: "heat-stress-work-prevention",
    excerpt: "עבודה בחום קיצוני מסכנת חיים. מדריך מקיף על עומס חום, סימני אזהרה, ואיך למנוע מקרי חירום.",
    date: "2024-04-14",
    category: "גיהות",
    metaTitle: "עומס חום בעבודה - מניעה וטיפול חירום | אדם בטיחות",
    metaDescription: "מדריך מקיף לעומס חום בעבודה - סימפטומים, מניעה, וטיפול חירום במכת חום ותשישות חום.",
    keywords: ["עומס חום", "מכת חום", "עבודה בחום", "תשישות חום", "בטיחות קיץ"],
    imageAlt: "עובד בניין בחום הקיץ",
    image: "/portfolio/construction-site.jpg",
    readTime: "11 דקות קריאה",
    content: [
      { type: "h2", text: "מהו עומס חום?" },
      { type: "p", text: "עומס חום מתרחש כאשר הגוף לא מצליח לוותר על חום עודף. זהו מצב מסוכן שעלול להוביל למוות. בישראל, עובדים רבים חשופים לחום קיצוני בחודשי הקיץ." },

      { type: "h2", text: "גורמים לעומס חום" },
      { type: "list", items: [
        "טמפרטורה גבוהה בסביבה",
        "לחות גבוהה",
        "מאמץ פיזי",
        "ביגוד מגן כבד",
        "חוסר הסתגלות לחום",
        "התייבשות"
      ]},

      { type: "h2", text: "דרגות עומס חום" },

      { type: "h3", text: "תשישות חום (Heat Exhaustion)" },
      { type: "p", text: "מצב קל יחסית הניתן לטיפול במקום:" },
      { type: "list", items: [
        "הזעה מרובה",
        "חולשה, עייפות",
        "סחרחורת, בחילה",
        "כאבי ראש",
        "עור חיוור ולח",
        "דופק מהיר וחלש"
      ]},

      { type: "h3", text: "מכת חום (Heat Stroke)" },
      { type: "p", text: "מצב חירום רפואי מסכן חיים!" },
      { type: "list", items: [
        "טמפרטורה גבוהה מעל 40°C",
        "עור יבש וחם",
        "בלבול, התנהגות משונה",
        "איבוד הכרה",
        "התכווצויות",
        "הפסקת הזעה"
      ]},

      { type: "h2", text: "מניעה ברמת הארגון" },

      { type: "h3", text: "תכנון העבודה" },
      { type: "list", items: [
        "התאמת שעות עבודה - להימנע מעבודה בשיא החום",
        "סיבוב עובדים בתפקידים חמים",
        "הפסקות מנוחה בהצללה",
        "התאמת קצב עבודה"
      ]},

      { type: "h3", text: "סביבת עבודה" },
      { type: "list", items: [
        "הצללה וקירור",
        "אוורור",
        "נקודות שתייה נגישות",
        "אזורי מנוחה ממוזגים"
      ]},

      { type: "h3", text: "ציוד והגנה" },
      { type: "list", items: [
        "ביגוד בהיר ומאוורר",
        "כיסוי ראש",
        "מערכות קירור אישיות במידת הצורך"
      ]},

      { type: "h2", text: "מניעה ברמת העובד" },

      { type: "h3", text: "שתייה" },
      { type: "list", items: [
        "לשתות לפני שמרגישים צמא",
        "כוס מים כל 15-20 דקות",
        "להימנע מאלכוהול וקפאין מופרז",
        "משקאות ספורט לעבודה ממושכת"
      ]},

      { type: "h3", text: "הסתגלות" },
      { type: "list", items: [
        "הסתגלות הדרגתית לחום (5-7 ימים)",
        "להכיר את גבולות הגוף",
        "לדווח מיד על סימפטומים"
      ]},

      { type: "h2", text: "טיפול חירום" },

      { type: "h3", text: "תשישות חום" },
      { type: "list", items: [
        "להעביר לצל או מקום ממוזג",
        "לשכב עם רגליים מורמות",
        "לשתות מים",
        "להרטיב בגדים",
        "לפנות לטיפול אם אין שיפור"
      ]},

      { type: "h3", text: "מכת חום - חירום!" },
      { type: "list", items: [
        "להזעיק מד\"א מיד",
        "לקרר במהירות - מים קרים על הגוף",
        "להניח קרח בבתי שחי ומפשעות",
        "לא לתת לשתות אם לא בהכרה",
        "לעקוב אחר הכרה ונשימה"
      ]},

      { type: "h2", text: "חובות המעסיק" },
      { type: "list", items: [
        "הערכת סיכון עומס חום",
        "נהלי עבודה בחום",
        "הכשרת עובדים ומנהלים",
        "אספקת מים ללא הגבלה",
        "מעקב אחר תנאי מזג אוויר"
      ]},

      { type: "h2", text: "סיכום" },
      { type: "p", text: "עומס חום הוא סכנה אמיתית שניתן למנוע. שילוב של תכנון נכון, ציוד מתאים, והתנהגות נכונה מציל חיים." },

      { type: "cta", text: "צריכים תוכנית להתמודדות עם חום? צרו קשר." }
    ]
  },
  {
    id: 45,
    title: "ניהול סיכונים בארגון - מדריך מקיף",
    slug: "organizational-risk-management-guide",
    excerpt: "ניהול סיכונים הוא הבסיס לבטיחות אפקטיבית. מדריך מלא לזיהוי, הערכה וניהול סיכונים בארגון.",
    date: "2024-04-17",
    category: "סיכונים",
    metaTitle: "ניהול סיכונים בארגון - מדריך שלם | אדם בטיחות",
    metaDescription: "מדריך מקיף לניהול סיכונים - זיהוי, הערכה, בקרה ומעקב. כולל מטריצת סיכונים והיררכיית בקרות.",
    keywords: ["ניהול סיכונים", "סקר סיכונים", "הערכת סיכונים", "מטריצת סיכונים"],
    imageAlt: "ניתוח סיכונים בארגון",
    image: "/portfolio/office-safety.jpg",
    readTime: "13 דקות קריאה",
    content: [
      { type: "h2", text: "מהו ניהול סיכונים?" },
      { type: "p", text: "ניהול סיכונים הוא תהליך שיטתי לזיהוי, הערכה והפחתת סיכונים בארגון. ניהול סיכונים אפקטיבי מונע תאונות, מפחית עלויות ומשפר ביצועים." },

      { type: "h2", text: "שלבי ניהול סיכונים" },

      { type: "h3", text: "שלב 1: זיהוי סיכונים" },
      { type: "p", text: "שיטות לזיהוי סיכונים:" },
      { type: "list", items: [
        "סקר עמדות עבודה",
        "ניתוח תאונות קודמות",
        "ראיונות עם עובדים",
        "בדיקת נתוני תחלואה",
        "ניתוח תהליכים (What-If, HAZOP)",
        "סיורים שטח"
      ]},

      { type: "h3", text: "שלב 2: הערכת סיכונים" },
      { type: "p", text: "לכל סיכון מעריכים:" },
      { type: "list", items: [
        "הסתברות - כמה סביר שיקרה?",
        "חומרה - מה הנזק הצפוי?",
        "חשיפה - כמה עובדים וכמה זמן?"
      ]},

      { type: "h3", text: "מטריצת סיכונים" },
      { type: "p", text: "דירוג סיכונים לפי סדר עדיפויות:" },
      { type: "list", items: [
        "גבוה מאוד (אדום) - טיפול מיידי",
        "גבוה (כתום) - טיפול תוך שבוע",
        "בינוני (צהוב) - טיפול תוך חודש",
        "נמוך (ירוק) - מעקב שוטף"
      ]},

      { type: "h3", text: "שלב 3: בקרת סיכונים" },
      { type: "p", text: "היררכיית הבקרות (מהאפקטיבי לפחות):" },
      { type: "list", items: [
        "ביטול - להסיר את הסיכון לחלוטין",
        "החלפה - להחליף לחומר/תהליך בטוח יותר",
        "בקרה הנדסית - מחסומים, אוורור, בידוד",
        "בקרה ניהולית - נהלים, הדרכות, סימון",
        "ציוד מגן אישי - קו הגנה אחרון"
      ]},

      { type: "h3", text: "שלב 4: יישום ומעקב" },
      { type: "list", items: [
        "תכנית פעולה עם אחראים ולוחות זמנים",
        "הקצאת משאבים",
        "מעקב אחר ביצוע",
        "בדיקת אפקטיביות",
        "עדכון שוטף"
      ]},

      { type: "h2", text: "תיעוד סקר סיכונים" },
      { type: "p", text: "מסמך סקר סיכונים צריך לכלול:" },
      { type: "list", items: [
        "תיאור הסיכון ומיקומו",
        "אוכלוסיה חשופה",
        "הערכת הסתברות וחומרה",
        "דירוג הסיכון",
        "בקרות קיימות",
        "בקרות נדרשות",
        "אחראי ולוח זמנים",
        "סטטוס טיפול"
      ]},

      { type: "h2", text: "סוגי סיכונים בארגון" },

      { type: "h3", text: "סיכונים פיזיים" },
      { type: "list", items: [
        "נפילות",
        "מכות ופגיעות",
        "רעש",
        "קרינה",
        "טמפרטורות קיצוניות"
      ]},

      { type: "h3", text: "סיכונים כימיים" },
      { type: "list", items: [
        "חומרים רעילים",
        "חומרים דליקים",
        "אבק וחלקיקים",
        "גזים מסוכנים"
      ]},

      { type: "h3", text: "סיכונים ביולוגיים" },
      { type: "list", items: [
        "חיידקים ווירוסים",
        "טפילים",
        "אלרגנים"
      ]},

      { type: "h3", text: "סיכונים ארגונומיים" },
      { type: "list", items: [
        "תנוחות לא נכונות",
        "עומס על שרירים",
        "תנועות חוזרות"
      ]},

      { type: "h3", text: "סיכונים פסיכוסוציאליים" },
      { type: "list", items: [
        "לחץ ועומס",
        "הטרדות ואלימות",
        "עבודה במשמרות"
      ]},

      { type: "h2", text: "עדכון סקר סיכונים" },
      { type: "p", text: "יש לעדכן את הסקר:" },
      { type: "list", items: [
        "לפחות פעם בשנה",
        "לאחר כל תאונה או אירוע חריג",
        "בשינוי תהליך או ציוד",
        "בכניסת עובדים חדשים",
        "בהתאם לדרישות רגולציה"
      ]},

      { type: "h2", text: "טיפים לניהול סיכונים אפקטיבי" },
      { type: "list", items: [
        "לערב עובדים בזיהוי סיכונים",
        "להתמקד בסיכונים הגבוהים ביותר",
        "לא לחפש פתרונות מושלמים - להתקדם",
        "לתעד הכל",
        "לעקוב ולמדוד"
      ]},

      { type: "h2", text: "סיכום" },
      { type: "p", text: "ניהול סיכונים הוא לא פעילות חד-פעמית אלא תהליך מתמשך. זיהוי, הערכה, טיפול ומעקב - המחזור הזה מבטיח סביבת עבודה בטוחה." },

      { type: "cta", text: "צריכים סיוע בסקר סיכונים? צרו קשר לייעוץ מקצועי." }
    ]
  },
  {
    id: 46,
    title: "תאונות עבודה - זכויות העובד ותהליך התביעה",
    slug: "work-accidents-employee-rights",
    excerpt: "מדריך מלא לעובד שנפגע בתאונת עבודה - הזכויות, הגורמים הרלוונטיים, והתהליך לקבלת פיצויים.",
    date: "2024-04-20",
    category: "רגולציה",
    metaTitle: "תאונות עבודה - זכויות העובד והתביעה | אדם בטיחות",
    metaDescription: "מדריך מלא לעובד שנפגע בתאונת עבודה - זכויות מול ביטוח לאומי, תביעות, פיצויים והליכים.",
    keywords: ["תאונת עבודה", "זכויות עובדים", "ביטוח לאומי", "פיצויים", "נכות מעבודה"],
    imageAlt: "עובד נפגע בתאונת עבודה",
    image: "/portfolio/warehouse.jpg",
    readTime: "12 דקות קריאה",
    content: [
      { type: "h2", text: "מהי תאונת עבודה?" },
      { type: "p", text: "תאונת עבודה מוגדרת כתאונה שאירעה תוך כדי ועקב העבודה, כולל בדרך לעבודה וממנה. ההכרה בתאונה כתאונת עבודה מזכה את העובד בזכויות מיוחדות." },

      { type: "h2", text: "מה נחשב תאונת עבודה?" },
      { type: "list", items: [
        "פגיעה במקום העבודה",
        "פגיעה בדרך לעבודה או ממנה",
        "פגיעה במהלך משימה מטעם העבודה",
        "מיקרוטראומה - פגיעה מתפתחת",
        "מחלת מקצוע"
      ]},

      { type: "h2", text: "הצעדים מיד לאחר התאונה" },

      { type: "h3", text: "צעד 1: טיפול רפואי" },
      { type: "list", items: [
        "לפנות לקבלת טיפול רפואי",
        "לציין לרופא שזו תאונת עבודה",
        "לשמור את כל המסמכים הרפואיים"
      ]},

      { type: "h3", text: "צעד 2: דיווח למעסיק" },
      { type: "list", items: [
        "לדווח למעסיק בהקדם האפשרי",
        "לוודא שהמעסיק מילא טופס BL 250",
        "לקבל העתק מהטופס"
      ]},

      { type: "h3", text: "צעד 3: פנייה לביטוח לאומי" },
      { type: "list", items: [
        "להגיש תביעה לביטוח לאומי תוך שנה",
        "לצרף את כל המסמכים הנדרשים",
        "להמתין לזימון לוועדה רפואית"
      ]},

      { type: "h2", text: "הגורמים המעורבים" },

      { type: "h3", text: "הביטוח הלאומי" },
      { type: "list", items: [
        "דמי פגיעה - פיצוי על ימי העבודה שהוחסרו",
        "גמלת נכות - אם נותרה נכות צמיתה",
        "טיפול רפואי - דרך קופת החולים"
      ]},

      { type: "h3", text: "קופת החולים" },
      { type: "list", items: [
        "טיפול רפואי שוטף",
        "שיקום",
        "מכשירים רפואיים",
        "תרופות"
      ]},

      { type: "h3", text: "המעסיק" },
      { type: "list", items: [
        "השלמת שכר (לפי הסכמים)",
        "ביטוח חבות מעבידים",
        "חובת דיווח לרשויות"
      ]},

      { type: "h2", text: "זכויות העובד" },

      { type: "h3", text: "דמי פגיעה" },
      { type: "list", items: [
        "75% מהשכר עד 91 יום",
        "משולם מהיום הראשון (אם היעדרות מעל 12 יום)",
        "תקרה של 5 פעמים השכר הממוצע במשק"
      ]},

      { type: "h3", text: "גמלת נכות" },
      { type: "list", items: [
        "קביעת אחוזי נכות בוועדה רפואית",
        "נכות זמנית או צמיתה",
        "קצבה חודשית או מענק חד-פעמי",
        "תוספת לנכות קשה"
      ]},

      { type: "h3", text: "זכויות נוספות" },
      { type: "list", items: [
        "הגנה מפיטורים בתקופת מחלה",
        "שיקום מקצועי",
        "התאמת מקום עבודה",
        "גמלת שאירים (במקרה מוות)"
      ]},

      { type: "h2", text: "תביעה נזיקית נגד המעסיק" },
      { type: "p", text: "בנוסף לביטוח לאומי, עובד יכול לתבוע את המעסיק על רשלנות:" },
      { type: "list", items: [
        "הוכחת אשמת המעסיק",
        "פיצוי על נזקים לא מכוסים",
        "כאב וסבל",
        "אובדן השתכרות עתידי",
        "הוצאות רפואיות"
      ]},

      { type: "h3", text: "חשוב לדעת" },
      { type: "list", items: [
        "התיישנות - 7 שנים מיום התאונה",
        "מומלץ להתייעץ עם עורך דין",
        "לאסוף הוכחות ועדויות"
      ]},

      { type: "h2", text: "ערעורים" },
      { type: "list", items: [
        "ערעור על החלטת הביטוח הלאומי - לבית הדין לעבודה",
        "ערעור על אחוזי נכות - לוועדה רפואית לעררים",
        "ערעור על ועדת עררים - לבית הדין לעבודה"
      ]},

      { type: "h2", text: "טיפים לעובד הנפגע" },
      { type: "list", items: [
        "לתעד הכל - תמונות, עדים, תאריכים",
        "לשמור כל מסמך רפואי",
        "לא לחתום על ויתור על זכויות",
        "להתייעץ עם גורם מקצועי",
        "לא לוותר - התהליך לוקח זמן"
      ]},

      { type: "h2", text: "סיכום" },
      { type: "p", text: "עובד שנפגע בתאונת עבודה זכאי להגנה ופיצוי. חשוב לדעת את הזכויות, לפעול מהר ולתעד הכל." },

      { type: "cta", text: "צריכים ייעוץ בנושא תאונת עבודה? צרו קשר." }
    ]
  },
  {
    id: 47,
    title: "בטיחות בבית - סיכונים נפוצים ומניעה",
    slug: "home-safety-risks-prevention",
    excerpt: "הבית נתפס כמקום בטוח, אך רבות מהתאונות מתרחשות דווקא בו. מדריך מקיף לזיהוי סיכונים ומניעת תאונות בבית.",
    date: "2024-04-20",
    category: "בית וסביבה",
    metaTitle: "בטיחות בבית - סיכונים נפוצים וטיפים למניעה | אדם בטיחות",
    metaDescription: "מדריך בטיחות בבית - נפילות, שריפות, הרעלות, חשמל ועוד. טיפים מעשיים להפיכת הבית למקום בטוח יותר.",
    keywords: ["בטיחות בבית", "תאונות בבית", "מניעת נפילות", "בטיחות ילדים", "בטיחות מבוגרים"],
    imageAlt: "משפחה בבית בטוח",
    image: "/portfolio/office-safety.jpg",
    readTime: "12 דקות קריאה",
    content: [
      { type: "h2", text: "למה בטיחות בבית חשובה?" },
      { type: "p", text: "כ-30% מהתאונות מתרחשות בבית. נפילות, כוויות, הרעלות וחנק הם הגורמים המובילים לפציעות ביתיות. מודעות ופעולות פשוטות יכולות למנוע את רובן." },

      { type: "h2", text: "נפילות בבית" },
      { type: "p", text: "נפילות הן הגורם המוביל לתאונות בבית, במיוחד בקרב ילדים וקשישים." },

      { type: "h3", text: "מניעת נפילות" },
      { type: "list", items: [
        "מעקות יציבים במדרגות ומרפסות",
        "שטיחים מונעי החלקה או קיבוע שטיחים",
        "תאורה מספקת במסדרונות ובמדרגות",
        "הסרת מכשולים ממעברים",
        "ידיות אחיזה בחדרי רחצה",
        "ניקוי מיידי של נוזלים שנשפכו"
      ]},

      { type: "h3", text: "בטיחות ילדים" },
      { type: "list", items: [
        "שערי בטיחות למדרגות",
        "נעילת חלונות גבוהים",
        "הרחקת רהיטים מחלונות",
        "רפידות למשטחים קשים"
      ]},

      { type: "h2", text: "בטיחות אש בבית" },

      { type: "h3", text: "מניעת שריפות" },
      { type: "list", items: [
        "גלאי עשן בכל קומה - בדיקה חודשית",
        "מטף כיבוי נגיש ותקין",
        "הרחקת חומרים דליקים ממקורות חום",
        "כיבוי מכשירי חשמל שאינם בשימוש",
        "לא להשאיר נרות דולקים ללא השגחה",
        "ניקוי קבוע של פילטר קולט האדים"
      ]},

      { type: "h3", text: "תוכנית מילוט" },
      { type: "list", items: [
        "זיהוי 2 דרכי יציאה מכל חדר",
        "תרגול עם המשפחה פעמיים בשנה",
        "נקודת מפגש מוסכמת מחוץ לבית",
        "סולמות מילוט לקומות גבוהות"
      ]},

      { type: "h2", text: "בטיחות חשמל" },

      { type: "h3", text: "מניעת סיכונים" },
      { type: "list", items: [
        "לא להעמיס שקעים",
        "החלפת כבלים פגומים",
        "שקעים מוגנים מילדים",
        "הרחקת מים ממכשירי חשמל",
        "בדיקה תקופתית של לוח חשמל",
        "התקנת מפסק פחת"
      ]},

      { type: "h2", text: "מניעת הרעלות" },

      { type: "h3", text: "אחסון נכון" },
      { type: "list", items: [
        "חומרי ניקוי במקום נעול",
        "תרופות במקום גבוה ונעול",
        "לא להעביר חומרים לבקבוקי שתייה",
        "סימון ברור של חומרים מסוכנים",
        "הרחקת צמחים רעילים מהישג יד"
      ]},

      { type: "h3", text: "מניעת הרעלת CO" },
      { type: "list", items: [
        "התקנת גלאי פחמן חד חמצני",
        "אוורור נאות בעת שימוש בגז",
        "תחזוקה שנתית של דוד גז",
        "לא להפעיל גנרטור בתוך הבית"
      ]},

      { type: "h2", text: "בטיחות במטבח" },
      { type: "list", items: [
        "הפניית ידיות סירים פנימה",
        "שימוש בכפפות תנור",
        "הרחקת ילדים מאזור הבישול",
        "אחסון סכינים במקום מוגן",
        "ניקוי מיידי של שמן שנשפך"
      ]},

      { type: "h2", text: "בטיחות בחדר רחצה" },
      { type: "list", items: [
        "משטח מונע החלקה באמבטיה",
        "ידיות אחיזה",
        "הגבלת טמפרטורת מים חמים",
        "לא להשאיר ילדים לבד במים",
        "מכשירי חשמל מרוחקים ממים"
      ]},

      { type: "h2", text: "בטיחות קשישים" },
      { type: "p", text: "התאמות מיוחדות לקשישים:" },
      { type: "list", items: [
        "לחצן מצוקה",
        "תאורה אוטומטית בלילה",
        "הסרת שטיחים קטנים",
        "רהיטים יציבים לתמיכה",
        "מדרגות מסומנות בצבע בולט",
        "טלפון נגיש בכל חדר"
      ]},

      { type: "h2", text: "רשימת בדיקה לבית בטוח" },
      { type: "list", items: [
        "גלאי עשן תקינים - פעם בחודש",
        "מטף כיבוי תקין - פעם בשנה",
        "תאורה מספקת בכל האזורים",
        "ידיות אחיזה בחדרי רחצה",
        "שקעים מוגנים מילדים",
        "חומרים מסוכנים נעולים",
        "אזורי עבודה מסודרים"
      ]},

      { type: "h2", text: "סיכום" },
      { type: "p", text: "בית בטוח דורש מודעות ותחזוקה שוטפת. השקעה קטנה בזמן ובאמצעי בטיחות יכולה למנוע תאונות ולהציל חיים." },

      { type: "cta", text: "רוצים ייעוץ בטיחות לבית? צרו קשר." }
    ]
  },
  {
    id: 48,
    title: "בטיחות בחגים ואירועים - מדריך מקיף",
    slug: "holiday-event-safety-guide",
    excerpt: "חגים ואירועים מביאים איתם שמחה, אך גם סיכוני בטיחות ייחודיים. מדריך לחגיגה בטוחה.",
    date: "2024-04-23",
    category: "בית וסביבה",
    metaTitle: "בטיחות בחגים ואירועים - טיפים לחגיגה בטוחה | אדם בטיחות",
    metaDescription: "מדריך בטיחות לחגים - פסח, יום העצמאות, חנוכה, פורים ועוד. טיפים למניעת שריפות, הרעלות ותאונות.",
    keywords: ["בטיחות בחגים", "בטיחות מדורה", "בטיחות נרות", "בטיחות זיקוקים", "בטיחות אירועים"],
    imageAlt: "אירוע חגיגי בטוח",
    image: "/portfolio/industrial-safety.jpg",
    readTime: "11 דקות קריאה",
    content: [
      { type: "h2", text: "בטיחות בחגי ישראל" },
      { type: "p", text: "לכל חג סיכוני בטיחות ייחודיים. הכרת הסיכונים והיערכות מראש מאפשרות חגיגה בטוחה ושמחה." },

      { type: "h2", text: "פסח" },

      { type: "h3", text: "ניקיון לפסח" },
      { type: "list", items: [
        "אוורור בעת שימוש בחומרי ניקוי",
        "לא לערבב חומרי ניקוי",
        "אחסון חומרים מסוכנים הרחק מילדים",
        "זהירות בעבודה על סולמות",
        "הרמת משאות בצורה נכונה"
      ]},

      { type: "h3", text: "ליל הסדר" },
      { type: "list", items: [
        "נרות במקום יציב ומרוחק מחומרים דליקים",
        "השגחה על ילדים ליד נרות",
        "מזון מאוחסן בטמפרטורה נכונה",
        "הימנעות מחנק - זהירות עם מצות לילדים קטנים"
      ]},

      { type: "h2", text: "יום העצמאות" },

      { type: "h3", text: "מדורות ומנגלים" },
      { type: "list", items: [
        "מיקום רחוק ממבנים וצמחייה",
        "דלי מים או מטף בקרבת מקום",
        "לא להשתמש בנוזל הצתה על אש דולקת",
        "השגחה מתמדת על האש",
        "כיבוי מלא לפני עזיבה"
      ]},

      { type: "h3", text: "זיקוקים" },
      { type: "list", items: [
        "אסור לקטינים - זה החוק",
        "רק זיקוקים מאושרים",
        "מרחק בטוח מאנשים ומבנים",
        "לא להחזיק זיקוק ביד",
        "לא לנסות להדליק זיקוק שלא נדלק"
      ]},

      { type: "h3", text: "טיולים והתכנסויות" },
      { type: "list", items: [
        "מים רבים ומגן שמש",
        "הימנעות מצפיפות יתר",
        "היכרות עם יציאות חירום באירועים",
        "נקודת מפגש מוסכמת למשפחות"
      ]},

      { type: "h2", text: "ל\"ג בעומר" },

      { type: "h3", text: "מדורות בטוחות" },
      { type: "list", items: [
        "מיקום מאושר בלבד",
        "מרחק מינימום 8 מטר ממבנים",
        "לא לשרוף צמיגים, פלסטיק או צבע",
        "מבוגר אחראי במקום",
        "ציוד עזרה ראשונה זמין",
        "כיבוי מלא לפני עזיבה"
      ]},

      { type: "h2", text: "חנוכה" },

      { type: "h3", text: "הדלקת נרות" },
      { type: "list", items: [
        "חנוכייה על משטח יציב ולא דליק",
        "הרחקה מוילונות וחומרים דליקים",
        "לא להזיז חנוכייה דולקת",
        "השגחה עד לכיבוי מוחלט",
        "שקול שימוש בחנוכייה חשמלית לילדים"
      ]},

      { type: "h3", text: "סופגניות ולביבות" },
      { type: "list", items: [
        "טיגון בשמן בזהירות",
        "לא למלא סיר יותר מחצי",
        "לא להשאיר שמן חם ללא השגחה",
        "מכסה סיר זמין לכיבוי אש שמן"
      ]},

      { type: "h2", text: "פורים" },

      { type: "h3", text: "תחפושות בטוחות" },
      { type: "list", items: [
        "בדים לא דליקים",
        "נראות טובה - לא לחסום שדה ראייה",
        "נעליים נוחות ובטוחות",
        "אביזרים ללא חלקים חדים",
        "פאות שלא מפריעות לראייה"
      ]},

      { type: "h3", text: "אלכוהול" },
      { type: "list", items: [
        "לא לקטינים",
        "צריכה מתונה למבוגרים",
        "לא לנהוג אחרי שתייה",
        "אכילה לפני ובמהלך שתייה"
      ]},

      { type: "h2", text: "סוכות" },

      { type: "h3", text: "בניית סוכה בטוחה" },
      { type: "list", items: [
        "קונסטרוקציה יציבה",
        "עמידות ברוח",
        "חיבורים בטוחים בלי ברגים בולטים",
        "קישוטים מוצמדים היטב"
      ]},

      { type: "h3", text: "חשמל בסוכה" },
      { type: "list", items: [
        "חיבור חשמלי תקני",
        "תאורה מוגנת ממים",
        "הרחקת כבלים ממים",
        "כיבוי חשמל בלילה"
      ]},

      { type: "h2", text: "בטיחות כללית באירועים" },
      { type: "list", items: [
        "היכרות עם יציאות חירום",
        "נקודת מפגש מוסכמת",
        "טלפון נייד טעון",
        "הימנעות מצפיפות יתר",
        "הקשבה להוראות מארגנים"
      ]},

      { type: "h2", text: "סיכום" },
      { type: "p", text: "חגים הם זמן של שמחה ומשפחתיות. הקפדה על כללי בטיחות בסיסיים תאפשר ליהנות מהחג בלב שלו." },

      { type: "cta", text: "צריכים ייעוץ בטיחות לאירועים? צרו קשר." }
    ]
  },
  {
    id: 49,
    title: "בטיחות בים ובבריכה - מדריך הצלה ומניעה",
    slug: "sea-pool-safety-drowning-prevention",
    excerpt: "טביעה היא אחד מגורמי המוות המובילים בקיץ. מדריך חיוני לבטיחות במים לכל המשפחה.",
    date: "2024-04-26",
    category: "בית וסביבה",
    metaTitle: "בטיחות בים ובבריכה - מניעת טביעה | אדם בטיחות",
    metaDescription: "מדריך בטיחות במים - כללי שחייה בטוחה, פיקוח על ילדים, זיהוי טובע והצלה. חיוני לקיץ הישראלי.",
    keywords: ["בטיחות בים", "בטיחות בבריכה", "מניעת טביעה", "הצלה", "שחייה בטוחה"],
    imageAlt: "בריכת שחייה עם מציל",
    image: "/portfolio/warehouse.jpg",
    readTime: "10 דקות קריאה",
    content: [
      { type: "h2", text: "הסכנה במספרים" },
      { type: "p", text: "כ-50 איש טובעים בישראל מדי שנה. טביעה היא גורם המוות המוביל בקרב ילדים בגילאי 1-4. רוב מקרי הטביעה ניתנים למניעה." },

      { type: "h2", text: "כללי בטיחות בסיסיים" },
      { type: "list", items: [
        "לשחות רק במקומות מוכרזים עם מציל",
        "לא לשחות לבד - תמיד עם חבר",
        "לציית להוראות המצילים ולדגלים",
        "לא להיכנס למים תחת השפעת אלכוהול",
        "לא לקפוץ למים רדודים או לא מוכרים"
      ]},

      { type: "h2", text: "פיקוח על ילדים" },

      { type: "h3", text: "כלל מרחק היד" },
      { type: "p", text: "ילד קטן צריך להיות תמיד במרחק יד ממבוגר אחראי. זה חל על בריכות, ים, ואפילו אמבטיה." },

      { type: "h3", text: "טיפים לפיקוח" },
      { type: "list", items: [
        "מבוגר ייעודי לפיקוח - לא טלפון, לא קריאה",
        "החלפת \"תורנות פיקוח\" בין מבוגרים",
        "לימוד שחייה מגיל צעיר",
        "אביזרי עזר אינם תחליף לפיקוח",
        "לא להסתמך על ילדים גדולים לשמור על קטנים"
      ]},

      { type: "h2", text: "בטיחות בבריכה פרטית" },
      { type: "list", items: [
        "גידור מסביב לבריכה - חובה חוקית",
        "שער נעילה עצמית",
        "כיסוי בריכה כשלא בשימוש",
        "הסרת צעצועים שמושכים ילדים",
        "ציוד הצלה זמין (מצוף, מוט)",
        "מספרי חירום מודבקים באזור הבריכה"
      ]},

      { type: "h2", text: "בטיחות בים" },

      { type: "h3", text: "משמעות הדגלים" },
      { type: "list", items: [
        "דגל לבן - רחצה מותרת",
        "דגל אדום - רחצה אסורה",
        "דגל שחור - חוף סגור, סכנת חיים"
      ]},

      { type: "h3", text: "סכנות בים" },
      { type: "list", items: [
        "זרמי רתע (ריפ-קרנט) - לשחות במקביל לחוף",
        "גלים גדולים - לא להפנות גב לים",
        "מדוזות - להתרחק מיד מהמים",
        "סלעים ושוניות - לא לקפוץ, לא לדרוך יחף"
      ]},

      { type: "h2", text: "זיהוי טובע" },
      { type: "p", text: "טביעה לא נראית כמו בסרטים. לרוב היא שקטה:" },
      { type: "list", items: [
        "ראש מוטה לאחור, פה פתוח",
        "עיניים זגוגיות או עצומות",
        "שיער על פנים",
        "לא יכול לקרוא לעזרה",
        "תנועות זרועות לצדדים (לא למעלה)",
        "נראה כמו מטפס בסולם"
      ]},

      { type: "h2", text: "מה לעשות אם רואים טובע" },
      { type: "list", items: [
        "לקרוא למציל מיד",
        "להתקשר ל-101",
        "לזרוק אמצעי ציפה (מצוף, כדור)",
        "להושיט מוט או חבל",
        "לא להיכנס למים אם לא מאומנים - אתם עלולים לטבוע גם"
      ]},

      { type: "h2", text: "החייאה" },
      { type: "p", text: "אם הוצאתם טובע מהמים:" },
      { type: "list", items: [
        "בדקו הכרה ונשימה",
        "התקשרו ל-101",
        "התחילו החייאה (CPR) מיד",
        "המשיכו עד הגעת עזרה"
      ]},

      { type: "h2", text: "לימוד שחייה" },
      { type: "p", text: "שחייה היא מיומנות מצילת חיים:" },
      { type: "list", items: [
        "להתחיל מגיל צעיר (מומלץ מגיל 4)",
        "קורסים מוכרים עם מדריכים מוסמכים",
        "תרגול קבוע לשמירה על הכישורים",
        "קורס הצלה מומלץ למבוגרים"
      ]},

      { type: "h2", text: "סיכום" },
      { type: "p", text: "בטיחות במים היא עניין של חיים ומוות. פיקוח צמוד, ציות לכללים ולימוד שחייה יכולים למנוע טרגדיות." },

      { type: "cta", text: "מעוניינים בהדרכת בטיחות מים לארגון? צרו קשר." }
    ]
  },
  {
    id: 50,
    title: "בטיחות בחשמל ביתי - מניעת התחשמלות ושריפות",
    slug: "home-electrical-safety-prevention",
    excerpt: "חשמל הוא חלק בלתי נפרד מחיינו, אך גם מקור לסכנות. מדריך מקיף לשימוש בטוח בחשמל בבית.",
    date: "2024-04-29",
    category: "בית וסביבה",
    metaTitle: "בטיחות בחשמל ביתי - מניעת התחשמלות ושריפות | אדם בטיחות",
    metaDescription: "מדריך בטיחות בחשמל - מניעת התחשמלות, שריפות חשמליות, בדיקת מערכות חשמל, ועוד טיפים חשובים.",
    keywords: ["בטיחות חשמל", "התחשמלות", "שריפות חשמל", "מפסק פחת", "חשמלאי מוסמך"],
    imageAlt: "לוח חשמל ביתי בטיחותי",
    image: "/assets/blog/home-electrical-safety.jpg",
    readTime: "11 דקות קריאה",
    content: [
      { type: "h2", text: "סיכוני חשמל בבית" },
      { type: "p", text: "חשמל גורם לכ-20% משריפות הבית ולמאות מקרי התחשמלות בשנה. הבנת הסיכונים ופעולות פשוטות יכולות למנוע אסונות." },

      { type: "h2", text: "התחשמלות" },

      { type: "h3", text: "גורמים עיקריים" },
      { type: "list", items: [
        "מגע עם חוטים חשופים",
        "מכשירים ליד מים",
        "שקעים פגומים",
        "תיקונים עצמאיים לא מקצועיים"
      ]},

      { type: "h3", text: "מניעה" },
      { type: "list", items: [
        "לא לגעת במכשירי חשמל עם ידיים רטובות",
        "לא להשתמש במכשירים ליד מים (מייבש שיער באמבטיה)",
        "לא לתקן מכשירים כשהם מחוברים לחשמל",
        "להחליף כבלים פגומים מיד",
        "לא להוציא תקע על ידי משיכת הכבל"
      ]},

      { type: "h2", text: "שריפות חשמל" },

      { type: "h3", text: "גורמים" },
      { type: "list", items: [
        "העמסת יתר על שקעים",
        "כבלים ישנים או פגומים",
        "מכשירים פגומים",
        "חום יתר ממכשירים",
        "התקנה לא תקנית"
      ]},

      { type: "h3", text: "סימני אזהרה" },
      { type: "list", items: [
        "שקע או מתג חם למגע",
        "ניצוצות בעת חיבור מכשיר",
        "ריח שריפה או פלסטיק",
        "נפילות חוזרות של מפסק",
        "האפלות או הבהובי אור"
      ]},

      { type: "h2", text: "מפסק פחת" },
      { type: "p", text: "מפסק פחת (RCD) הוא מכשיר מציל חיים שמנתק את החשמל כשמזהה זליגה." },
      { type: "list", items: [
        "חובה בכל לוח חשמל חדש",
        "לבדוק פעם בחודש בלחיצה על כפתור הבדיקה",
        "אם לא מנתק - לקרוא לחשמלאי",
        "לא לעקוף או לבטל אותו"
      ]},

      { type: "h2", text: "העמסת יתר" },

      { type: "h3", text: "מה לא לעשות" },
      { type: "list", items: [
        "לא לחבר מפצלים בשרשרת",
        "לא להעמיס מכשירים צורכי אנרגיה רבים על שקע אחד",
        "לא להשתמש בכבלים מאריכים כפתרון קבוע"
      ]},

      { type: "h3", text: "מה לעשות" },
      { type: "list", items: [
        "להשתמש במפצלים עם הגנת עומס",
        "לבדוק הספק מקסימלי של מפצל",
        "להוסיף שקעים על ידי חשמלאי מוסמך"
      ]},

      { type: "h2", text: "בטיחות ילדים" },
      { type: "list", items: [
        "מגיני שקעים (תקע בטיחות)",
        "לא להשאיר כבלים נגישים",
        "הסבר על סכנות החשמל מגיל צעיר",
        "מכשירים חשמליים מרוחקים מהישג יד"
      ]},

      { type: "h2", text: "עבודות חשמל" },
      { type: "list", items: [
        "רק חשמלאי מוסמך מורשה לבצע עבודות",
        "לבקש אישור טופס 4 לעבודות חדשות",
        "לבדוק רישיון של הקבלן",
        "לא לבצע עבודות חשמל עצמאיות - מסוכן ולא חוקי"
      ]},

      { type: "h2", text: "בדיקות תקופתיות" },
      { type: "list", items: [
        "בדיקת מפסק פחת - חודשית",
        "בדיקת שקעים ומתגים - שנתית",
        "בדיקת מערכת החשמל על ידי חשמלאי - כל 5 שנים",
        "בדיקת מכשירים ניידים - לפי צורך"
      ]},

      { type: "h2", text: "מה לעשות במקרה התחשמלות" },
      { type: "list", items: [
        "לנתק את מקור החשמל (לא לגעת בנפגע!)",
        "להזעיק 101",
        "אם הנפגע לא נושם - החייאה",
        "גם אם נראה בסדר - לפנות לבדיקה רפואית"
      ]},

      { type: "h2", text: "מה לעשות בשריפה חשמלית" },
      { type: "list", items: [
        "לנתק חשמל מיד (אם בטוח)",
        "לא לכבות במים - סכנת התחשמלות",
        "להשתמש במטף CO2 או אבקה",
        "לפנות את המקום ולהזעיק כבאות"
      ]},

      { type: "h2", text: "סיכום" },
      { type: "p", text: "בטיחות בחשמל מתחילה במודעות. שימוש נכון, תחזוקה שוטפת ופנייה למקצוענים יכולים למנוע התחשמלות ושריפות." },

      { type: "cta", text: "צריכים בדיקת בטיחות חשמל? צרו קשר." }
    ]
  },
  {
    id: 51,
    title: "בטיחות בעבודה על סולמות - מניעת נפילות",
    slug: "ladder-safety-fall-prevention",
    excerpt: "נפילות מסולמות הן אחת הסיבות הנפוצות לפציעות בעבודה ובבית. מדריך לשימוש בטוח בסולמות.",
    date: "2024-05-02",
    category: "מדריכים",
    metaTitle: "בטיחות בסולמות - מניעת נפילות וכללי שימוש | אדם בטיחות",
    metaDescription: "מדריך בטיחות לעבודה על סולמות - בחירת סולם מתאים, הצבה נכונה, כללי עלייה וירידה, ומניעת תאונות.",
    keywords: ["בטיחות סולמות", "נפילות מגובה", "סולם בטיחותי", "עבודה בגובה", "מניעת נפילות"],
    imageAlt: "עובד משתמש בסולם בצורה בטוחה",
    image: "/portfolio/construction-site.jpg",
    readTime: "9 דקות קריאה",
    content: [
      { type: "h2", text: "הסטטיסטיקה המדאיגה" },
      { type: "p", text: "כ-20% מפציעות העבודה קשורות לנפילות, ומתוכן חלק משמעותי מסולמות. רוב התאונות ניתנות למניעה באמצעות שימוש נכון." },

      { type: "h2", text: "בחירת סולם מתאים" },

      { type: "h3", text: "סוגי סולמות" },
      { type: "list", items: [
        "סולם יחיד (נשען) - לעבודות קצרות",
        "סולם כפול (A) - עמידה יציבה יותר",
        "סולם מתקפל - רב שימושי",
        "סולם טלסקופי - קל לאחסון",
        "סולם משולב - מתאים למגוון עבודות"
      ]},

      { type: "h3", text: "קריטריונים לבחירה" },
      { type: "list", items: [
        "גובה העבודה הנדרש",
        "משקל העובד + כלים",
        "סוג המשטח (חלק, לא ישר)",
        "עבודה עם חשמל - סולם מבודד",
        "תקן ישראלי (ת\"י 1847)"
      ]},

      { type: "h2", text: "בדיקה לפני שימוש" },
      { type: "list", items: [
        "שלבים שלמים ויציבים",
        "רגליות תקינות ולא שחוקות",
        "מנגנון נעילה תקין (בסולם כפול)",
        "ללא סדקים, קורוזיה או עיוותים",
        "מדבקות אזהרה קריאות"
      ]},

      { type: "h2", text: "הצבת סולם נשען" },

      { type: "h3", text: "כלל 4:1" },
      { type: "p", text: "על כל 4 מטרים גובה - מטר אחד מרחק מהקיר. זווית אידיאלית: 75 מעלות." },

      { type: "h3", text: "כללים נוספים" },
      { type: "list", items: [
        "משטח יציב ושטוח",
        "נעילה או קיבוע בבסיס",
        "הארכה מעל נקודת היציאה (מטר לפחות)",
        "לא על דלתות או משטחים זזים",
        "לא על קופסאות או חביות"
      ]},

      { type: "h2", text: "כללי עלייה וירידה" },
      { type: "list", items: [
        "פנים לסולם בכל עת",
        "3 נקודות מגע תמיד (2 ידיים + רגל או 2 רגליים + יד)",
        "לא לקפוץ מהסולם",
        "להימנע מהגעה רחוקה לצדדים",
        "נעליים מתאימות (לא סנדלים)",
        "לא לעלות עם ידיים עמוסות"
      ]},

      { type: "h2", text: "מה אסור לעשות" },
      { type: "list", items: [
        "לעמוד על 3 השלבים העליונים",
        "להזיז סולם כשמישהו עליו",
        "להשתמש בסולם פגום",
        "לעבוד בתנאי מזג אוויר קיצוניים",
        "לשים סולם מול דלת ללא נעילה",
        "לחבר שני סולמות יחד"
      ]},

      { type: "h2", text: "עבודה מסולם כפול" },
      { type: "list", items: [
        "לפתוח את הסולם עד הסוף",
        "לוודא שמנגנון הנעילה תפוס",
        "לא לעמוד על המדף העליון",
        "לא להתרחק מציר הסולם"
      ]},

      { type: "h2", text: "אחסון ותחזוקה" },
      { type: "list", items: [
        "אחסון במקום יבש",
        "תלייה אופקית או אנכית יציבה",
        "לא להשאיר בשמש ממושכת",
        "בדיקה תקופתית ותיעוד",
        "סימון סולם פגום והוצאה משימוש"
      ]},

      { type: "h2", text: "חלופות לסולם" },
      { type: "p", text: "לעתים עדיף להשתמש באמצעי אחר:" },
      { type: "list", items: [
        "פיגום נייד - לעבודה ממושכת",
        "במה מתרוממת - לגובה משתנה",
        "סל הרמה - לעבודה בגובה רב"
      ]},

      { type: "h2", text: "סיכום" },
      { type: "p", text: "סולם הוא כלי נפוץ ושימושי, אך דורש זהירות. בחירה נכונה, הצבה בטוחה ושימוש זהיר ימנעו תאונות." },

      { type: "cta", text: "צריכים הדרכת בטיחות בעבודה בגובה? צרו קשר." }
    ]
  },
  {
    id: 52,
    title: "בטיחות בטיולים וחופשות - מדריך מקיף",
    slug: "travel-vacation-safety-guide",
    excerpt: "טיול משפחתי או חופשה בחו\"ל? מדריך בטיחות מקיף להנאה ללא דאגות.",
    date: "2024-05-05",
    category: "בית וסביבה",
    metaTitle: "בטיחות בטיולים וחופשות - מדריך לנסיעה בטוחה | אדם בטיחות",
    metaDescription: "מדריך בטיחות לטיולים - הכנה לטיול, בטיחות בדרכים, טיולי שטח, ונסיעות לחו\"ל. טיפים חיוניים לכל מטייל.",
    keywords: ["בטיחות בטיולים", "בטיחות בחופשה", "טיול משפחתי", "טיולי שטח", "נסיעות לחול"],
    imageAlt: "משפחה בטיול בטוח בטבע",
    image: "/portfolio/warehouse.jpg",
    readTime: "12 דקות קריאה",
    content: [
      { type: "h2", text: "הכנה לטיול" },

      { type: "h3", text: "תכנון מראש" },
      { type: "list", items: [
        "לבדוק מזג אוויר צפוי",
        "להכיר את המסלול ורמת הקושי",
        "לעדכן מישהו על התוכניות",
        "לבדוק זמינות של עזרה ראשונה",
        "להכין תוכנית גיבוי"
      ]},

      { type: "h3", text: "ציוד חיוני" },
      { type: "list", items: [
        "מים מספיקים (4 ליטר לאדם ביום חם)",
        "אוכל ונשנושים אנרגטיים",
        "ערכת עזרה ראשונה",
        "מגן שמש וכובע",
        "טלפון טעון ומפת שטח",
        "פנס ובגדים חמים (גם בקיץ)"
      ]},

      { type: "h2", text: "בטיחות בטיולי רגל" },

      { type: "h3", text: "כללים בסיסיים" },
      { type: "list", items: [
        "ללכת בשביל המסומן",
        "לא ללכת לבד באזורים מבודדים",
        "לצאת מוקדם ולחזור לפני חשיכה",
        "להתאים קצב לחוליה החלשה",
        "לשתות לפני שמרגישים צמא"
      ]},

      { type: "h3", text: "סיכונים נפוצים" },
      { type: "list", items: [
        "התייבשות ומכת חום",
        "נקעים ושברים",
        "הלם קור בלילה",
        "נחשים ועקרבים",
        "שיטפונות בנחלים"
      ]},

      { type: "h2", text: "בטיחות בנהרות ובמדבר" },

      { type: "h3", text: "טיול בנחלים" },
      { type: "list", items: [
        "לבדוק תחזית גשם - גם רחוק מהמסלול",
        "להכיר נקודות מילוט מהנחל",
        "לא להיכנס לנחל בזרימה",
        "לא לקפוץ לגבי מים לא מוכרים"
      ]},

      { type: "h3", text: "טיול במדבר" },
      { type: "list", items: [
        "מים כפולים מהרגיל",
        "לטייל בשעות הבוקר המוקדמות",
        "כיסוי גוף מפני שמש",
        "לדעת לזהות סימני התייבשות"
      ]},

      { type: "h2", text: "בטיחות בכביש" },

      { type: "h3", text: "נהיגה בטיולים" },
      { type: "list", items: [
        "מנוחה מספקת לפני נסיעה ארוכה",
        "עצירות כל שעתיים",
        "לא לנהוג בעייפות",
        "התאמת מהירות לתנאי הדרך",
        "בדיקת רכב לפני יציאה"
      ]},

      { type: "h3", text: "נסיעה עם ילדים" },
      { type: "list", items: [
        "מושבי בטיחות מותאמים לגיל ומשקל",
        "עצירות תכופות יותר",
        "שתייה ונשנושים",
        "פעילות לילדים בנסיעה",
        "לא להשאיר ילדים ברכב לבד"
      ]},

      { type: "h2", text: "טיולים בחו\"ל" },

      { type: "h3", text: "הכנה" },
      { type: "list", items: [
        "לבדוק התרעות נסיעה במשרד החוץ",
        "ביטוח נסיעות מקיף",
        "חיסונים נדרשים",
        "להעתיק מסמכים חשובים",
        "לדעת מספרי חירום במקום"
      ]},

      { type: "h3", text: "בזמן השהות" },
      { type: "list", items: [
        "לשמור על מודעות סביבתית",
        "לא להציג עושר",
        "לשמור דרכון במקום בטוח",
        "להימנע ממקומות מסוכנים",
        "לשתות מים בבקבוקים סגורים"
      ]},

      { type: "h2", text: "עזרה ראשונה בשטח" },

      { type: "h3", text: "ערכה בסיסית" },
      { type: "list", items: [
        "תחבושות ופלסטרים",
        "חומר חיטוי",
        "תחבושת לחץ",
        "משולש לקיבוע",
        "משככי כאבים",
        "תרופות אישיות"
      ]},

      { type: "h3", text: "מצבי חירום" },
      { type: "list", items: [
        "התייבשות - הצללה, שתייה, קירור",
        "נקע - קרח, קיבוע, הנחה",
        "עקיצת נחש - לא לחתוך, לא למצוץ, לפנות לטיפול",
        "שבר - לא להזיז, לקבע, לפנות"
      ]},

      { type: "h2", text: "מספרי חירום" },
      { type: "list", items: [
        "מד\"א: 101",
        "משטרה: 100",
        "יחידת חילוץ ארצית: *3633",
        "שירות מטאורולוגי: *6681"
      ]},

      { type: "h2", text: "סיכום" },
      { type: "p", text: "טיול בטוח הוא טיול מהנה. הכנה נכונה, ציוד מתאים וזהירות בדרך יבטיחו חזרה בריאה ושלמה." },

      { type: "cta", text: "מתכננים טיול ארגוני? צרו קשר לייעוץ בטיחות." }
    ]
  },
  {
    id: 53,
    title: "בטיחות בספורט ופעילות גופנית",
    slug: "sports-physical-activity-safety",
    excerpt: "פעילות גופנית חיונית לבריאות, אך טומנת סיכונים. מדריך לפעילות ספורטיבית בטוחה.",
    date: "2024-05-08",
    category: "בית וסביבה",
    metaTitle: "בטיחות בספורט - מניעת פציעות בפעילות גופנית | אדם בטיחות",
    metaDescription: "מדריך בטיחות בספורט - חימום, ציוד מגן, מניעת פציעות, ופעילות בטוחה לכל הגילאים.",
    keywords: ["בטיחות בספורט", "מניעת פציעות ספורט", "ציוד מגן ספורט", "פעילות גופנית בטוחה"],
    imageAlt: "ספורטאים עם ציוד מגן",
    image: "/portfolio/factory-worker.jpg",
    readTime: "10 דקות קריאה",
    content: [
      { type: "h2", text: "חשיבות הפעילות הגופנית" },
      { type: "p", text: "פעילות גופנית סדירה מפחיתה סיכון למחלות, משפרת מצב רוח ומעלה איכות חיים. אך כדי ליהנות מהיתרונות, יש לפעול בבטיחות." },

      { type: "h2", text: "עקרונות בסיסיים" },

      { type: "h3", text: "חימום וקירור" },
      { type: "list", items: [
        "5-10 דקות חימום לפני כל פעילות",
        "העלאה הדרגתית של קצב הלב",
        "מתיחות דינמיות בחימום",
        "5-10 דקות קירור בסיום",
        "מתיחות סטטיות אחרי האימון"
      ]},

      { type: "h3", text: "עקרון ההדרגתיות" },
      { type: "list", items: [
        "להתחיל בעצימות נמוכה",
        "להעלות עומס לאט לאט",
        "לא יותר מ-10% עלייה בשבוע",
        "להקשיב לגוף",
        "לתת זמן להתאוששות"
      ]},

      { type: "h2", text: "ציוד מגן לפי ענף" },

      { type: "h3", text: "רכיבה על אופניים" },
      { type: "list", items: [
        "קסדה - חובה!",
        "כפפות",
        "משקפי מגן",
        "בגדים בולטים או מחזירי אור",
        "נעליים מתאימות"
      ]},

      { type: "h3", text: "ריצה" },
      { type: "list", items: [
        "נעלי ריצה מתאימות",
        "בגדים נושמים",
        "מחזירי אור בחשיכה",
        "הגנה מהשמש"
      ]},

      { type: "h3", text: "משחקי כדור" },
      { type: "list", items: [
        "נעליים מתאימות למגרש",
        "מגני שוקיים (כדורגל)",
        "מגני ברכיים ומרפקים (כדורסל, כדורעף)",
        "מגן שיניים (ספורט מגע)"
      ]},

      { type: "h2", text: "מניעת פציעות נפוצות" },

      { type: "h3", text: "נקעים ומתיחות" },
      { type: "list", items: [
        "חימום יסודי",
        "חיזוק שרירים סביב המפרקים",
        "נעליים יציבות",
        "הימנעות מאימון יתר"
      ]},

      { type: "h3", text: "שברי מאמץ" },
      { type: "list", items: [
        "עלייה הדרגתית בעומס",
        "תזונה עשירה בסידן וויטמין D",
        "מנוחה מספקת",
        "הקשבה לכאב מתמשך"
      ]},

      { type: "h3", text: "פציעות בברך" },
      { type: "list", items: [
        "חיזוק שרירי הירך",
        "אימון שיווי משקל",
        "טכניקה נכונה בקפיצה ונחיתה",
        "הימנעות מעומס יתר"
      ]},

      { type: "h2", text: "פעילות במזג אוויר קיצוני" },

      { type: "h3", text: "בחום" },
      { type: "list", items: [
        "להתאמן בשעות הבוקר או הערב",
        "לשתות הרבה לפני, במהלך ואחרי",
        "לובשים בגדים בהירים ונושמים",
        "להכיר סימני מכת חום",
        "להפסיק אם מרגישים לא טוב"
      ]},

      { type: "h3", text: "בקור" },
      { type: "list", items: [
        "לבוש שכבות",
        "חימום ממושך יותר",
        "הגנה על אוזניים וידיים",
        "שתייה גם בקור"
      ]},

      { type: "h2", text: "פעילות לפי גיל" },

      { type: "h3", text: "ילדים ונוער" },
      { type: "list", items: [
        "דגש על הנאה ופיתוח מיומנויות",
        "הימנעות מהתמחות מוקדמת",
        "ציוד מגן מותאם גודל",
        "פיקוח מבוגרים"
      ]},

      { type: "h3", text: "מבוגרים (60+)" },
      { type: "list", items: [
        "התייעצות עם רופא לפני התחלה",
        "פעילות מותאמת ליכולת",
        "דגש על שיווי משקל וגמישות",
        "הימנעות מפעילות עם סיכון נפילה גבוה"
      ]},

      { type: "h2", text: "מתי להפסיק" },
      { type: "list", items: [
        "כאב חד או חזק",
        "קוצר נשימה חריג",
        "סחרחורת או בחילה",
        "דופק לב לא סדיר",
        "כאבים בחזה",
        "חולשה פתאומית"
      ]},

      { type: "h2", text: "סיכום" },
      { type: "p", text: "ספורט בריא הוא ספורט בטוח. חימום, ציוד מתאים, הדרגתיות והקשבה לגוף יבטיחו פעילות מהנה ונטולת פציעות." },

      { type: "cta", text: "צריכים הדרכת בטיחות לקבוצת ספורט? צרו קשר." }
    ]
  },
  {
    id: 54,
    title: "בטיחות בנהיגה - מניעת תאונות דרכים",
    slug: "driving-safety-accident-prevention",
    excerpt: "תאונות דרכים הן גורם המוות המוביל בקרב צעירים. מדריך לנהיגה בטוחה ומניעת תאונות.",
    date: "2024-05-11",
    category: "בית וסביבה",
    metaTitle: "בטיחות בנהיגה - טיפים למניעת תאונות דרכים | אדם בטיחות",
    metaDescription: "מדריך בטיחות בכביש - נהיגה מותאמת, הימנעות מהסחות דעת, נהיגה בתנאים קשים, ועוד טיפים מצילי חיים.",
    keywords: ["בטיחות בדרכים", "נהיגה בטוחה", "מניעת תאונות", "נהיגה ברשלנות", "תאונות דרכים"],
    imageAlt: "נהג זהיר בכביש",
    image: "/portfolio/industrial-safety.jpg",
    readTime: "11 דקות קריאה",
    content: [
      { type: "h2", text: "הסטטיסטיקה בישראל" },
      { type: "p", text: "כ-300 הרוגים ואלפי פצועים מדי שנה בתאונות דרכים. מרבית התאונות נגרמות מטעויות אנוש שניתן למנוע." },

      { type: "h2", text: "גורמי סיכון עיקריים" },

      { type: "h3", text: "מהירות" },
      { type: "list", items: [
        "מהירות מופרזת - גורם מוביל לתאונות קטלניות",
        "מהירות לא מותאמת לתנאים",
        "על כל 10 קמ\"ש מעל המותר - עלייה ב-50% בסיכון לתאונה"
      ]},

      { type: "h3", text: "הסחות דעת" },
      { type: "list", items: [
        "טלפון נייד - הסחה ויזואלית, קוגניטיבית ופיזית",
        "אכילה ושתייה",
        "התעסקות עם ניווט או רדיו",
        "שיחה עם נוסעים",
        "עייפות"
      ]},

      { type: "h3", text: "אלכוהול וסמים" },
      { type: "list", items: [
        "אפס אלכוהול לנהגים חדשים",
        "עד 0.05 פרומיל לנהגים ותיקים",
        "סמים - אפס סובלנות",
        "תרופות - לבדוק אם משפיעות על נהיגה"
      ]},

      { type: "h2", text: "עקרונות נהיגה בטוחה" },

      { type: "h3", text: "ריכוז בנהיגה" },
      { type: "list", items: [
        "100% ריכוז בכביש",
        "טלפון במצב שקט או \"נהיגה\"",
        "לעצור לצד הדרך אם צריך לענות",
        "תכנות ניווט לפני היציאה"
      ]},

      { type: "h3", text: "מרחק בטיחות" },
      { type: "list", items: [
        "כלל 2 השניות - מרחק מהרכב שלפניכם",
        "4 שניות בגשם או בחשיכה",
        "יותר מאחורי רכב כבד"
      ]},

      { type: "h3", text: "נהיגה מגננתית" },
      { type: "list", items: [
        "לצפות לטעויות של אחרים",
        "לא להניח שיפנו לכם זכות קדימה",
        "להיות מוכנים לעצור",
        "להיזהר במיוחד בצמתים"
      ]},

      { type: "h2", text: "נהיגה בתנאים קשים" },

      { type: "h3", text: "גשם" },
      { type: "list", items: [
        "להאט משמעותית",
        "להגביר מרחקים",
        "להימנע מבלימות חדות",
        "להיזהר מהצפות",
        "להדליק אורות"
      ]},

      { type: "h3", text: "לילה" },
      { type: "list", items: [
        "לוודא שהאורות תקינים",
        "לא להסתנוור מאור גבוה",
        "להאט במקומות לא מוארים",
        "להיזהר מהולכי רגל והולכי אופניים"
      ]},

      { type: "h3", text: "ערפל" },
      { type: "list", items: [
        "אורות ערפל (לא גבוהים)",
        "להאט משמעותית",
        "לשמור מרחק גדול",
        "לעצור אם הראות אפסית"
      ]},

      { type: "h2", text: "חגורות בטיחות" },
      { type: "list", items: [
        "חובה על כל הנוסעים",
        "מפחיתה סיכון למוות ב-50%",
        "חגורה נכונה - על הכתף ועל האגן",
        "נשים בהריון - חגורה מתחת לבטן"
      ]},

      { type: "h2", text: "מושבי ילדים" },
      { type: "list", items: [
        "סל קל (0-9 ק\"ג) - פונה לאחור",
        "מושב תינוק (עד 13 ק\"ג) - פונה לאחור",
        "מושב ילד (9-36 ק\"ג) - פונה קדימה",
        "בוסטר (15-36 ק\"ג) - עד גיל 8 לפחות",
        "ילדים מתחת לגיל 3 - אסור במושב קדמי"
      ]},

      { type: "h2", text: "תחזוקת רכב" },
      { type: "list", items: [
        "בדיקת צמיגים - לחץ ומצב",
        "בדיקת בלמים",
        "תאורה תקינה",
        "מגבי שמשה",
        "רמת שמנים ונוזלים",
        "טסט שנתי"
      ]},

      { type: "h2", text: "מה לעשות בתאונה" },
      { type: "list", items: [
        "לעצור בצד הדרך בבטחה",
        "להדליק אורות חירום",
        "להתקשר ל-100 או 101",
        "לא להזיז פצועים (אלא בסכנה מיידית)",
        "לתעד את הזירה",
        "להחליף פרטים עם הצד השני"
      ]},

      { type: "h2", text: "סיכום" },
      { type: "p", text: "נהיגה בטוחה היא בחירה. ריכוז, מהירות מותאמת וזהירות יכולים להציל את חייכם ואת חיי אחרים." },

      { type: "cta", text: "מעוניינים בהדרכת נהיגה בטוחה לארגון? צרו קשר." }
    ]
  },
  {
    id: 55,
    title: "עזרה ראשונה - מדריך בסיסי להצלת חיים",
    slug: "first-aid-basic-life-saving-guide",
    excerpt: "ידע בעזרה ראשונה יכול להציל חיים. מדריך בסיסי לטיפול במצבי חירום נפוצים.",
    date: "2024-05-14",
    category: "מדריכים",
    metaTitle: "עזרה ראשונה - מדריך בסיסי להצלת חיים | אדם בטיחות",
    metaDescription: "מדריך עזרה ראשונה - החייאה, חנק, דימום, שבר, כוויות ועוד. ידע בסיסי שכל אחד צריך.",
    keywords: ["עזרה ראשונה", "החייאה", "CPR", "חנק", "דימום", "טיפול חירום"],
    imageAlt: "ערכת עזרה ראשונה",
    image: "/portfolio/office-safety.jpg",
    readTime: "13 דקות קריאה",
    content: [
      { type: "h2", text: "למה עזרה ראשונה חשובה?" },
      { type: "p", text: "הדקות הראשונות קריטיות. טיפול מהיר ונכון יכול להציל חיים, למנוע החמרה ולהקל על הפצוע עד הגעת עזרה מקצועית." },

      { type: "h2", text: "עקרונות בסיסיים" },
      { type: "list", items: [
        "קודם כל - להזעיק עזרה (101)",
        "לוודא שאתם בבטחה - לא להפוך לנפגעים",
        "להישאר רגועים",
        "לא לעשות יותר ממה שיודעים",
        "לא לתת אוכל או שתייה לנפגע"
      ]},

      { type: "h2", text: "בדיקת נפגע" },
      { type: "list", items: [
        "לבדוק הכרה - לקרוא ולנער קלות",
        "לבדוק נשימה - להסתכל, להקשיב, להרגיש",
        "אם לא מגיב ולא נושם - להתחיל החייאה"
      ]},

      { type: "h2", text: "החייאה (CPR)" },

      { type: "h3", text: "למבוגרים" },
      { type: "list", items: [
        "לשכב על משטח קשה",
        "ידיים במרכז החזה",
        "לחיצות: 30 לחיצות, עומק 5 ס\"מ, קצב 100-120 לדקה",
        "נשימות: 2 נשימות הנשמה",
        "להמשיך 30:2 עד הגעת עזרה או החייאה"
      ]},

      { type: "h3", text: "לילדים ותינוקות" },
      { type: "p", text: "עקרונות דומים עם התאמות:" },
      { type: "list", items: [
        "ילדים - יד אחת, עומק 5 ס\"מ",
        "תינוקות - 2 אצבעות, עומק 4 ס\"מ",
        "להתחיל עם 5 נשימות הנשמה"
      ]},

      { type: "h2", text: "חנק" },

      { type: "h3", text: "סימנים" },
      { type: "list", items: [
        "אחיזה בגרון",
        "לא יכול לדבר או לנשום",
        "הכחלה",
        "איבוד הכרה"
      ]},

      { type: "h3", text: "טיפול במבוגר" },
      { type: "list", items: [
        "לעודד להשתעל אם יכול",
        "5 מכות בין השכמות",
        "5 לחיצות בטן (תמרון היימליך)",
        "לחזור עד שהחפץ יוצא או איבוד הכרה"
      ]},

      { type: "h3", text: "טיפול בתינוק" },
      { type: "list", items: [
        "תינוק על הזרוע, פנים למטה",
        "5 מכות בין השכמות",
        "להפוך - 5 לחיצות חזה",
        "לא תמרון היימליך לתינוק!"
      ]},

      { type: "h2", text: "דימום" },

      { type: "h3", text: "דימום חיצוני" },
      { type: "list", items: [
        "לחיצה ישירה על הפצע",
        "להרים את האיבר מעל הלב",
        "תחבושת לחץ",
        "אם לא נעצר - להוסיף תחבושת, לא להסיר"
      ]},

      { type: "h3", text: "דימום חמור" },
      { type: "list", items: [
        "חוסם עורקים (טורניקט) - רק במקרה קיצוני",
        "לרשום שעת ההנחה",
        "לפנות מיד לבית חולים"
      ]},

      { type: "h2", text: "שברים ופריקות" },
      { type: "list", items: [
        "לא להזיז את האיבר",
        "לקבע במצב שנמצא",
        "קרח עטוף בבד",
        "להרים אם אפשר",
        "לפנות לטיפול"
      ]},

      { type: "h2", text: "כוויות" },

      { type: "h3", text: "טיפול ראשוני" },
      { type: "list", items: [
        "לקרר במים זורמים 10-20 דקות",
        "להסיר תכשיטים ובגדים (לא אם נדבקו)",
        "לכסות בתחבושת נקייה",
        "לא קרח ישירות!",
        "לא משחות או חמאה!"
      ]},

      { type: "h3", text: "מתי לפנות מיד" },
      { type: "list", items: [
        "כוויה בפנים, ידיים, איברי מין",
        "כוויה גדולה (יותר מכף יד)",
        "כוויה מחשמל או כימיקלים",
        "קושי בנשימה"
      ]},

      { type: "h2", text: "הלם" },
      { type: "p", text: "סימנים: חיוורון, זיעה קרה, דופק מהיר וחלש, בלבול" },
      { type: "list", items: [
        "לשכב עם רגליים מורמות",
        "לכסות בשמיכה",
        "לא לתת לשתות",
        "להזעיק עזרה מיד"
      ]},

      { type: "h2", text: "התקף לב" },
      { type: "p", text: "סימנים: כאב בחזה, קוצר נשימה, כאב בזרוע שמאל, זיעה קרה" },
      { type: "list", items: [
        "להזעיק 101 מיד",
        "לתת אספירין ללעוס (אם לא אלרגי)",
        "לישב בתנוחה נוחה",
        "להרגיע",
        "להתכונן להחייאה"
      ]},

      { type: "h2", text: "שבץ מוחי" },
      { type: "p", text: "זיהוי מהיר - FAST:" },
      { type: "list", items: [
        "Face - צניחת פנים",
        "Arms - חולשה בזרוע",
        "Speech - דיבור מטושטש",
        "Time - להזעיק עזרה מיד!"
      ]},

      { type: "h2", text: "ערכת עזרה ראשונה" },
      { type: "list", items: [
        "תחבושות בגדלים שונים",
        "פלסטרים",
        "חומר חיטוי",
        "תחבושת לחץ",
        "משולש",
        "מספריים",
        "כפפות חד פעמיות",
        "מסכת הנשמה"
      ]},

      { type: "h2", text: "סיכום" },
      { type: "p", text: "ידע בסיסי בעזרה ראשונה יכול להציל חיים. מומלץ לעבור קורס עזרה ראשונה מוסמך ולרענן את הידע מדי שנה." },

      { type: "cta", text: "מעוניינים בקורס עזרה ראשונה לארגון? צרו קשר." }
    ]
  },
  {
    id: 56,
    title: "בטיחות בגינה ובחצר - סיכונים ומניעה",
    slug: "garden-yard-safety-prevention",
    excerpt: "עבודה בגינה ובחצר כוללת סיכונים רבים. מדריך לעבודה בטוחה בסביבה הביתית.",
    date: "2024-05-17",
    category: "בית וסביבה",
    metaTitle: "בטיחות בגינה ובחצר - מניעת תאונות | אדם בטיחות",
    metaDescription: "מדריך בטיחות לעבודה בגינה - כלי עבודה, מכסחות דשא, סולמות, חומרי הדברה ועוד טיפים חשובים.",
    keywords: ["בטיחות בגינה", "עבודה בחצר", "מכסחת דשא", "כלי גינון", "הדברה ביתית"],
    imageAlt: "עבודה בטוחה בגינה",
    image: "/portfolio/construction-site.jpg",
    readTime: "10 דקות קריאה",
    content: [
      { type: "h2", text: "סיכונים בעבודת גינון" },
      { type: "p", text: "גינון נתפס כפעילות רגועה, אך אלפי אנשים נפגעים מדי שנה מתאונות גינון. הכרת הסיכונים ופעולה נכונה ימנעו פציעות." },

      { type: "h2", text: "כלי עבודה ידניים" },

      { type: "h3", text: "שימוש בטוח" },
      { type: "list", items: [
        "לבדוק תקינות לפני שימוש",
        "להשתמש בכלי הנכון לעבודה",
        "להחזיק בידית ולא בלהב",
        "לאחסן עם להב/שיניים כלפי מטה",
        "לנקות ולתחזק אחרי שימוש"
      ]},

      { type: "h3", text: "כלים חדים" },
      { type: "list", items: [
        "גזמים - לחתוך רחוק מהגוף",
        "מגזמות - להשתמש בשתי ידיים",
        "מספריים - להרחיק מהפנים",
        "כפפות הגנה תמיד"
      ]},

      { type: "h2", text: "מכונות גינון" },

      { type: "h3", text: "מכסחת דשא" },
      { type: "list", items: [
        "לקרוא הוראות הפעלה",
        "לבדוק שאין אבנים או חפצים בדשא",
        "נעליים סגורות וחזקות",
        "לא לגעת בלהבים כשהמנוע פועל",
        "לכבות לפני ניקוי או תיקון",
        "לא לכסח דשא רטוב"
      ]},

      { type: "h3", text: "גוזם גדר חיה" },
      { type: "list", items: [
        "משקפי מגן ואטמי אוזניים",
        "אחיזה יציבה בשתי ידיים",
        "לא להשתמש על סולם",
        "להיזהר מכבלי חשמל"
      ]},

      { type: "h3", text: "מסור שרשרת" },
      { type: "list", items: [
        "הכשרה מקצועית נדרשת",
        "ציוד מגן מלא",
        "לא לעבוד לבד",
        "לוודא יציבות העץ לפני ניסור"
      ]},

      { type: "h2", text: "עבודה בגובה" },
      { type: "list", items: [
        "סולם יציב על משטח ישר",
        "לא להתמתח יותר מדי",
        "להשתמש בחגורה לכלים",
        "עדיף שני אנשים",
        "גיזום גבוה - להזמין מקצוען"
      ]},

      { type: "h2", text: "חומרי הדברה ודשנים" },

      { type: "h3", text: "אחסון בטוח" },
      { type: "list", items: [
        "במקום נעול מילדים וחיות",
        "באריזה המקורית עם תווית",
        "לא ליד מזון",
        "במקום מאוורר"
      ]},

      { type: "h3", text: "שימוש בטוח" },
      { type: "list", items: [
        "לקרוא הוראות יצרן",
        "כפפות ומסכה בעת ריסוס",
        "לא לרסס ברוח חזקה",
        "להרחיק ילדים וחיות",
        "לשטוף ידיים אחרי שימוש"
      ]},

      { type: "h2", text: "חשמל בגינה" },
      { type: "list", items: [
        "כבלים מאריכים מוגנים מים",
        "שקעי חוץ עם כיסוי",
        "לא להשתמש בציוד רטוב",
        "לנתק לפני תיקון",
        "מפסק פחת חיצוני"
      ]},

      { type: "h2", text: "בריכות וג'קוזי" },
      { type: "list", items: [
        "גידור מסביב לבריכה",
        "כיסוי כשלא בשימוש",
        "פיקוח על ילדים",
        "בדיקת איכות מים",
        "אחסון כימיקלים בבטחה"
      ]},

      { type: "h2", text: "חיות וחרקים" },
      { type: "list", items: [
        "לבדוק אזורים מוסתרים לפני עבודה",
        "נעליים גבוהות באזורי נחשים",
        "דוחה יתושים ושמש",
        "לא לגעת בקינים",
        "מודעות לאלרגיות לעקיצות"
      ]},

      { type: "h2", text: "הגנה מהשמש" },
      { type: "list", items: [
        "כובע רחב שוליים",
        "קרם הגנה SPF 30+",
        "בגדים מכסים",
        "עבודה בשעות הבוקר או הערב",
        "שתייה מרובה"
      ]},

      { type: "h2", text: "ציוד מגן מומלץ" },
      { type: "list", items: [
        "כפפות עבודה",
        "משקפי מגן",
        "נעליים סגורות",
        "אטמי אוזניים (למכונות רועשות)",
        "מגני ברכיים לעבודה על הקרקע"
      ]},

      { type: "h2", text: "סיכום" },
      { type: "p", text: "גינון בטוח דורש זהירות וציוד מתאים. השקעה קטנה במניעה תחסוך פציעות ותאפשר ליהנות מהגינה." },

      { type: "cta", text: "צריכים ייעוץ בטיחות לעבודות גינון? צרו קשר." }
    ]
  },
  {
    id: 57,
    title: "בטיחות חיות מחמד - הגנה על המשפחה והחיה",
    slug: "pet-safety-family-protection",
    excerpt: "חיות מחמד הן חלק מהמשפחה, אך גם מקור לסיכוני בטיחות. מדריך לחיים בטוחים יחד.",
    date: "2024-05-20",
    category: "בית וסביבה",
    metaTitle: "בטיחות חיות מחמד - הגנה על המשפחה והחיה | אדם בטיחות",
    metaDescription: "מדריך בטיחות לבעלי חיות מחמד - מניעת נשיכות, בטיחות ילדים, סיכוני מזון, וטיפול נכון בחיה.",
    keywords: ["בטיחות חיות מחמד", "נשיכות כלבים", "בטיחות ילדים וחיות", "טיפול בכלב", "טיפול בחתול"],
    imageAlt: "משפחה עם כלב בבטחה",
    image: "/portfolio/office-safety.jpg",
    readTime: "9 דקות קריאה",
    content: [
      { type: "h2", text: "חיות מחמד בבית" },
      { type: "p", text: "כ-70% מהמשפחות בישראל מחזיקות חיות מחמד. הקשר עם החיה מועיל מאוד, אך דורש הבנה של סיכונים ומניעתם." },

      { type: "h2", text: "מניעת נשיכות כלבים" },

      { type: "h3", text: "הבנת שפת גוף" },
      { type: "list", items: [
        "זנב מכווץ - פחד או חרדה",
        "שיניים חשופות - אזהרה",
        "נהמה - הכלב לא מרוצה",
        "גב מקושת ושיער סמור - מוכנות לתקיפה"
      ]},

      { type: "h3", text: "כללי התנהגות" },
      { type: "list", items: [
        "לא לגשת לכלב זר ללא רשות הבעלים",
        "לא להפריע לכלב שאוכל או ישן",
        "לא להתגרות או לרדוף",
        "לא להסתכל ישירות בעיניים",
        "לתת לכלב להריח לפני ליטוף"
      ]},

      { type: "h3", text: "אם כלב תוקף" },
      { type: "list", items: [
        "להישאר רגוע ולא לרוץ",
        "לעמוד בלי תנועה (\"להיות עץ\")",
        "לא לצעוק",
        "אם נפלתם - להתכרבל ולהגן על הפנים"
      ]},

      { type: "h2", text: "ילדים וחיות מחמד" },

      { type: "h3", text: "כללים לילדים" },
      { type: "list", items: [
        "לא להתקרב לחיה ישנה",
        "לא לקחת מזון או צעצוע מהחיה",
        "לא למשוך אוזניים או זנב",
        "לא לחבק חזק מדי",
        "להתייחס בעדינות"
      ]},

      { type: "h3", text: "אחריות הורים" },
      { type: "list", items: [
        "לעולם לא להשאיר תינוק לבד עם חיה",
        "לפקח על אינטראקציות",
        "ללמד כבוד לחיה",
        "לזהות סימני מתח בחיה"
      ]},

      { type: "h2", text: "סיכוני מזון לחיות" },
      { type: "p", text: "מזונות מסוכנים לכלבים וחתולים:" },
      { type: "list", items: [
        "שוקולד - רעיל מאוד",
        "בצל ושום",
        "ענבים וצימוקים",
        "קסיליטול (ממתיק מלאכותי)",
        "אלכוהול",
        "קפאין",
        "אבוקדו (לציפורים במיוחד)"
      ]},

      { type: "h2", text: "סיכונים בבית" },

      { type: "h3", text: "חפצים מסוכנים" },
      { type: "list", items: [
        "צמחים רעילים (שושן, אלוורה, פיקוס)",
        "תרופות אנושיות",
        "חומרי ניקוי",
        "חוטים וגומיות",
        "עצמות קטנות"
      ]},

      { type: "h3", text: "מניעה" },
      { type: "list", items: [
        "אחסון תרופות במקום סגור",
        "הרחקת צמחים רעילים",
        "לא להשאיר חוטים נגישים",
        "פחי אשפה עם מכסה"
      ]},

      { type: "h2", text: "בריאות וחיסונים" },
      { type: "list", items: [
        "חיסונים שנתיים",
        "טיפול נגד פרעושים וקרציות",
        "בדיקות וטרינר תקופתיות",
        "סירוס/עיקור - מפחית תוקפנות"
      ]},

      { type: "h2", text: "בטיחות בטיולים" },
      { type: "list", items: [
        "רצועה תמיד במקומות ציבוריים",
        "מים ושתייה",
        "לא להשאיר ברכב חם",
        "תג זיהוי ושבב",
        "שקיות לאיסוף צרכים"
      ]},

      { type: "h2", text: "מחלות משותפות (זואונוזות)" },
      { type: "list", items: [
        "לשטוף ידיים אחרי מגע",
        "לא לנשק את החיה על הפה",
        "ניקוי ארגז חול (חתולים) עם כפפות",
        "טיפול בפצעים מנשיכות/שריטות"
      ]},

      { type: "h2", text: "מתי לפנות לוטרינר" },
      { type: "list", items: [
        "שינוי בהתנהגות פתאומי",
        "סירוב לאכול",
        "הקאות או שלשולים",
        "קושי בנשימה",
        "פציעה או דימום"
      ]},

      { type: "h2", text: "סיכום" },
      { type: "p", text: "חיה בריאה ומאומנת היא חיה בטוחה. השקעה בחינוך, בריאות ופיקוח תבטיח חיים משותפים מאושרים." },

      { type: "cta", text: "מחפשים הדרכת בטיחות לבעלי חיות? צרו קשר." }
    ]
  },
  {
    id: 58,
    title: "בטיחות ברשת ובמדיה חברתית",
    slug: "internet-social-media-safety",
    excerpt: "העולם הדיגיטלי טומן סכנות רבות. מדריך להגנה על עצמכם ועל ילדיכם ברשת.",
    date: "2024-05-23",
    category: "טכנולוגיה",
    metaTitle: "בטיחות ברשת ובמדיה חברתית - הגנה דיגיטלית | אדם בטיחות",
    metaDescription: "מדריך בטיחות באינטרנט - הגנה על פרטיות, זיהוי הונאות, בטיחות ילדים ברשת, וטיפים לשימוש בטוח.",
    keywords: ["בטיחות באינטרנט", "הגנת פרטיות", "בטיחות ילדים ברשת", "הונאות אינטרנט", "סייבר"],
    imageAlt: "גלישה בטוחה באינטרנט",
    image: "/portfolio/warehouse.jpg",
    readTime: "11 דקות קריאה",
    content: [
      { type: "h2", text: "סיכונים בעולם הדיגיטלי" },
      { type: "p", text: "האינטרנט הוא כלי נפלא, אך גם זירה לפשיעה, הונאות ופגיעה בפרטיות. מודעות והתנהגות נכונה יגנו עליכם." },

      { type: "h2", text: "הגנה על סיסמאות" },

      { type: "h3", text: "סיסמה חזקה" },
      { type: "list", items: [
        "לפחות 12 תווים",
        "אותיות גדולות וקטנות",
        "מספרים וסימנים מיוחדים",
        "לא מידע אישי (תאריך לידה, שם)",
        "סיסמה שונה לכל חשבון"
      ]},

      { type: "h3", text: "ניהול סיסמאות" },
      { type: "list", items: [
        "מנהל סיסמאות מאובטח",
        "אימות דו-שלבי (2FA)",
        "לא לשמור בדפדפן ציבורי",
        "להחליף סיסמאות בחשד לפריצה"
      ]},

      { type: "h2", text: "זיהוי הונאות (פישינג)" },

      { type: "h3", text: "סימנים מחשידים" },
      { type: "list", items: [
        "דחיפות מוגזמת (\"חשבונך ייחסם!\")",
        "שגיאות כתיב ועיצוב לקוי",
        "כתובת שולח חשודה",
        "קישורים לא מוכרים",
        "בקשה למידע אישי או סיסמה"
      ]},

      { type: "h3", text: "מה לעשות" },
      { type: "list", items: [
        "לא ללחוץ על קישורים חשודים",
        "לבדוק כתובת האתר בזהירות",
        "להתקשר ישירות לחברה (לא דרך המייל)",
        "לדווח על הודעות חשודות"
      ]},

      { type: "h2", text: "פרטיות ברשתות חברתיות" },

      { type: "h3", text: "הגדרות פרטיות" },
      { type: "list", items: [
        "פרופיל פרטי ולא ציבורי",
        "לבדוק מי יכול לראות פוסטים",
        "להגביל מידע אישי",
        "לא לפרסם מיקום בזמן אמת"
      ]},

      { type: "h3", text: "מידע לא לפרסם" },
      { type: "list", items: [
        "כתובת מגורים",
        "מספר טלפון",
        "תמונות של ילדים עם שם בית ספר",
        "תוכניות נסיעה (\"טסים מחר!\")",
        "מידע פיננסי"
      ]},

      { type: "h2", text: "בטיחות ילדים ברשת" },

      { type: "h3", text: "פיקוח הורי" },
      { type: "list", items: [
        "מחשב במקום משותף",
        "הגבלת זמן מסך",
        "תוכנת סינון תוכן",
        "שיחות פתוחות על חוויות ברשת"
      ]},

      { type: "h3", text: "סיכונים לילדים" },
      { type: "list", items: [
        "חשיפה לתוכן לא מתאים",
        "בריונות רשת",
        "גרומינג (פיתוי על ידי מבוגרים)",
        "תמונות פוגעניות"
      ]},

      { type: "h3", text: "מה ללמד ילדים" },
      { type: "list", items: [
        "לא לשתף מידע אישי",
        "לא להיפגש עם זרים מהרשת",
        "לספר להורים על דברים מפחידים",
        "לא לפתוח הודעות מזרים"
      ]},

      { type: "h2", text: "קניות מקוונות בטוחות" },
      { type: "list", items: [
        "לקנות רק מאתרים מוכרים",
        "לבדוק HTTPS ומנעול",
        "לא לשמור פרטי כרטיס אשראי",
        "לבדוק חיובים בחשבון בנק",
        "להיזהר מעסקאות \"טובות מדי\""
      ]},

      { type: "h2", text: "הגנה על המחשב" },
      { type: "list", items: [
        "אנטי-וירוס מעודכן",
        "עדכוני מערכת הפעלה",
        "לא להוריד מאתרים לא מוכרים",
        "גיבוי קבוע של מידע חשוב",
        "Wi-Fi ביתי מאובטח"
      ]},

      { type: "h2", text: "מה לעשות אם נפגעתם" },
      { type: "list", items: [
        "להחליף סיסמאות מיד",
        "לדווח לבנק על גניבת כרטיס",
        "לדווח למשטרה על הונאה",
        "לשמור הוכחות (צילומי מסך)",
        "להתייעץ עם מומחה סייבר"
      ]},

      { type: "h2", text: "סיכום" },
      { type: "p", text: "העולם הדיגיטלי מלא הזדמנויות וסיכונים. גלישה בטוחה, סיסמאות חזקות ופיקוח על ילדים יגנו על המשפחה." },

      { type: "cta", text: "מעוניינים בהדרכת בטיחות סייבר לארגון? צרו קשר." }
    ]
  },
  {
    id: 59,
    title: "סיכום - תרבות בטיחות בבית ובעבודה",
    slug: "safety-culture-summary-home-work",
    excerpt: "בטיחות היא לא מקרה - היא תוצאה של תרבות. מאמר מסכם על בניית תרבות בטיחות.",
    date: "2024-05-26",
    category: "ניהול",
    metaTitle: "תרבות בטיחות בבית ובעבודה - מדריך מסכם | אדם בטיחות",
    metaDescription: "מאמר מסכם על תרבות בטיחות - איך לבנות סביבה בטוחה בבית ובעבודה, עקרונות מנחים וצעדים מעשיים.",
    keywords: ["תרבות בטיחות", "בטיחות בעבודה", "בטיחות בבית", "מניעת תאונות", "הדרכת בטיחות"],
    imageAlt: "תרבות בטיחות בארגון",
    image: "/portfolio/industrial-safety.jpg",
    readTime: "8 דקות קריאה",
    content: [
      { type: "h2", text: "מהי תרבות בטיחות?" },
      { type: "p", text: "תרבות בטיחות היא מערכת הערכים, האמונות וההתנהגויות שמעמידה את הבטיחות כערך עליון. היא לא נוצרת מעצמה - היא נבנית." },

      { type: "h2", text: "עקרונות מנחים" },

      { type: "h3", text: "מחויבות מלמעלה" },
      { type: "list", items: [
        "הנהלה מחויבת לבטיחות",
        "הקצאת משאבים",
        "דוגמה אישית",
        "בטיחות כחלק מקבלת החלטות"
      ]},

      { type: "h3", text: "מעורבות כולם" },
      { type: "list", items: [
        "כל עובד אחראי",
        "עידוד דיווח",
        "הקשבה להצעות",
        "למידה משותפת"
      ]},

      { type: "h3", text: "שיפור מתמיד" },
      { type: "list", items: [
        "למידה מתקריות",
        "עדכון נהלים",
        "הדרכות שוטפות",
        "מדידה והערכה"
      ]},

      { type: "h2", text: "בניית תרבות בטיחות בעבודה" },

      { type: "h3", text: "צעדים מעשיים" },
      { type: "list", items: [
        "הגדרת מדיניות בטיחות ברורה",
        "הדרכות חובה לכל עובד",
        "סקרי סיכונים תקופתיים",
        "ועדת בטיחות פעילה",
        "מערכת דיווח על מפגעים",
        "תגמול על התנהגות בטיחותית"
      ]},

      { type: "h3", text: "מדדי הצלחה" },
      { type: "list", items: [
        "ירידה בתאונות",
        "עלייה בדיווחים",
        "השתתפות בהדרכות",
        "ציוני ביקורת",
        "שביעות רצון עובדים"
      ]},

      { type: "h2", text: "תרבות בטיחות בבית" },

      { type: "h3", text: "הרגלים יומיומיים" },
      { type: "list", items: [
        "בדיקת גלאי עשן חודשית",
        "סדר ונקיון שוטף",
        "אחסון נכון של חומרים מסוכנים",
        "תקשורת פתוחה על סיכונים"
      ]},

      { type: "h3", text: "חינוך ילדים" },
      { type: "list", items: [
        "הסבר מותאם גיל",
        "דוגמה אישית",
        "תרגול מצבי חירום",
        "עידוד לשאול ולדווח"
      ]},

      { type: "h2", text: "טעויות נפוצות" },
      { type: "list", items: [
        "\"לא יקרה לי\" - זלזול בסיכונים",
        "\"אין זמן\" - דילוג על בטיחות",
        "\"זה יקר\" - חיסכון על חשבון בטיחות",
        "\"תמיד עשינו ככה\" - התנגדות לשינוי",
        "\"אחרי זה\" - דחיינות בטיפול במפגעים"
      ]},

      { type: "h2", text: "התמודדות עם התנגדות" },
      { type: "list", items: [
        "הסבר על היתרונות",
        "שיתוף בקבלת החלטות",
        "הדגמת הצלחות",
        "טיפול בחסמים מעשיים",
        "סבלנות ועקביות"
      ]},

      { type: "h2", text: "משאבים והמשך למידה" },
      { type: "list", items: [
        "קורסי בטיחות מקצועיים",
        "התייעצות עם מומחים",
        "עדכון שוטף בחקיקה",
        "למידה מארגונים דומים",
        "השתתפות בכנסים ומפגשים"
      ]},

      { type: "h2", text: "לסיכום" },
      { type: "p", text: "בטיחות היא מסע, לא יעד. כל פעולה קטנה, כל הרגל טוב, כל החלטה נכונה - מצטברים לסביבה בטוחה יותר. התחילו היום." },

      { type: "quote", text: "\"הבטיחות לא מתרחשת במקרה\" - אמרה מוכרת בתחום הבטיחות" },

      { type: "h2", text: "מה עכשיו?" },
      { type: "list", items: [
        "לבחור נושא אחד לשיפור",
        "ליישם צעד מעשי אחד",
        "לשתף את הסביבה",
        "לבדוק ולשפר",
        "להמשיך לנושא הבא"
      ]},

      { type: "cta", text: "מוכנים לבנות תרבות בטיחות בארגון? צרו קשר ונתחיל יחד." }
    ]
  }
];

// Export normalized blog posts with valid, unique images
export const blogPosts = normalizeBlogPosts(rawBlogPosts);

export const categories = ["הכל", "הדרכות", "רגולציה", "ניהול", "טכנולוגיה", "מדריכים", "כלכלה", "ענפים", "בנייה", "גיהות", "סיכונים", "מגזרים", "בית וסביבה"];

// Export fallback generator for use in UI onError handlers
export function getFallbackImageUrl(postId: number, category: string): string {
  return generateFallbackImage(postId, category);
}

export function getPostBySlug(slug: string): BlogPost | undefined {
  return blogPosts.find(post => post.slug === slug);
}

export function getRelatedPosts(currentPost: BlogPost, limit = 3): BlogPost[] {
  return blogPosts
    .filter(post => post.id !== currentPost.id)
    .filter(post => post.category === currentPost.category || post.keywords.some(k => currentPost.keywords.includes(k)))
    .slice(0, limit);
}

export function isNewPost(dateStr: string, thresholdDays = 14): boolean {
  const postDate = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - postDate.getTime()) / (1000 * 60 * 60 * 24));
  return diffDays <= thresholdDays;
}
